import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getCommunityState,
  publishCommunityState,
  updateCommunityState,
  useCommunityState,
  VISIBILITY,
  EDIT_WINDOW_MS,
  UNLOCK_PRICE_INR,
  FREE_VIEWER_COUNT,
  CURRENT_USER_ID,
  FALLBACK_AD_IMAGE,
  MOCK_MEMBERS,
  buildAd,
  MOCK_VIEWERS,
  REACTION_TYPES,
  REACTION_LABELS,
  REACTION_ICONS,
  MOCK_REACTIONS_BY_AD,
  MOCK_REACTIONS_BY_POST,
  MOCK_COMMENTS_BY_AD,
  MOCK_COMMENTS_BY_POST,
  formatTimeAgo,
  isValidEmail,
  getMemberById,
  POST_VISIBILITY,
  COMMENT_PERMISSION,
  EVENT_TYPE,
  POST_MAX_PHOTOS,
  POST_MAX_MEDIA,
  POST_MAX_VIDEOS,
  POST_MAX_TEXT,
  EVENT_TITLE_MAX,
  EVENT_DESC_MAX,
  COMMENT_MAX_WORDS,
  EVENT_LINK_MAX,
  EVENT_WORD_MAX,
  POST_TITLE_MAX_WORDS,
  EVENT_COVER_MIN_WIDTH,
  EVENT_COVER_MAX_MEDIA,
  EVENT_COVER_MAX_VIDEOS,
  TIMEZONES,
  buildEvent,
  buildPost,
  isValidUrl,
  formatDateLabel,
  REPORT_REASONS,
  REPORT_OTHER_MAX,
} from "../Constants/CommunityConst";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../Styles/CommunityStyle.css";
import CommunityMultipleImg from "../../assets/Community/multiple.png";

const commentCountDeltas = {};
const deletedCommentIds = new Set();
const commentCountSubscribers = new Set();
export const commentStore = {
  getDelta: (id) => commentCountDeltas[id] || 0,
  add: (id) => {
    commentCountDeltas[id] = (commentCountDeltas[id] || 0) + 1;
    commentCountSubscribers.forEach((fn) => fn());
  },
  removeDelta: (id) => {
    commentCountDeltas[id] = Math.max(0, (commentCountDeltas[id] || 0) - 1);
    commentCountSubscribers.forEach((fn) => fn());
  },
  markDeleted: (commentId) => {
    if (!deletedCommentIds.has(commentId)) {
      deletedCommentIds.add(commentId);
      commentCountSubscribers.forEach((fn) => fn());
    }
  },
  isDeleted: (commentId) => deletedCommentIds.has(commentId),
  subscribe: (fn) => {
    commentCountSubscribers.add(fn);
    return () => commentCountSubscribers.delete(fn);
  },
};

export const useCommentCount = (base, id) => {
  const [delta, setDelta] = useState(() => commentStore.getDelta(id));
  useEffect(() => commentStore.subscribe(() => setDelta(commentStore.getDelta(id))), [id]);
  const liveBase = base.filter((c) => !commentStore.isDeleted(c.id));
  return liveBase.length + delta;
};

const textColorStyle = (color) => {
  if (!color) return {};
  if (typeof color === "string" && color.startsWith("linear-gradient")) {
    return {
      backgroundImage: color,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      color: "transparent",
    };
  }
  return { color };
};

const isGradientColor = (color) =>
  !!color && typeof color === "string" && color.startsWith("linear-gradient");

const fontRenderStyle = ({ color, weight, size, italic, underline, fontFamily }) => ({
  ...textColorStyle(color),
  fontWeight: weight || 600,
  fontSize: `${size || 20}px`,
  fontStyle: italic ? "italic" : "normal",
  textDecoration: underline ? "underline" : "none",
  fontFamily: fontFamily || "auto",
});

const FONT_TOOL_COLORS = ["#e63946", "#e76f51", "#f4a261", "#2a9d8f", "#457b9d", "#6d597a"];

const wordCount = (value) => (value || "").trim().split(/\s+/).filter(Boolean).length;

const isOverWordLimit = (value, max, prev) => {
  if (wordCount(value) > max) return true;
  if (wordCount(prev) >= max && (value || "").length > (prev || "").length) return true;
  return false;
};

const getYoutubeEmbedUrl = (link) => {
  const text = String(link || "").trim();
  if (!text) return "";
  const match =
    text.match(
      /(?:youtube\.com\/(?:watch\?[^#]*v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
    ) || text.match(/[?&]v=([A-Za-z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : "";
};

const getYoutubeId = (embed) => {
  const m = String(embed || "").match(/([A-Za-z0-9_-]{11})/);
  return m ? m[1] : "";
};

const getYoutubeThumb = (embed) =>
  getYoutubeId(embed) ? `https://img.youtube.com/vi/${getYoutubeId(embed)}/hqdefault.jpg` : "";

const parseYoutubeLink = (link) => {
  const embed = getYoutubeEmbedUrl(link);
  if (!embed) return null;
  return {
    url: String(link || "").trim(),
    embed,
    thumb: getYoutubeThumb(embed),
  };
};

const YT_MAX = 5;

const normalizeYoutube = (y) => {
  if (!y) return [];
  const list = Array.isArray(y) ? y : [y];
  return list
    .filter((v) => v && v.embed)
    .map((v) => ({ url: v.url || "", embed: v.embed, thumb: v.thumb || getYoutubeThumb(v.embed) }));
};

const FONT_TOOL_FAMILIES = [
  "auto",
  "Arial",
  "Verdana",
  "Tahoma",
  "Trebuchet MS",
  "Times New Roman",
  "Georgia",
  "Courier New",
  "Comic Sans MS",
  "Impact",
];

const FontTool = ({ value, color, setColor, weight, setWeight, size, setSize, italic, setItalic, underline, setUnderline, maxSize, open, setOpen, showButton = true }) => {
  const sizeOptions = [];
  for (let s = 14; s <= maxSize; s++) sizeOptions.push(s);
  return (
    <>
      {showButton && (
      <button
        type="button"
        className="Community-font-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label="Text style"
        title="Text style"
      >
        <svg viewBox="0 0 512 512" width="16" height="16" aria-hidden="true">
          <defs>
            <linearGradient id="abFontGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#861b68" />
              <stop offset="100%" stopColor="#e0245e" />
            </linearGradient>
          </defs>
          <path fill="url(#abFontGradient)" d="M488.1 434.6H462L316.1 30.2C311.2 15.7 297.6 6 282.3 6h-52.6c-15.3 0-28.9 9.7-33.8 24.2L50 434.6H23.9c-9.9 0-17.9 8-17.9 17.9v35.7C6 498 14 506 23.9 506h142.9c9.9 0 17.9-8 17.9-17.9v-35.7c0-9.9-8-17.9-17.9-17.9h-21.9l26-71.4h170.3l26 71.4h-21.9c-9.9 0-17.9 8-17.9 17.9v35.7c0 9.9 8 17.9 17.9 17.9h142.9c9.9 0 17.9-8 17.9-17.9v-35.7c-.1-9.8-8.1-17.8-18-17.8zM203.4 273.9 256 129.3l52.6 144.5H203.4v.1z" />
        </svg>
      </button>
      )}
      {open && (
        <>
          <div className="Community-font-backdrop" onClick={() => setOpen(false)} />
          <div className="Community-font-overlay">
            <p
              className="Community-font-preview"
              style={fontRenderStyle({ color, weight, size, italic, underline })}
            >
              {value || "Your text"}
            </p>
            <div className="Community-font-biu-row">
              <button
                type="button"
                className={`Community-font-biu ${(weight || 600) >= 700 ? "Community-font-biu-active" : ""}`}
                onClick={() => setWeight((weight || 600) >= 700 ? 400 : 700)}
                title="Bold"
                aria-label="Bold"
              >B</button>
              <button
                type="button"
                className={`Community-font-biu ${italic ? "Community-font-biu-active" : ""}`}
                onClick={() => setItalic(!italic)}
                title="Italic"
                aria-label="Italic"
              >I</button>
              <button
                type="button"
                className={`Community-font-biu ${underline ? "Community-font-biu-active" : ""}`}
                onClick={() => setUnderline(!underline)}
                title="Underline"
                aria-label="Underline"
              >U</button>
            </div>
            <div className="Community-font-controls">
              <div className="Community-font-control">
                <span className="Community-font-label">Font Size</span>
                <select
                  className="Community-input Community-font-select"
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                >
                  {sizeOptions.map((s) => <option key={s} value={s}>{s}px</option>)}
                </select>
              </div>
              <div className="Community-font-color-block">
                <span className="Community-font-label">Color</span>
                <div className="Community-font-colors">
                  <button
                    type="button"
                    className={`Community-title-color-swatch ${color === "#000000" ? "Community-title-color-active" : ""}`}
                    style={{ background: "#000000" }}
                    onClick={() => setColor("#000000")}
                    aria-label="Color #000000"
                  />
                  {FONT_TOOL_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`Community-title-color-swatch ${color === c ? "Community-title-color-active" : ""}`}
                      style={{ background: c }}
                      onClick={() => setColor(c)}
                      aria-label={`Color ${c}`}
                    />
                  ))}
                  <input
                    type="color"
                    className="Community-title-color-swatch Community-custom-color-swatch"
                    value={isGradientColor(color) ? "#861b68" : (color || "#1a1a1a")}
                    onChange={(e) => setColor(e.target.value)}
                    aria-label="Custom color"
                    title="Custom color"
                  />
                </div>
              </div>
            </div>
            <button type="button" className="Community-font-done" onClick={() => setOpen(false)}>Done</button>
          </div>
        </>
      )}
    </>
  );
};

const PrimaryButton = ({ children, onClick, type = "button", disabled, fullWidth, className = "" }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`Community-btn Community-btn-primary ${fullWidth ? "Community-btn-full" : ""} ${className}`}
  >
    {children}
  </button>
);

const SecondaryButton = ({ children, onClick, type = "button", fullWidth, disabled }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`Community-btn Community-btn-secondary ${fullWidth ? "Community-btn-full" : ""}`}
  >
    {children}
  </button>
);

const ShareMessageArrow = ({ onClick }) => (
  <button
    type="button"
    className="Community-share-arrow"
    onClick={onClick}
    aria-label="Open message form"
    title="Open message form"
  >
    <span aria-hidden="true">&#8594;</span>
    <span className="Community-share-arrow-label">Message</span>
  </button>
);

const ReactionPicker = ({ onReact, onHoverOut }) => {
  const types = [
    REACTION_TYPES.LIKE,
    REACTION_TYPES.CELEBRATE,
    REACTION_TYPES.SUPPORT,
    REACTION_TYPES.LOVE,
    REACTION_TYPES.INSIGHTFUL,
    REACTION_TYPES.FUNNY,
  ];
  return (
    <div
      className="Community-reaction-picker"
      onMouseLeave={onHoverOut}
      role="menu"
      aria-label="Choose a reaction"
    >
      {types.map((t) => (
        <button
          key={t}
          type="button"
          className="Community-reaction-option"
          title={REACTION_LABELS[t]}
          aria-label={REACTION_LABELS[t]}
          role="menuitem"
          onClick={(e) => {
            e.stopPropagation();
            onReact(t);
          }}
        >
          {t === REACTION_TYPES.LIKE ? "👍" : REACTION_ICONS[t]}
          <span className="Community-reaction-tooltip">{REACTION_LABELS[t]}</span>
        </button>
      ))}
    </div>
  );
};

const ReactionButton = ({ count, activeType, onPick, onHistory, heart, hideCount }) => {
  const [open, setOpen] = useState(false);
  const activeIcon =
    activeType && activeType !== REACTION_TYPES.LIKE ? REACTION_ICONS[activeType] : "👍";

  if (heart) {
    const liked = Boolean(activeType);
    const shownIcon = activeType ? (activeType === REACTION_TYPES.LIKE ? "👍" : REACTION_ICONS[activeType]) : "♡";
    return (
      <span
        className={`Community-reaction-btn ${liked ? "Community-reaction-open" : ""}`}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        {open && <ReactionPicker onReact={onPick} onHoverOut={() => setOpen(false)} />}
        <button
          type="button"
          className={`Community-reaction-trigger Community-heart-btn ${liked ? "Community-heart-liked" : ""}`}
          onClick={() => onPick(activeType || REACTION_TYPES.LOVE)}
          title={liked ? "Unlike" : "Love this post"}
          aria-label={`${count} ${liked ? "loves" : "loves"}. Click to ${liked ? "unlike" : "love"}.`}
          aria-pressed={liked}
        >
          <span className="Community-reaction-btn-icon Community-heart-icon">{shownIcon}</span>
          {!hideCount && count > 0 && <span className="Community-reaction-btn-count">{count}</span>}
        </button>
      </span>
    );
  }

  return (
    <span
      className={`Community-reaction-btn ${open ? "Community-reaction-open" : ""}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {open && <ReactionPicker onReact={onPick} onHoverOut={() => setOpen(false)} />}
      <button
        type="button"
        className={`Community-reaction-trigger ${activeType ? "Community-reaction-active" : ""}`}
        onClick={() => onPick(REACTION_TYPES.LIKE)}
        onMouseEnter={() => setOpen(true)}
        title="React to this advertisement"
        aria-label={`${count} reactions. Click to react.`}
      >
        <span className="Community-reaction-btn-icon">{activeIcon}</span>
        <span className="Community-reaction-btn-count">{count}</span>
      </button>
    </span>
  );
};

const CommentButton = ({ count, onClick, hideCount }) => (
  <button
    type="button"
    className="Community-engagement-btn"
    onClick={onClick}
    title="View and add comments"
    aria-label={`${count} comments. Click to view comments.`}
  >
    <svg className="Community-engagement-btn-icon Community-comment-empty-icon" viewBox="0 0 24 24" width="25" height="25" fill="none" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    {!hideCount && count > 0 && <span className="Community-engagement-btn-count">{count}</span>}
  </button>
);

const shareCurrentPage = async (post, { onCopied } = {}) => {
  const text = post
    ? (post.event && post.event.name) || post.text || post.title || "Check out this post on IHN"
    : "Check out this post on IHN";
  const url = window.location.href;

  const copyFallback = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      if (onCopied) onCopied();
    } catch (_) {}
  };

  try {
    if (navigator.share) {
      await navigator.share({ title: document.title, text, url });
    } else {
      await copyFallback();
    }
  } catch (err) {
    // Cancellation (AbortError) is silent; any other failure copies the link.
    if (err && err.name === "AbortError") return;
    await copyFallback();
  }
};

const ShareButton = ({ onClick }) => (
  <button
    type="button"
    className="Community-engagement-btn"
    onClick={onClick}
    title="Share this advertisement"
    aria-label="Share this advertisement"
  >
    <svg className="Community-sharepost-icon" viewBox="0 0 24 24" width="25" height="25" fill="#000000" aria-hidden="true">
      <path d="M20.3359 3.22136L3.87333 8.70889C3.56801 8.81066 3.55033 9.23586 3.84614 9.36263L9.89655 11.9557C9.96078 11.9832 10.0347 11.9752 10.0916 11.9346L16.0235 7.69749C16.2073 7.56618 16.4338 7.79266 16.3025 7.97648L12.0654 13.9084C12.0248 13.9653 12.0168 14.0392 12.0443 14.1034L14.6374 20.1539C14.7641 20.4497 15.1893 20.432 15.2911 20.1267L20.7786 3.66408C20.8698 3.39046 20.6095 3.13015 20.3359 3.22136Z" fill="currentColor" />
    </svg>
  </button>
);

const ViewsButton = ({ count, onClick }) => (
  <button
    type="button"
    className="Community-engagement-btn Community-engagement-btn-views"
    onClick={onClick}
    title="See everyone who this advertisement"
    aria-label={`See who what this advertisement. ${count} views.`}
  >
    <svg className="Community-engagement-btn-icon" viewBox="0 0 24 24" width="25" height="25" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
    <span className="Community-engagement-btn-count">{count}</span>
  </button>
);

const otherReactions = (reactions = []) =>
  Array.isArray(reactions) ? reactions.filter((r) => r.memberId !== CURRENT_USER_ID) : [];

const reactionRecency = (label) => {
  const s = String(label || "").trim().toLowerCase();
  if (s === "just now") return 0;
  const m = s.match(/^(\d+(?:\.\d+)?)\s*(s|m|h|d|w)$/);
  if (!m) return Number.MAX_SAFE_INTEGER;
  const n = parseFloat(m[1]);
  return n * { s: 1, m: 60, h: 3600, d: 86400, w: 604800 }[m[2]];
};

const sortReactionsByRecency = (reactions = []) =>
  [...reactions].sort((a, b) => reactionRecency(a.time) - reactionRecency(b.time));

const EngagementBar = ({
  ad,
  onOpenMessage,
  onOpenComments,
  onOpenShare,
  onOpenViews,
  isOwner,
  hideLikeCount,
  hideCommentsCount,
  myReaction,
  onReactionChange,
}) => {
  const baseReactions = otherReactions(MOCK_REACTIONS_BY_AD[ad.id]);
  const comments = MOCK_COMMENTS_BY_AD[ad.id] || [];
  const viewers = MOCK_VIEWERS[ad.id] || [];
  const reactionCount = baseReactions.length + (myReaction ? 1 : 0);
  const commentCount = useCommentCount(comments, ad.id);

  const handlePick = (type) => {
    onReactionChange(myReaction === type ? null : type);
  };

  return (
    <div className="Community-engagement-row">
      <ReactionButton heart count={reactionCount} activeType={myReaction} onPick={handlePick} hideCount={hideLikeCount} />
      <CommentButton count={commentCount} onClick={() => onOpenComments(ad)} hideCount={hideCommentsCount} />
      <ShareButton onClick={() => onOpenShare(ad)} />
      {isOwner ? (
        <ViewsButton count={viewers.length} onClick={() => onOpenViews(ad, "viewed")} />
      ) : (
        <button
          type="button"
          className="Community-reactions-visible-btn"
          onClick={() => onOpenViews(ad, "all")}
          title="See who reacted to this advertisement"
          aria-label="See who reacted to this advertisement"
        >
          <svg viewBox="0 0 32 32" width="25" height="25" fill="currentColor" aria-hidden="true">
            <path d="M23.313 26.102l-6.296-3.488c2.34-1.841 2.976-5.459 2.976-7.488v-4.223c0-2.796-3.715-5.91-7.447-5.91-3.73 0-7.544 3.114-7.544 5.91v4.223c0 1.845 0.78 5.576 3.144 7.472l-6.458 3.503s-1.688 0.752-1.688 1.689v2.534c0 0.933 0.757 1.689 1.688 1.689h21.625c0.931 0 1.688-0.757 1.688-1.689v-2.534c0-0.994-1.689-1.689-1.689-1.689zM23.001 30.015h-21.001v-1.788c0.143-0.105 0.344-0.226 0.502-0.298 0.047-0.021 0.094-0.044 0.139-0.070l6.459-3.503c0.589-0.32 0.979-0.912 1.039-1.579s-0.219-1.32-0.741-1.739c-1.677-1.345-2.396-4.322-2.396-5.911v-4.223c0-1.437 2.708-3.91 5.544-3.91 2.889 0 5.447 2.44 5.447 3.91v4.223c0 1.566-0.486 4.557-2.212 5.915-0.528 0.416-0.813 1.070-0.757 1.739s0.446 1.267 1.035 1.589l6.296 3.488c0.055 0.030 0.126 0.063 0.184 0.089 0.148 0.063 0.329 0.167 0.462 0.259v1.809zM30.312 21.123l-6.39-3.488c2.34-1.841 3.070-5.459 3.070-7.488v-4.223c0-2.796-3.808-5.941-7.54-5.941-2.425 0-4.904 1.319-6.347 3.007 0.823 0.051 1.73 0.052 2.514 0.302 1.054-0.821 2.386-1.308 3.833-1.308 2.889 0 5.54 2.47 5.54 3.941v4.223c0 1.566-0.58 4.557-2.305 5.915-0.529 0.416-0.813 1.070-0.757 1.739 0.056 0.67 0.445 1.267 1.035 1.589l6.39 3.488c0.055 0.030 0.126 0.063 0.184 0.089 0.148 0.063 0.329 0.167 0.462 0.259v1.779h-4.037c0.61 0.46 0.794 1.118 1.031 2h3.319c0.931 0 1.688-0.757 1.688-1.689v-2.503c-0.001-0.995-1.689-1.691-1.689-1.691z"/>
          </svg>
        </button>
      )}
    </div>
  );
};

const PostEngagementBar = ({ post, onOpenComments, hideLikeCount, hideCommentCount, isOwner, onOpenViews, onOpenShare }) => {
  const store = useCommunityState();
  const myReaction = (store.likedPosts && store.likedPosts.get(post.id)) || null;
  const baseReactions = otherReactions(MOCK_REACTIONS_BY_AD[post.id] || MOCK_REACTIONS_BY_POST[post.id]);
  const baseComments = MOCK_COMMENTS_BY_AD[post.id] || MOCK_COMMENTS_BY_POST[post.id] || [];
  const [reactionCount, setReactionCount] = useState(baseReactions.length + (myReaction ? 1 : 0));
  const commentCount = useCommentCount(baseComments, post.id);

  const hideLikesBtn = Boolean(post.hideLikes);
  const hideCommentsBtn = Boolean(post.hideComments) || post.commentPermission === COMMENT_PERMISSION.OFF;

  const handlePick = (type) => {
    const prevReaction = (store.likedPosts && store.likedPosts.get(post.id)) || null;
    const nextReaction = prevReaction === type ? null : type;
    setReactionCount((c) => {
      if (prevReaction === type) return c - 1;
      if (!prevReaction) return c + 1;
      return c;
    });
    updateCommunityState((s) => {
      const next = new Map(s.likedPosts);
      if (nextReaction === null) next.delete(post.id);
      else next.set(post.id, nextReaction);
      return { ...s, likedPosts: next };
    });
  };

  return (
    <div className="Community-engagement-row">
      {!hideLikesBtn && (
        <ReactionButton heart count={reactionCount} activeType={myReaction} onPick={handlePick} hideCount={hideLikeCount} />
      )}
      {!hideCommentsBtn && (
        <CommentButton count={commentCount} onClick={() => onOpenComments(post)} hideCount={hideCommentCount} isPost />
      )}
<ShareButton onClick={() => onOpenShare && onOpenShare(post)} />
      <button
        type="button"
        className="Community-reactions-visible-btn"
        onClick={() => onOpenViews && onOpenViews(post, "all", myReaction)}
        title="See who reacted to this post"
        aria-label="See who reacted to this post"
      >
        <svg viewBox="0 0 24 24" width="25" height="25" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      </button>
    </div>
  );
};

const Toast = ({ message, onDismiss, undoLabel, onUndo }) => {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3500);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="Community-toast" role="status" aria-live="polite">
      <span className="Community-toast-message">{message}</span>
      {undoLabel && onUndo && (
        <button type="button" className="Community-toast-undo" onClick={onUndo}>
          {undoLabel}
        </button>
      )}
      <button type="button" className="Community-toast-close" onClick={onDismiss} aria-label="Dismiss notification">
        ×
      </button>
    </div>
  );
};

const PostCardControls = ({
  isFollowing,
  onToggleFollow,
  isSaved,
  onToggleSave,
  isArchived,
  onToggleArchive,
  onCopyLink,
  onHide,
  onReport,
  isOwner,
  onEdit,
  isLikeCountHidden,
  onToggleHideLikes,
  isCommentCountHidden,
  onToggleHideCommentCounts,
  isLikesOff,
  onToggleLikesOff,
  isCommentsOff,
  onToggleCommentsOff,
  onDelete,
}) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const run = (fn) => {
    setOpen(false);
    fn();
  };

  if (isOwner) {
    return (
<div className="Community-post-controls">
        <div className="Community-dotmenu" ref={menuRef}>
          <div className="Community-dotmenu-btns">
            <button
              ref={btnRef}
              type="button"
              className="Community-dotmenu-btn"
              onClick={() => setOpen((v) => !v)}
              aria-label="Post options"
              aria-haspopup="menu"
              aria-expanded={open}
            >
              ⋯
            </button>
          </div>
          {open && (
            <div className="Community-dotmenu-dropdown" role="menu">
              <button
                type="button"
                role="menuitem"
                className="Community-dotmenu-item"
                onClick={() => run(onEdit)}
              >
                <span aria-hidden="true">✏️</span>
                Edit
              </button>
              <button
                type="button"
                role="menuitem"
                className="Community-dotmenu-item"
                onClick={() => run(onCopyLink)}
              >
                <span aria-hidden="true">🔗</span>
                Copy link
              </button>
              <button
                type="button"
                role="menuitem"
                className="Community-dotmenu-item"
                onClick={() => run(onToggleSave)}
              >
                <span aria-hidden="true" style={{ fontSize: "15px", lineHeight: 1 }}>⭐</span>
                {isSaved ? "Unsave" : "Save"}
              </button>
              <button
                type="button"
                role="menuitem"
                className="Community-dotmenu-item"
                onClick={() => run(onToggleArchive)}
              >
                <span aria-hidden="true">📁</span>
                {isArchived ? "Unarchive post" : "Archive post"}
              </button>
              <button
                type="button"
                role="menuitem"
                className="Community-dotmenu-item"
                onClick={() => run(() => onToggleHideLikes())}
              >
                <span aria-hidden="true">{isLikeCountHidden ? "❤️" : "🚫"}</span>
                {isLikeCountHidden ? "Show likes count" : "Hide likes count"}
              </button>
              <button
                type="button"
                role="menuitem"
                className="Community-dotmenu-item"
                onClick={() => run(() => onToggleHideCommentCounts())}
              >
                <span aria-hidden="true">{isCommentCountHidden ? "💬" : "🚫"}</span>
                {isCommentCountHidden ? "Show comment count" : "Hide comment count"}
              </button>
              <button
                type="button"
                role="menuitem"
                className="Community-dotmenu-item"
                onClick={() => run(() => onToggleLikesOff())}
              >
                <span aria-hidden="true">❤️</span>
                {isLikesOff ? "Turn on likes" : "Turn off likes"}
              </button>
              <button
                type="button"
                role="menuitem"
                className="Community-dotmenu-item"
                onClick={() => run(() => onToggleCommentsOff())}
              >
                <span aria-hidden="true">💬</span>
                {isCommentsOff ? "Turn on comments" : "Turn off comments"}
              </button>
              <button
                type="button"
                role="menuitem"
                className="Community-dotmenu-item Community-dotmenu-item-danger"
                onClick={() => run(onDelete)}
              >
                <span aria-hidden="true">🗑️</span>
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="Community-post-controls">
      <button
        type="button"
        className={`Community-follow-btn ${isFollowing ? "Community-following" : ""}`}
        onClick={onToggleFollow}
        aria-pressed={isFollowing}
        aria-label={isFollowing ? "Unfollow this creator" : "Follow this creator"}
      >
        {isFollowing ? (
          <>
            <span className="Community-follow-tick" aria-hidden="true">✓</span>
            Following
          </>
                  ) : (
                    <>
                      <span className="Community-follow-plus" aria-hidden="true">+</span>
                      Follow
                    </>
                  )}
      </button>

      <div className="Community-dotmenu" ref={menuRef}>
        <div className="Community-dotmenu-btns">
          <button
            ref={btnRef}
            type="button"
            className="Community-dotmenu-btn"
            onClick={() => setOpen((v) => !v)}
            aria-label="Post options"
            aria-haspopup="menu"
            aria-expanded={open}
          >
            ⋯
          </button>
        </div>
        {open && (
          <div className="Community-dotmenu-dropdown" role="menu">
            <button
              type="button"
              role="menuitem"
              className="Community-dotmenu-item"
              onClick={() => run(onToggleSave)}
            >
              <span aria-hidden="true" style={{ fontSize: "15px", lineHeight: 1 }}>⭐</span>
              {isSaved ? "Unsave" : "Save"}
            </button>
            <button
              type="button"
              role="menuitem"
              className="Community-dotmenu-item"
              onClick={() => run(onCopyLink)}
            >
              <span aria-hidden="true">🔗</span>
              Copy link
            </button>
            <button
              type="button"
              role="menuitem"
              className="Community-dotmenu-item"
              onClick={() => run(onHide)}
            >
              <span aria-hidden="true">👎</span>
              Not interested
            </button>
            <button
              type="button"
              role="menuitem"
              className="Community-dotmenu-item Community-dotmenu-item-danger"
              onClick={() => run(onReport)}
            >
              <span aria-hidden="true">⚠️</span>
              Report
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const DotsMenu = ({ items, label = "Options" }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div className="Community-dotmenu" ref={menuRef}>
      <div className="Community-dotmenu-btns">
        <button
          ref={btnRef}
          type="button"
          className="Community-dotmenu-btn"
          onClick={() => setOpen((v) => !v)}
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          ⋯
        </button>
      </div>
      {open && (
        <div className="Community-dotmenu-dropdown" role="menu">
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              className={`Community-dotmenu-item ${it.danger ? "Community-dotmenu-item-danger" : ""}`}
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
            >
              <span aria-hidden="true">{it.icon}</span>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ReportPostModal = ({
  post,
  reporterUserId,
  onClose,
  onSubmitReport,
}) => {
  const [reasonId, setReasonId] = useState("");
  const [otherText, setOtherText] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const reason = REPORT_REASONS.find((r) => r.id === reasonId);
  const isNc = reasonId === "nc_imagery";
  const isOther = reasonId === "other";
  const notSelected = reasonId === "";

  const canSubmit =
    confirmed &&
    (!isOther || otherText.trim().length > 0) &&
    (!notSelected) &&
    signature.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setTimeout(() => {
      onSubmitReport({
        postId: post.id,
        reportedUserId: post.posterId,
        reporterUserId,
        reason: reason ? reason.label : reasonId,
        reasonId,
        additionalReason: otherText.trim() || null,
        confirmationAccepted: confirmed,
        policyReviewAccepted: isNc ? confirmed : false,
        signature: signature.trim(),
        createdAt: new Date().toISOString(),
      });
      setSubmitting(false);
      setSent(true);
    }, 700);
  };

  useEffect(() => {
    if (sent) {
      const t = setTimeout(onClose, 1800);
      return () => clearTimeout(t);
    }
  }, [sent, onClose]);

  if (sent) {
    return (
      <Overlay title="Report submitted" onClose={onClose}>
        <div className="Community-report-success">
          <div className="Community-report-success-icon" aria-hidden="true">✓</div>
          <p className="Community-body-text">
            Thank you for helping keep our community safe. We’ll review the report according to our policies.
          </p>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay
      title="Report"
      onClose={onClose}
      panelClassName="Community-report-panel"
      closeOnlyByButton
    >
      <div className="Community-report-body">
        <div className="Community-report-reasons">
          <label htmlFor="Community-report-reason-select" className="Community-report-label">
            Select a reason
          </label>
          <select
            id="Community-report-reason-select"
            className="Community-input"
            value={reasonId}
            onChange={(e) => {
              setReasonId(e.target.value);
              setConfirmed(false);
            }}
          >
            <option value="">Select a reason...</option>
            {REPORT_REASONS.map((r) => (
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>
        </div>

        {isOther && (
          <div className="Community-report-other">
            <label htmlFor="Community-report-other" className="Community-report-label">
              Please tell us why you're reporting this post
            </label>
            <textarea
              id="Community-report-other"
              className="Community-textarea"
              rows={5}
              maxLength={REPORT_OTHER_MAX}
              value={otherText}
              onChange={(e) => {
                setOtherText(e.target.value);
                setConfirmed(false);
              }}
              placeholder="Explain your reason for reporting this post"
            />
            <p className="Community-report-char-count">
              {otherText.length}/{REPORT_OTHER_MAX}
            </p>
            <label className="Community-report-check">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              <span>I confirm that the information I have provided is accurate and that I am submitting this report in good faith.</span>
            </label>
          </div>
        )}

        {!isOther && !notSelected && (
          <label className="Community-report-check">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <span>I confirm that the information I have provided is accurate and that I am submitting this report in good faith.</span>
          </label>
        )}

        {confirmed && (
          <div className="Community-report-signature">
            <label htmlFor="Community-report-signature" className="Community-report-label">
              Digital signature
            </label>
            <input
              id="Community-report-signature"
              type="text"
              className="Community-input"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="Type your full name"
            />
          </div>
        )}
      </div>

      <div className="Community-report-actions">
        <PrimaryButton onClick={handleSubmit} disabled={!canSubmit || submitting || notSelected}>
          {submitting ? "Submitting..." : "Submit"}
        </PrimaryButton>
      </div>
    </Overlay>
  );
};

export const Overlay = ({ title, subtitle, onClose, children, wide, panelClassName = "", closeOnlyByButton = false, hideTitle = false }) => {
  const overlayRef = useRef(null);

  useEffect(() => {
    if (closeOnlyByButton) return undefined;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, closeOnlyByButton]);

  return (
    <div
      className="Community-overlay-backdrop"
      onMouseDown={(e) => {
        if (!closeOnlyByButton && e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        className={`Community-overlay-panel ${wide ? "Community-overlay-wide" : ""} ${panelClassName}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="Community-overlay-title"
        ref={overlayRef}
      >
        <div className="Community-overlay-header">
          {!hideTitle && <h2 id="Community-overlay-title" className="Community-heading-section">
            {title}
          </h2>}
          {subtitle && <p className="Community-subheading">{subtitle}</p>}
          <button
            type="button"
            className="Community-overlay-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="Community-overlay-body">{children}</div>
      </div>
    </div>
  );
};

const EmptyState = ({ title, message }) => (
  <div className="Community-empty-state">
    <h3 className="Community-heading-section">{title}</h3>
    <p className="Community-body-text">{message}</p>
  </div>
);

/* ============================================================
   Community Posts & Events
   ============================================================ */

const EmojiPalette = [
  "😀", "😂", "😍", "😎", "🤔", "👍", "👏", "🙌", "🎉", "🥳",
  "❤️", "💡", "🤝", "🔥", "🎯", "📅", "📚", "💼", "🏆", "🌟",
];

const GIPHY_API_KEY = "YOUR_GIPHY_API_KEY";
const GIPHY_TRENDING_URL = "https://api.giphy.com/v1/gifs/trending";
const GIPHY_SEARCH_URL = "https://api.giphy.com/v1/gifs/search";

const FALLBACK_GIFS = [
  { id: "fb1", media: "https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy-downsized.gif", preview: "https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif" },
  { id: "fb2", media: "https://media.giphy.com/media/l0HlNaQ6gWfllcjDO/giphy-downsized.gif", preview: "https://media.giphy.com/media/l0HlNaQ6gWfllcjDO/giphy.gif" },
  { id: "fb3", media: "https://media.giphy.com/media/13CoXDiaCcCoyk/giphy-downsized.gif", preview: "https://media.giphy.com/media/13CoXDiaCcCoyk/giphy.gif" },
  { id: "fb4", media: "https://media.giphy.com/media/l3q2K5jinAlChoCLS/giphy-downsized.gif", preview: "https://media.giphy.com/media/l3q2K5jinAlChoCLS/giphy.gif" },
  { id: "fb5", media: "https://media.giphy.com/media/xT0xeJpnrWC4XWblEk/giphy-downsized.gif", preview: "https://media.giphy.com/media/xT0xeJpnrWC4XWblEk/giphy.gif" },
  { id: "fb6", media: "https://media.giphy.com/media/3oEjHV0z8S7WM4MwnK/giphy-downsized.gif", preview: "https://media.giphy.com/media/3oEjHV0z8S7WM4MwnK/giphy.gif" },
  { id: "fb7", media: "https://media.giphy.com/media/4Zo41lhzKt6iZ8xff9/giphy-downsized.gif", preview: "https://media.giphy.com/media/4Zo41lhzKt6iZ8xff9/giphy.gif" },
  { id: "fb8", media: "https://media.giphy.com/media/YRvxMINpG5Q9W/giphy-downsized.gif", preview: "https://media.giphy.com/media/YRvxMINpG5Q9W/giphy.gif" },
  { id: "fb9", media: "https://media.giphy.com/media/11sBLVxNsQv6Te/giphy-downsized.gif", preview: "https://media.giphy.com/media/11sBLVxNsQv6Te/giphy.gif" },
  { id: "fb10", media: "https://media.giphy.com/media/l2JegQCHQ2gsqWdbC/giphy-downsized.gif", preview: "https://media.giphy.com/media/l2JegQCHQ2gsqWdbC/giphy.gif" },
];

const GifPicker = ({ onSelect, onClose }) => {
  const [query, setQuery] = useState("");
  const [gifs, setGifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const searchInputRef = useRef(null);

  const toGif = (g) => ({
    id: g.id,
    media: g.images?.fixed_height?.url || g.media_formats?.[0]?.url || g.url,
    preview: g.images?.preview_gif?.url || g.images?.fixed_height_small?.url || "",
  });

  const load = useCallback(async (term) => {
    setLoading(true);
    setError("");
    try {
      let url;
      if (term) {
        url = `${GIPHY_SEARCH_URL}?api_key=${encodeURIComponent(GIPHY_API_KEY)}&q=${encodeURIComponent(term)}&limit=20&rating=g&lang=en`;
      } else {
        url = `${GIPHY_TRENDING_URL}?api_key=${encodeURIComponent(GIPHY_API_KEY)}&limit=20&rating=g`;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error("Giphy request failed");
      const data = await res.json();
      const items = (data.data || []).map(toGif);
      setGifs(items.length ? items : FALLBACK_GIFS);
    } catch (e) {
      setError("GIF service unavailable — showing sample GIFs.");
      setGifs(FALLBACK_GIFS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load("");
  }, [load]);

  const debounceTimer = useRef(null);
  const handleSearch = (term) => {
    setQuery(term);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => load(term.trim()), 400);
  };

  return (
    <div className="Community-gif-picker">
      <div className="Community-gif-picker-top">
        <input
          ref={searchInputRef}
          type="text"
          className="Community-input Community-gif-search"
          placeholder="Search GIFs..."
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <button type="button" className="Community-gif-close" onClick={onClose} aria-label="Close GIF picker">
          ×
        </button>
      </div>
      {error && <p className="Community-gif-notice">{error}</p>}
      {loading ? (
        <p className="Community-gif-notice">Loading GIFs...</p>
      ) : (
        <div className="Community-gif-grid">
          {gifs.map((g) => (
            <button key={g.id} type="button" className="Community-gif-item" onClick={() => onSelect(g)}>
              <img src={g.preview || g.media} alt="GIF" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MediaUploader = ({ label = "Add media", photos, videos, onPhotosChange, onVideosChange, idPrefix, maxTotal = POST_MAX_PHOTOS, onLimitExceeded, onSelectMedia }) => {
  const MAX = maxTotal;
  const [limitError, setLimitError] = useState("");

  const removePhoto = (idx) => {
    onPhotosChange(photos.filter((_, i) => i !== idx));
    setLimitError("");
  };

  const removeVideo = (idx) => {
    onVideosChange(videos.filter((_, i) => i !== idx));
    setLimitError("");
  };

  return (
    <div className="Community-media-uploader">
      {label && <span className={`Community-label Community-post-label-lg`}>{label}</span>}

      {limitError && <p className="Community-error-text">{limitError}</p>}

      {(photos.length > 0 || videos.length > 0) && (
        <div className="Community-mixed-media-grid">
          {photos.map((p, i) => (
            <div key={`${idPrefix}-photo-${i}`} className="Community-photo-preview" onClick={() => onSelectMedia && onSelectMedia(i)}>
              <img src={p} alt={`Preview ${i + 1}`} />
              <button
                type="button"
                className="Community-media-remove"
                onClick={(e) => { e.stopPropagation(); removePhoto(i); }}
                aria-label="Remove photo"
              >
                &times;
              </button>
            </div>
          ))}
          {videos.map((v, i) => (
            <div key={`${idPrefix}-video-${i}`} className="Community-photo-preview Community-video-preview-tile" onClick={() => onSelectMedia && onSelectMedia(photos.length + i)}>
              <video src={v} muted />
              <button
                type="button"
                className="Community-media-remove"
                onClick={(e) => { e.stopPropagation(); removeVideo(i); }}
                aria-label="Remove video"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PostVisibilityDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const options = [
    { value: POST_VISIBILITY.ANYONE, title: "Anyone", desc: "Anyone can see this post" },
    { value: POST_VISIBILITY.CONNECTIONS, title: "Connections only", desc: "Only your connections can see this post" },
  ];

  return (
    <div className="Community-dropdown" ref={ref}>
      <button
        type="button"
        className="Community-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span>Visible</span>
        <span aria-hidden="true">▼</span>
      </button>
      {open && (
        <div className="Community-dropdown-menu" role="menu">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="menuitemradio"
              aria-checked={value === o.value}
              className={`Community-dropdown-option ${value === o.value ? "Community-dropdown-selected" : ""}`}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              <span className="Community-dropdown-option-text">
                <span className="Community-dropdown-option-title">{o.title}</span>
                <span className="Community-dropdown-option-desc">{o.desc}</span>
              </span>
              {value === o.value && <span className="Community-dropdown-check" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CommentSettingsDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const options = [
    { value: COMMENT_PERMISSION.ANYONE, title: "Anyone" },
    { value: COMMENT_PERMISSION.CONNECTIONS, title: "Connections only" },
    { value: COMMENT_PERMISSION.OFF, title: "Comments off" },
  ];

  return (
    <div className="Community-dropdown" ref={ref}>
      <button
        type="button"
        className="Community-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span>Comment</span>
        <span aria-hidden="true">▼</span>
      </button>
      {open && (
        <div className="Community-dropdown-menu" role="menu">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="menuitemradio"
              aria-checked={value === o.value}
              className={`Community-dropdown-option ${value === o.value ? "Community-dropdown-selected" : ""}`}
              onClick={() => {
                onChange(o.value);
              }}
            >
              <span className="Community-dropdown-option-text">
                <span className="Community-dropdown-option-title">{o.title}</span>
              </span>
              {value === o.value && <span className="Community-dropdown-check" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const HideEngagementDropdown = ({ value, onChange, editMode = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const options = [
    { key: "hideLikes", icon: "❤️", title: "Hide likes" },
    { key: "hideLikeCount", icon: "🔢", title: "Hide like count" },
    { key: "hideCommentCount", icon: "💬", title: "Hide comment count" },
  ].filter((o) => !editMode || o.key === "hideLikes");

  const activeCount = options.filter((o) => value[o.key]).length;

  const toggle = (key, checked) => {
    onChange(key, checked);
  };

  return (
    <div className="Community-dropdown" ref={ref}>
      <button
        type="button"
        className="Community-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span>Hide{activeCount > 0 ? ` (${activeCount})` : ""}</span>
        <span aria-hidden="true">▼</span>
      </button>
      {open && (
        <div className="Community-dropdown-menu" role="menu">
          {options.map((o) => (
            <label
              key={o.key}
              className={`Community-dropdown-option Community-hide-option ${value[o.key] ? "Community-dropdown-selected" : ""}`}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={Boolean(value[o.key])}
                onChange={(e) => toggle(o.key, e.target.checked)}
              />
              <span className="Community-dropdown-option-text">
                <span className="Community-dropdown-option-title">{o.title}</span>
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const CommentSortDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const options = [
    { value: "recent", title: "Most Recent" },
    { value: "relevant", title: "Most Relevant" },
  ];

  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div className="Community-comment-filterbox">
      <div className="Community-dropdown" ref={ref}>
        <button
          type="button"
          className="Community-dropdown-trigger Community-comment-sort-trigger"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="true"
          aria-expanded={open}
        >
          <span>{selected.title}</span>
          <span aria-hidden="true">▼</span>
        </button>
        {open && (
          <div className="Community-dropdown-menu" role="menu">
            <p className="Community-comment-sort-heading">Sort by</p>
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                role="menuitemradio"
                aria-checked={value === o.value}
                className={`Community-comment-sort-option ${value === o.value ? "Community-comment-sort-option-active" : ""}`}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.title}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SharePostCard = ({ onStartPost, onAddPhoto, onAddVideo, onAddEvent }) => {
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = "";
    onAddPhoto(files);
  };

  const handleVideos = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = "";
    onAddVideo(files);
  };

  return (
    <div className="Community-sharepost-card">
      <div className="Community-sharepost-content">
        <div className="Community-sharepost-pill-row">
          <span className="Community-sharepost-avatar" aria-hidden="true">
            {getMemberById(CURRENT_USER_ID)?.avatar}
          </span>
          <button type="button" className="Community-sharepost-pill" onClick={onStartPost}>
            <span className="Community-sharepost-pill-text">Start a post</span>
          </button>
        </div>
        <div className="Community-sharepost-actions">
        <button type="button" className="Community-sharepost-action" onClick={() => videoInputRef.current && videoInputRef.current.click()}>
          <svg className="Community-sharepost-icon" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <defs>
              <linearGradient id="shareVideoIcon" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7dd3fc" />
                <stop offset="100%" stopColor="#0369a1" />
              </linearGradient>
            </defs>
            <path d="M23 7l-7 5 7 5V7z" fill="#e11d48" />
            <path d="M23 7l-7 5 7 5V7z" fill="#f43f5e" fillOpacity="0.6" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" fill="url(#shareVideoIcon)" />
            <rect x="1" y="5" width="15" height="3" rx="1.5" fill="#bae6fd" />
          </svg>
          Video
        </button>
        <button type="button" className="Community-sharepost-action" onClick={() => photoInputRef.current && photoInputRef.current.click()}>
          <svg className="Community-sharepost-icon" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <defs>
              <linearGradient id="sharePhotoIcon" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fdba74" />
                <stop offset="100%" stopColor="#ea580c" />
              </linearGradient>
            </defs>
            <rect x="3" y="5" width="18" height="14" rx="2" ry="2" fill="url(#sharePhotoIcon)" />
            <rect x="3" y="5" width="18" height="4" rx="2" fill="#ffedd5" />
            <circle cx="8.5" cy="10.5" r="1.6" fill="#7f1d1d" />
            <path d="M21 15l-5-5L5 21" fill="#fff7ed" />
          </svg>
          Photo
        </button>
        <button type="button" className="Community-sharepost-action" onClick={onAddEvent}>
          <svg className="Community-sharepost-icon" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <defs>
              <linearGradient id="shareEventIcon" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#a5f3fc" />
                <stop offset="100%" stopColor="#0891b2" />
              </linearGradient>
            </defs>
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="url(#shareEventIcon)" />
            <line x1="16" y1="2" x2="16" y2="6" stroke="#cffafe" strokeWidth="2" strokeLinecap="round" />
            <line x1="8" y1="2" x2="8" y2="6" stroke="#cffafe" strokeWidth="2" strokeLinecap="round" />
            <rect x="3" y="10" width="18" height="3" fill="#22d3ee" />
          </svg>
          Event
        </button>
        </div>
      </div>
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        multiple
        className="Community-file-input"
        onChange={handlePhotos}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        className="Community-file-input"
        onChange={handleVideos}
      />
    </div>
  );
};

/* ---------- Event validation ---------- */
const validateEvent = (event) => {
  const errs = {};
  if (!event.eventType) errs.eventType = "Select an event type.";
  if (!event.name.trim()) errs.name = "Event name is required.";
  else if (wordCount(event.name) > EVENT_WORD_MAX) errs.name = `Event name cannot exceed ${EVENT_WORD_MAX} words.`;
  if (!event.timezone) errs.timezone = "Select a time zone.";
  if (!event.startDate) errs.startDate = "Select a start date.";
  if (!event.startTime) errs.startTime = "Select a start time.";
  if (event.hasEndDate) {
    if (!event.endDate) errs.endDate = "Select an end date.";
    if (!event.endTime) errs.endTime = "Select an end time.";
  }
  if (event.eventType === EVENT_TYPE.IN_PERSON) {
    if (!event.address.trim()) errs.address = "Event address is required.";
    if (!event.venue.trim()) errs.venue = "Event venue is required.";
  }
  if (event.description.trim().split(/\s+/).filter(Boolean).length > EVENT_DESC_MAX) errs.description = `Description cannot exceed ${EVENT_DESC_MAX} words.`;
  if (event.externalLink.length > EVENT_LINK_MAX) errs.externalLink = `External link cannot exceed ${EVENT_LINK_MAX} characters.`;
  if (event.externalLink && !isValidUrl(event.externalLink)) errs.externalLink = "Enter a valid URL.";
  if (event.coverPhotos.length + (event.coverVideo ? 1 : 0) + normalizeYoutube(event.youtube).length > EVENT_COVER_MAX_MEDIA) {
    errs.coverImage = `You can add up to ${EVENT_COVER_MAX_MEDIA} cover items in total (photos + one video).`;
  }
  return errs;
};

const buildCoverMedia = (event) => {
  const photos = event.coverPhotos || [];
  const ys = normalizeYoutube(event.youtube);
  const hasVideo = !!event.coverVideo;
  if (Array.isArray(event.coverItems) && event.coverItems.length > 0) {
    return event.coverItems.map((it) => (
      it.type === "photo"
        ? { type: "photo", src: it.src }
        : it.type === "video"
          ? { type: "video", src: it.src, edits: it.edits || null }
          : { type: "youtube", src: it.src || it.embed, url: it.url || "", thumb: it.thumb || getYoutubeThumb(it.src || it.embed), embed: it.embed || it.src }
    ));
  }
  const base = photos.length + (hasVideo ? 1 : 0);
  const total = base + ys.length;
  if (total === 0) return [];
  let vi = hasVideo ? (typeof event.coverVideoIndex === "number" ? event.coverVideoIndex : photos.length) : -1;
  if (vi > total - 1) vi = total - 1;
  let pc = 0;
  let yc = 0;
  const out = [];
  for (let i = 0; i < total; i++) {
    if (hasVideo && i === vi) out.push({ type: "video", src: event.coverVideo, edits: event.coverVideoEdits || null });
    else if (yc < ys.length && i >= base) {
      out.push({ type: "youtube", src: ys[yc].embed, url: ys[yc].url, thumb: ys[yc].thumb, embed: ys[yc].embed });
      yc += 1;
    }
    else out.push({ type: "photo", src: photos[pc++] });
  }
  return out;
};

const rebuildCoverState = (orderedItems) => {
  const video = orderedItems.find((i) => i.type === "video");
  return {
    coverPhotos: orderedItems.filter((i) => i.type === "photo").map((i) => i.src),
    coverVideo: video ? video.src : "",
    coverVideoIndex: video ? orderedItems.indexOf(video) : -1,
    coverVideoEdits: video ? video.edits || null : null,
    youtube: orderedItems.filter((i) => i.type === "youtube").map((i) => ({ url: i.url || "", embed: i.src, thumb: i.thumb || getYoutubeThumb(i.src) })),
    coverItems: orderedItems.map((i) => (
      i.type === "photo"
        ? { type: "photo", src: i.src }
        : i.type === "video"
          ? { type: "video", src: i.src, edits: i.edits || null }
          : { type: "youtube", src: i.src, url: i.url || "", thumb: i.thumb || getYoutubeThumb(i.src), embed: i.embed || i.src }
    )),
  };
};

const CreateEventModal = ({ onClose, onCreate, initialEvent }) => {
  const [event, setEvent] = useState(() => buildEvent(initialEvent));
  const [coverInputError, setCoverInputError] = useState("");
  const [errors, setErrors] = useState({});
  const [showCoverEditor, setShowCoverEditor] = useState(null);
  const [coverImageCorners, setCoverImageCorners] = useState({});
  const [showCoverVideoEditor, setShowCoverVideoEditor] = useState(false);
  const [showEventEmoji, setShowEventEmoji] = useState(false);
  const [showNameFont, setShowNameFont] = useState(false);
  const [showTitleFont, setShowTitleFont] = useState(false);
  const [showDescFont, setShowDescFont] = useState(false);
  const [coverIndex, setCoverIndex] = useState(0);
  const [showCoverLimit, setShowCoverLimit] = useState(false);
  const [showCoverThumbOverlay, setShowCoverThumbOverlay] = useState(false);
  const [showEventThumbPreview, setShowEventThumbPreview] = useState(false);
  const [coverThumbnail, setCoverThumbnail] = useState(initialEvent ? initialEvent.coverThumbnail || null : null);
  const [coverThumbnails, setCoverThumbnails] = useState(initialEvent && initialEvent.coverThumbnails && initialEvent.coverThumbnails.length ? initialEvent.coverThumbnails : []);
  const [showLocOverlay, setShowLocOverlay] = useState(false);
  const [locTarget, setLocTarget] = useState("address");
  const [showTagOverlay, setShowTagOverlay] = useState(false);
  const [showSocialOverlay, setShowSocialOverlay] = useState(false);
  const [youtubeOpen, setYoutubeOpen] = useState(false);
  const [youtubeLink, setYoutubeLink] = useState("");
  const [youtubeError, setYoutubeError] = useState("");
  const [dragCoverFrom, setDragCoverFrom] = useState(null);
  const coverUploadInputRef = useRef(null);
  const pendingCoverThumbRef = useRef(null);

  const TITLE_COLORS = [
    "#e63946", "#e76f51", "#f4a261", "#2a9d8f",
    "#457b9d", "#6d597a",
  ];

  const insertDescEmoji = (emoji) => {
    if ((event.description + emoji).length > EVENT_DESC_MAX) return;
    setField("description", event.description + emoji);
  };

  const setField = (key, value) => {
    setEvent((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const renderFontTool = ({ text, colorKey, weightKey, sizeKey, italicKey, underlineKey, fontFamilyKey, maxSize, open, setOpen, showButton = true }) => {
    const sizeOptions = [];
    for (let s = 14; s <= maxSize; s++) sizeOptions.push(s);
    return (
      <>
        {showButton && (
        <button
          type="button"
          className="Community-font-btn"
          onClick={() => setOpen((v) => !v)}
          aria-label="Text style"
          title="Text style"
        >
          <svg viewBox="0 0 512 512" width="16" height="16" aria-hidden="true">
            <defs>
              <linearGradient id="abFontGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#861b68" />
                <stop offset="100%" stopColor="#e0245e" />
              </linearGradient>
            </defs>
            <path fill="url(#abFontGradient)" d="M488.1 434.6H462L316.1 30.2C311.2 15.7 297.6 6 282.3 6h-52.6c-15.3 0-28.9 9.7-33.8 24.2L50 434.6H23.9c-9.9 0-17.9 8-17.9 17.9v35.7C6 498 14 506 23.9 506h142.9c9.9 0 17.9-8 17.9-17.9v-35.7c0-9.9-8-17.9-17.9-17.9h-21.9l26-71.4h170.3l26 71.4h-21.9c-9.9 0-17.9 8-17.9 17.9v35.7c0 9.9 8 17.9 17.9 17.9h142.9c9.9 0 17.9-8 17.9-17.9v-35.7c-.1-9.8-8.1-17.8-18-17.8zM203.4 273.9 256 129.3l52.6 144.5H203.4v.1z" />
          </svg>
        </button>
        )}
        {open && (
          <>
            <div className="Community-font-backdrop" onClick={() => setOpen(false)} />
            <div className="Community-font-overlay">
            <p
              className="Community-font-preview"
              style={fontRenderStyle({ color: event[colorKey], weight: event[weightKey], size: event[sizeKey], italic: event[italicKey], underline: event[underlineKey], fontFamily: fontFamilyKey ? event[fontFamilyKey] : "" })}
            >
              {text || "Your text"}
            </p>
            <div className="Community-font-biu-row">
              <button
                type="button"
                className={`Community-font-biu ${event[weightKey] >= 700 ? "Community-font-biu-active" : ""}`}
                onClick={() => setField(weightKey, event[weightKey] >= 700 ? 400 : 700)}
                title="Bold"
                aria-label="Bold"
              >B</button>
              <button
                type="button"
                className={`Community-font-biu ${event[italicKey] ? "Community-font-biu-active" : ""}`}
                onClick={() => setField(italicKey, !event[italicKey])}
                title="Italic"
                aria-label="Italic"
              >I</button>
              <button
                type="button"
                className={`Community-font-biu ${event[underlineKey] ? "Community-font-biu-active" : ""}`}
                onClick={() => setField(underlineKey, !event[underlineKey])}
                title="Underline"
                aria-label="Underline"
              >U</button>
            </div>
            <div className="Community-font-controls">
              <div className="Community-font-control">
                <span className="Community-font-label">Font Size</span>
                <select
                  className="Community-input Community-font-select"
                  value={event[sizeKey]}
                  onChange={(e) => setField(sizeKey, Number(e.target.value))}
                >
                  {sizeOptions.map((s) => <option key={s} value={s}>{s}px</option>)}
                </select>
              </div>
              <div className="Community-font-color-block">
                <span className="Community-font-label">Color</span>
                <div className="Community-font-colors">
                  <button
                    type="button"
                    className={`Community-title-color-swatch ${event[colorKey] === "#000000" ? "Community-title-color-active" : ""}`}
                    style={{ background: "#000000" }}
                    onClick={() => setField(colorKey, "#000000")}
                    aria-label="Color #000000"
                  />
                  {TITLE_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`Community-title-color-swatch ${event[colorKey] === c ? "Community-title-color-active" : ""}`}
                      style={{ background: c }}
                      onClick={() => setField(colorKey, c)}
                      aria-label={`Color ${c}`}
                    />
                  ))}
                  <input
                    type="color"
                    className="Community-title-color-swatch Community-custom-color-swatch"
                    value={isGradientColor(event[colorKey]) ? "#861b68" : (event[colorKey] || "#1a1a1a")}
                    onChange={(e) => setField(colorKey, e.target.value)}
                    aria-label="Custom color"
                    title="Custom color"
                  />
                </div>
              </div>
            </div>
            <button type="button" className="Community-font-done" onClick={() => setOpen(false)}>Done</button>
            </div>
          </>
        )}
      </>
    );
  };

  const handleCoverFiles = (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    const images = arr.filter((f) => f.type.startsWith("image/"));
    const videos = arr.filter((f) => f.type.startsWith("video/"));
    if (images.length > 0) handleCoverPhotos(images);
    if (videos.length > 0) handleCoverVideo(videos[0]);
  };

  const handleCoverPhotos = (files) => {
    if (!files || files.length === 0) return;
    const picked = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (picked.length === 0) return;
    const currentCount = event.coverPhotos.length + (event.coverVideo ? 1 : 0) + (event.youtube || []).length;
    const room = Math.max(0, EVENT_COVER_MAX_MEDIA - currentCount);
    if (room <= 0 || picked.length > room) {
      setShowCoverLimit(true);
      return;
    }
    const selected = picked.slice(0, room);
    const urls = selected.map((f) => URL.createObjectURL(f));
    let checked = 0;
    const good = [];
    const finish = () => {
      if (good.length === 0) {
        setCoverInputError(`Cover images must be at least ${EVENT_COVER_MIN_WIDTH}px wide.`);
        return;
      }
setCoverInputError("");
    setEvent((prev) => {
      const media = buildCoverMedia(prev);
      good.forEach((src) => media.push({ type: "photo", src }));
      return { ...prev, ...rebuildCoverState(media) };
    });
  };
    urls.forEach((url) => {
      const img = new Image();
      img.onload = () => {
        if (img.width >= EVENT_COVER_MIN_WIDTH) good.push(url);
        checked += 1;
        if (checked === urls.length) finish();
      };
      img.onerror = () => {
        checked += 1;
        if (checked === urls.length) finish();
      };
      img.src = url;
    });
  };

  const handleCoverVideo = (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) return;
    const currentCount = event.coverPhotos.length + (event.coverVideo ? 1 : 0) + (event.youtube || []).length;
    if (currentCount >= EVENT_COVER_MAX_MEDIA) {
      setShowCoverLimit(true);
      return;
    }
    if (event.coverVideo) {
      setCoverInputError("Only one video is allowed. Remove the current video to add a different one.");
      return;
    }
    setCoverInputError("");
    setEvent((prev) => {
      const media = buildCoverMedia(prev);
      media.push({ type: "video", src: URL.createObjectURL(file), edits: null });
      return { ...prev, ...rebuildCoverState(media) };
    });
  };

  const addEventYoutube = (value) => {
    const parsed = parseYoutubeLink(value);
    if (!parsed) {
      setYoutubeError("Enter a valid YouTube link.");
      return;
    }
    const currentCount = event.coverPhotos.length + (event.coverVideo ? 1 : 0) + (event.youtube || []).length;
    if (currentCount >= EVENT_COVER_MAX_MEDIA) {
      setShowCoverLimit(true);
      return;
    }
    if ((event.youtube || []).some((y) => y.embed === parsed.embed)) {
      setYoutubeError("This YouTube video is already added.");
      return;
    }
    setEvent((prev) => {
      const media = buildCoverMedia(prev);
      media.push({ type: "youtube", src: parsed.embed, url: parsed.url, thumb: parsed.thumb, embed: parsed.embed });
      return { ...prev, ...rebuildCoverState(media) };
    });
    setYoutubeLink("");
    setYoutubeError("");
    setYoutubeOpen(false);
    setCoverIndex(currentCount);
  };

  const removeCoverAt = (mediaIdx) => {
    setEvent((prev) => {
      const media = buildCoverMedia(prev);
      if (mediaIdx < 0 || mediaIdx >= media.length) return prev;
      media.splice(mediaIdx, 1);
      const rebuilt = rebuildCoverState(media);
      return { ...prev, ...rebuilt };
    });
    setCoverInputError("");
  };

  const applyCoverEdit = (dataUrl, idx, corners = 0) => {
    setEvent((prev) => {
      const media = buildCoverMedia(prev);
      let p = 0;
      const next = media.map((m) => {
        if (m.type === "photo") {
          const cur = p;
          p += 1;
          if (cur === idx) return { ...m, src: dataUrl };
        }
        return m;
      });
      return { ...prev, ...rebuildCoverState(next) };
    });
    setCoverImageCorners((prev) => ({ ...prev, [dataUrl]: corners }));
    setShowCoverEditor(null);
  };

  const coverMedia = buildCoverMedia(event);
  const hasCoverVideo = !!event.coverVideo;
  const coverVideoIdx = coverMedia.findIndex((i) => i.type === "video");
  const safeCoverIndex = Math.min(coverIndex, Math.max(0, coverMedia.length - 1));
  const currentCover = coverMedia[safeCoverIndex];
  const coverPhotoArrayIdx = currentCover && currentCover.type === "photo"
    ? safeCoverIndex - (hasCoverVideo && coverVideoIdx < safeCoverIndex ? 1 : 0)
    : -1;
  const coverPrev = (e) => { e.stopPropagation(); setCoverIndex((coverIndex - 1 + coverMedia.length) % coverMedia.length); };
  const coverNext = (e) => { e.stopPropagation(); setCoverIndex((coverIndex + 1) % coverMedia.length); };
  const removeCurrentCover = (e) => {
    e.stopPropagation();
    if (!currentCover) return;
    removeCoverAt(safeCoverIndex);
  };
  const applyCoverOrder = (target) => {
    if (!currentCover) return;
    if (target < 1 || target > EVENT_COVER_MAX_MEDIA) return;
    const tgtIdx = Math.min(target - 1, coverMedia.length - 1);
    if (tgtIdx === safeCoverIndex) return;
    const media = [...coverMedia];
    const [moved] = media.splice(safeCoverIndex, 1);
    media.splice(tgtIdx, 0, moved);
    setEvent((prev) => ({ ...prev, ...rebuildCoverState(media) }));
    setCoverIndex(tgtIdx);
  };
  const reorderCover = (from, to) => {
    if (from === null || to === null || from === to) return;
    setEvent((prev) => {
      const media = buildCoverMedia(prev);
      if (from < 0 || from >= media.length || to < 0 || to >= media.length) return prev;
      const list = [...media];
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      return { ...prev, ...rebuildCoverState(list) };
    });
    setCoverIndex(to);
  };

  const handleCoverOrderKeyDown = (e) => {
    e.stopPropagation();
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      applyCoverOrder(parseInt(e.key, 10));
    } else if (e.key !== "Backspace" && e.key !== "Delete" && e.key !== "Tab") {
      e.preventDefault();
    }
  };

  const handleSubmit = () => {
    const errs = validateEvent(event);
    delete errs.coverImage;
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (event.coverVideo) {
      if (initialEvent) {
        setShowEventThumbPreview(true);
      } else {
        pendingCoverThumbRef.current = event;
        setCoverThumbnail(null);
        setCoverThumbnails([]);
        setShowCoverThumbOverlay(true);
      }
      return;
    }
    onCreate(event);
  };

  const inPerson = event.eventType === EVENT_TYPE.IN_PERSON;

  return (
    <Overlay title={initialEvent ? "Edit Event" : "Create an Event"} onClose={onClose} wide closeOnlyByButton panelClassName={`Community-overlay-event${initialEvent ? " Community-overlay-event-editing" : ""}`}>
      <div className="Community-event-form">
        <div className={`Community-field${errors.eventType ? " Community-field--error" : ""}`}>
          <div className="Community-event-type-options">
            {!initialEvent && <span className="Community-event-type-slider" aria-hidden="true"></span>}
            <button
              type="button"
              className={`Community-event-type-card ${event.eventType === EVENT_TYPE.ONLINE ? "Community-event-type-active" : ""}`}
              onClick={() => { if (!initialEvent) setField("eventType", EVENT_TYPE.ONLINE); }}
              disabled={!!initialEvent}
            >
              <span className="Community-event-type-title">Online</span>
            </button>
            <button
              type="button"
              className={`Community-event-type-card ${event.eventType === EVENT_TYPE.IN_PERSON ? "Community-event-type-active" : ""}`}
              onClick={() => { if (!initialEvent) setField("eventType", EVENT_TYPE.IN_PERSON); }}
              disabled={!!initialEvent}
            >
              <span className="Community-event-type-title">Offline</span>
            </button>
          </div>
        </div>

        <div className="Community-field">
          <span className="Community-label-row">
            <span className="Community-label">Cover Media</span>
            {!initialEvent && (
              <button
                type="button"
                className={`Community-comment-tool-btn Community-post-action-btn${(event.youtube || []).length > 0 ? " Community-post-action-btn--active" : ""}`}
                onClick={() => { setYoutubeOpen((v) => !v); setYoutubeError(""); }}
                aria-label="Add YouTube video"
                aria-expanded={youtubeOpen}
                title="Add YouTube video"
              >
                <span className="Community-post-action-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FF0000" />
                  </svg>
                </span>
                <span className="Community-post-action-text">YouTube</span>
                {(event.youtube || []).length > 0 && <span className="Community-post-action-arrow" aria-hidden="true">&#8250;</span>}
              </button>
            )}
          </span>
          {youtubeOpen && !initialEvent && (
            <div className="Community-post-youtube-row">
              <div className="Community-post-youtube-row-inner">
                <input
                  type="url"
                  className="Community-post-youtube-input"
                  placeholder="Paste YouTube link"
                  value={youtubeLink}
                  onChange={(e) => {
                    const v = e.target.value;
                    setYoutubeLink(v);
                    if (parseYoutubeLink(v)) addEventYoutube(v);
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    if (parseYoutubeLink(pasted)) {
                      e.preventDefault();
                      addEventYoutube(pasted);
                    } else {
                      setYoutubeError("Enter a valid YouTube link.");
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEventYoutube(youtubeLink); } }}
                />
              </div>
              {youtubeError && <p className="Community-post-youtube-error">{youtubeError}</p>}
            </div>
          )}
          <div
            className={`Community-image-preview-wrap Community-cover-stage ${coverMedia.length > 0 ? "Community-cover-stage--hasmedia" : ""}${initialEvent ? " Community-cover-stage--editing" : ""}`}
            {...(coverMedia.length === 0 ? { role: "button", tabIndex: 0 } : {})}
            aria-label="Upload cover photo or video"
            onClick={() => { if (!initialEvent && coverMedia.length === 0) coverUploadInputRef.current && coverUploadInputRef.current.click(); }}
            onKeyDown={(e) => {
              if (!initialEvent && coverMedia.length === 0 && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                coverUploadInputRef.current && coverUploadInputRef.current.click();
              }
            }}
          >
            {coverMedia.length > 0 ? (
              <>
                <div className="Community-post-media-carousel" onClick={(e) => e.stopPropagation()}>
                  {currentCover.type === "video" ? (
                    currentCover.edits ? (
                      <EditedVideo src={currentCover.src} edits={currentCover.edits} controls className="Community-image-preview" />
                    ) : (
                      <video src={currentCover.src} className="Community-image-preview" controls playsInline />
                    )
                  ) : currentCover.type === "youtube" ? (
                    <iframe src={currentCover.src} className="Community-post-youtube-embed Community-image-preview" title="YouTube preview" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
                  ) : (
                    <img src={currentCover.src} alt={`Cover ${safeCoverIndex + 1}`} className="Community-image-preview" />
                  )}
                  {coverMedia.length > 1 && (
                    <>
                      <button type="button" className="Community-post-nav Community-post-nav-left" onClick={coverPrev} aria-label="Previous cover">‹</button>
                      <button type="button" className="Community-post-nav Community-post-nav-right" onClick={coverNext} aria-label="Next cover">›</button>
                    </>
                  )}
                </div>
                {!initialEvent && (
                  <button
                    type="button"
                    className="Community-image-delete-btn"
                    aria-label="Remove cover"
                    onClick={removeCurrentCover}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
                {(currentCover.type === "photo" || currentCover.type === "video") && (
                  <button
                    type="button"
                    className="Community-image-edit-btn"
                    aria-label={currentCover.type === "photo" ? "Edit cover image" : "Edit cover video"}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentCover.type === "photo") setShowCoverEditor(coverPhotoArrayIdx);
                      else setShowCoverVideoEditor(true);
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                    </svg>
                  </button>
                )}
                {!initialEvent && (
                <button
                  type="button"
                  className="Community-image-add-btn"
                  aria-label="Add photos or video"
                  onClick={(e) => { e.stopPropagation(); coverUploadInputRef.current && coverUploadInputRef.current.click(); }}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
              )}
              </>
            ) : (
              <div className="Community-image-placeholder">
                <p className="Community-body-text">
                  Upload cover photo (min {EVENT_COVER_MIN_WIDTH}px) or video
                </p>
              </div>
            )}
            {!initialEvent && coverMedia.length === 0 && (
              <div className="Community-upload-inline">
                <SecondaryButton onClick={(e) => { e.stopPropagation(); coverUploadInputRef.current && coverUploadInputRef.current.click(); }}>
                  Upload
                </SecondaryButton>
              </div>
            )}
            {event.taggedPeople && event.taggedPeople.length > 0 && (
              <button
                type="button"
                className="Community-post-tag-btn Community-post-tag-btn-bottom"
                onClick={(e) => { e.stopPropagation(); setShowTagOverlay(true); }}
                aria-label="People"
                title="People"
              >
                <svg className="Community-post-tag-icon" viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
                  <path d="M13.1 3.2a2 2 0 0 1 1.42.59l5.69 5.69a2 2 0 0 1 0 2.82L12.4 20.5a2 2 0 0 1-2.82 0L4.61 15.2a2 2 0 0 1-.59-1.73L4.55 8.9a3 3 0 0 1 .43-1.35l2.6-4.1a2 2 0 0 1 1.67-.95l3.2-.27a2 2 0 0 1 .65 0z" fill="#861b68" />
                  <circle cx="8.3" cy="8.3" r="2.1" fill="#ffffff" />
                  <path d="M13.1 3.2a2 2 0 0 1 1.42.59l5.69 5.69a2 2 0 0 1 0 2.82L12.4 20.5a2 2 0 0 1-2.82 0L4.61 15.2a2 2 0 0 1-.59-1.73l.53-4.57a3 3 0 0 1 .43-1.35l2.6-4.1a2 2 0 0 1 1.67-.95l3.2-.27a2 2 0 0 1 .65 0z" fill="#ffffff" fillOpacity="0.18" />
                </svg>
                <span className="Community-post-tag-count">{event.taggedPeople.length}</span>
              </button>
            )}
            <input
              ref={coverUploadInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              className="Community-file-input"
              onChange={(e) => { handleCoverFiles(e.target.files); e.target.value = ""; }}
            />
          </div>
          {coverInputError && <p className="Community-error-text">{coverInputError}</p>}
          {coverMedia.length > 1 && (
            <p className="Community-post-preview-drag-hint">Drag items to change the ordering.</p>
          )}
          {coverMedia.length > 0 && (
            <div className="Community-post-preview-thumbs Community-cover-thumbs">
              {coverMedia.map((m, i) => (
                <div
                  key={i}
                  draggable
                  onDragStart={() => setDragCoverFrom(i)}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); reorderCover(dragCoverFrom, i); setDragCoverFrom(null); }}
                  onDragEnd={() => setDragCoverFrom(null)}
                  className={`Community-post-preview-thumb${i === safeCoverIndex ? " Community-post-preview-thumb-active" : ""}${dragCoverFrom === i ? " Community-post-preview-thumb-dragging" : ""}${dragCoverFrom !== null && dragCoverFrom !== i ? " Community-post-preview-thumb-drop" : ""}`}
                  onClick={() => setCoverIndex(i)}
                >
                  {m.type === "video"
                    ? <video src={m.src} muted className="Community-post-preview-thumb-media" />
                    : m.type === "youtube"
                      ? <img src={m.thumb} alt={`YouTube ${i + 1}`} className="Community-post-preview-thumb-media" />
                      : <img src={m.src} alt={`Cover ${i + 1}`} className="Community-post-preview-thumb-media" />}
                  <span className="Community-post-preview-thumb-order">{i + 1}</span>
                  {!initialEvent && (
                    <button
                      type="button"
                      className="Community-media-remove"
                      onClick={(e) => { e.stopPropagation(); removeCoverAt(i); }}
                      aria-label="Remove media"
                    >&times;</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="Community-form-grid">
          <div className={`Community-field${errors.name ? " Community-field--error" : ""}`}>
            <label className="Community-label" htmlFor="Community-event-name">
              Event Name <span className="Community-required">*</span>
            </label>
            <div className="Community-title-color-wrap">
              <input
                id="Community-event-name"
                type="text"
                className="Community-input"
                value={event.name}
                onChange={(e) => {
                  const v = e.target.value;
                  if (isOverWordLimit(v, EVENT_WORD_MAX, event.name)) return;
                  setField("name", v);
                }}
                placeholder="Event name"
                style={fontRenderStyle({ color: event.nameColor, weight: event.nameWeight, size: event.nameSize, italic: event.nameItalic, underline: event.nameUnderline, fontFamily: event.nameFontFamily })}
              />
              {renderFontTool({ text: event.name, colorKey: "nameColor", weightKey: "nameWeight", sizeKey: "nameSize", italicKey: "nameItalic", underlineKey: "nameUnderline", fontFamilyKey: "nameFontFamily", maxSize: 25, open: showNameFont, setOpen: setShowNameFont })}
            </div>
            <div className="Community-char-counter">{(event.name.trim() ? event.name.trim().split(/\s+/).filter(Boolean).length : 0)} / {EVENT_WORD_MAX} words</div>
          </div>

          <div className={`Community-field${errors.timezone ? " Community-field--error" : ""}`}>
            <span className="Community-label">Time Zone <span className="Community-required">*</span></span>
            <select
              className="Community-input"
              value={event.timezone}
              onChange={(e) => setField("timezone", e.target.value)}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="Community-event-datetime-grid">
          <div className={`Community-field${errors.startDate ? " Community-field--error" : ""}`}>
            <label className="Community-label" htmlFor="Community-event-start-date">Start Date <span className="Community-required">*</span></label>
            <input
              id="Community-event-start-date"
              type="date"
              className="Community-input"
              value={event.startDate}
              onChange={(e) => setField("startDate", e.target.value)}
            />
          </div>
          <div className={`Community-field${errors.startTime ? " Community-field--error" : ""}`}>
            <label className="Community-label" htmlFor="Community-event-start-time">Start Time <span className="Community-required">*</span></label>
            <input
              id="Community-event-start-time"
              type="time"
              className="Community-input"
              value={event.startTime}
              onChange={(e) => setField("startTime", e.target.value)}
            />
          </div>
        </div>

        <label className="Community-checkbox-row">
          <input
            type="checkbox"
            checked={event.hasEndDate}
            onChange={(e) => setField("hasEndDate", e.target.checked)}
          />
          <span>Add end date and time</span>
        </label>

        {event.hasEndDate && (
          <div className="Community-event-datetime-grid">
            <div className={`Community-field${errors.endDate ? " Community-field--error" : ""}`}>
              <label className="Community-label" htmlFor="Community-event-end-date">End Date <span className="Community-required">*</span></label>
              <input
                id="Community-event-end-date"
                type="date"
                className="Community-input"
                value={event.endDate}
                onChange={(e) => setField("endDate", e.target.value)}
              />
            </div>
            <div className={`Community-field${errors.endTime ? " Community-field--error" : ""}`}>
              <label className="Community-label" htmlFor="Community-event-end-time">End Time <span className="Community-required">*</span></label>
              <input
                id="Community-event-end-time"
                type="time"
                className="Community-input"
                value={event.endTime}
                onChange={(e) => setField("endTime", e.target.value)}
              />
            </div>
          </div>
        )}

        {inPerson && (
          <div className="Community-form-grid">
            <div className={`Community-field${errors.address ? " Community-field--error" : ""}`}>
              <label className="Community-label" htmlFor="Community-event-address">Event Address <span className="Community-required">*</span></label>
              <div className="Community-loc-input-wrap">
                <input
                  id="Community-event-address"
                  type="text"
                  className="Community-input"
                  value={event.address}
                  onChange={(e) => setField("address", e.target.value)}
                  placeholder="Street address"
                />
                <button
                  type="button"
                  className="Community-loc-input-btn"
                  onClick={() => { setLocTarget("address"); setShowLocOverlay(true); }}
                  aria-label="Pick location for address"
                  title="Pick a location"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                    <defs>
                      <radialGradient id="locPinInputAddress" cx="50%" cy="32%" r="75%">
                        <stop offset="0%" stopColor="#ff7ab8" />
                        <stop offset="60%" stopColor="#d92776" />
                        <stop offset="100%" stopColor="#8e1149" />
                      </radialGradient>
                    </defs>
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="url(#locPinInputAddress)" stroke="#6b0d3b" strokeWidth="0.8" />
                    <ellipse cx="12" cy="9" rx="3" ry="3" fill="#ffffff" />
                  </svg>
                </button>
              </div>
            </div>
            <div className={`Community-field${errors.venue ? " Community-field--error" : ""}`}>
              <label className="Community-label" htmlFor="Community-event-venue">Event Venue <span className="Community-required">*</span></label>
              <div className="Community-loc-input-wrap">
                <input
                  id="Community-event-venue"
                  type="text"
                  className="Community-input"
                  value={event.venue}
                  onChange={(e) => setField("venue", e.target.value)}
                  placeholder="Venue name"
                />
                <button
                  type="button"
                  className="Community-loc-input-btn"
                  onClick={() => { setLocTarget("venue"); setShowLocOverlay(true); }}
                  aria-label="Pick location for venue"
                  title="Pick a location"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                    <defs>
                      <radialGradient id="locPinInputVenue" cx="50%" cy="32%" r="75%">
                        <stop offset="0%" stopColor="#ff7ab8" />
                        <stop offset="60%" stopColor="#d92776" />
                        <stop offset="100%" stopColor="#8e1149" />
                      </radialGradient>
                    </defs>
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="url(#locPinInputVenue)" stroke="#6b0d3b" strokeWidth="0.8" />
                    <ellipse cx="12" cy="9" rx="3" ry="3" fill="#ffffff" />
                  </svg>
                </button>
</div>
            </div>
          </div>
        )}

        <div className="Community-form-grid">
          <div className="Community-field">
            <label className="Community-label" htmlFor="Community-event-link">
              {inPerson ? "External Link (optional)" : "Event Link (meeting / event URL)"}
            </label>
            <input
              id="Community-event-link"
              type="text"
              className="Community-input"
              value={event.externalLink}
              onChange={(e) => {
                const v = e.target.value;
                if (isOverWordLimit(v, EVENT_WORD_MAX, event.externalLink)) return;
                setField("externalLink", v);
              }}
              placeholder="https://..."
            />
            <div className="Community-char-counter">{(event.externalLink.trim() ? event.externalLink.trim().split(/\s+/).filter(Boolean).length : 0)} / {EVENT_WORD_MAX} words</div>
          </div>

          <div className="Community-field">
            <label className="Community-label Community-post-label-lg" htmlFor="Community-event-title">Title</label>
            <div className="Community-title-color-wrap">
              <input
                id="Community-event-title"
                type="text"
                className="Community-input Community-post-title-input"
                value={event.title}
                onChange={(e) => {
                  const v = e.target.value;
                  if (isOverWordLimit(v, EVENT_WORD_MAX, event.title)) return;
                  setField("title", v);
                }}
                placeholder="Add a title"
              style={fontRenderStyle({ color: event.titleColor, weight: event.titleWeight, size: event.titleSize, italic: event.titleItalic, underline: event.titleUnderline, fontFamily: event.titleFontFamily })}
              />
              {renderFontTool({ text: event.title, colorKey: "titleColor", weightKey: "titleWeight", sizeKey: "titleSize", italicKey: "titleItalic", underlineKey: "titleUnderline", fontFamilyKey: "titleFontFamily", maxSize: 25, open: showTitleFont, setOpen: setShowTitleFont })}
            </div>
            <div className="Community-char-counter">{(event.title.trim() ? event.title.trim().split(/\s+/).filter(Boolean).length : 0)} / {EVENT_WORD_MAX} words</div>
          </div>
        </div>

        <div className="Community-field">
          <label className="Community-label" htmlFor="Community-event-desc">Description</label>
          <div className="Community-event-composer">
            <textarea
              id="Community-event-desc"
              className="Community-textarea Community-post-composer"
              value={event.description}
              maxLength={EVENT_DESC_MAX}
              onChange={(e) => {
                const v = e.target.value;
                const words = v.trim().split(/\s+/).filter(Boolean);
                if (words.length > EVENT_DESC_MAX) return;
                setField("description", v);
              }}
              placeholder="Describe your event..."
              style={fontRenderStyle({ color: event.descColor, weight: event.descWeight, size: event.descSize, italic: event.descItalic, underline: event.descUnderline })}
            />
            {renderFontTool({ text: event.description, colorKey: "descColor", weightKey: "descWeight", sizeKey: "descSize", italicKey: "descItalic", underlineKey: "descUnderline", maxSize: 20, open: showDescFont, setOpen: setShowDescFont, showButton: false })}
            <div className="Community-post-toolbar">
              <div className="Community-post-toolbar-btns">
                <button type="button" className="Community-comment-tool-btn" onClick={() => setShowEventEmoji((v) => !v)} aria-label="Add emoji" title="Add emoji">😊</button>
                <button type="button" className="Community-comment-tool-btn Community-font-toolbar-btn" onClick={() => setShowDescFont((v) => !v)} aria-label="Text style" title="Text style">
                  <svg viewBox="0 0 512 512" width="18" height="18" aria-hidden="true">
                    <defs>
                      <linearGradient id="abFontGradientToolbar" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#861b68" />
                        <stop offset="100%" stopColor="#e0245e" />
                      </linearGradient>
                    </defs>
                    <path fill="url(#abFontGradientToolbar)" d="M488.1 434.6H462L316.1 30.2C311.2 15.7 297.6 6 282.3 6h-52.6c-15.3 0-28.9 9.7-33.8 24.2L50 434.6H23.9c-9.9 0-17.9 8-17.9 17.9v35.7C6 498 14 506 23.9 506h142.9c9.9 0 17.9-8 17.9-17.9v-35.7c0-9.9-8-17.9-17.9-17.9h-21.9l26-71.4h170.3l26 71.4h-21.9c-9.9 0-17.9 8-17.9 17.9v35.7c0 9.9 8 17.9 17.9 17.9h142.9c9.9 0 17.9-8 17.9-17.9v-35.7c-.1-9.8-8.1-17.8-18-17.8zM203.4 273.9 256 129.3l52.6 144.5H203.4v.1z" />
                  </svg>
                </button>
              </div>
              <span className="Community-post-toolbar-count">{event.description.length} / {EVENT_DESC_MAX} characters</span>
            </div>
          </div>
          {showEventEmoji && (
            <div className="Community-emoji-palette">
              {EmojiPalette.map((e) => (
                <button key={e} type="button" className="Community-emoji-btn" onClick={() => insertDescEmoji(e)}>
                  {e}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="Community-post-actions-row">
          <button
            type="button"
            className="Community-comment-tool-btn Community-post-action-btn"
            onClick={() => setShowTagOverlay(true)}
            aria-label="People"
            title="People"
          >
            <span className="Community-post-action-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M13.1 3.2a2 2 0 0 1 1.42.59l5.69 5.69a2 2 0 0 1 0 2.82L12.4 20.5a2 2 0 0 1-2.82 0L4.61 15.2a2 2 0 0 1-.59-1.73l.53-4.57a3 3 0 0 1 .43-1.35l2.6-4.1a2 2 0 0 1 1.67-.95l3.2-.27a2 2 0 0 1 .65 0z" fill="#861b68" stroke="#5a1145" strokeWidth="0.3" />
                <circle cx="8.3" cy="8.3" r="2.1" fill="#ffffff" />
              </svg>
            </span>
            People
            {event.taggedPeople && event.taggedPeople.length > 0 && (
              <span className="Community-post-action-arrow" aria-hidden="true">&#8250;</span>
            )}
          </button>

          <button
            type="button"
            className="Community-comment-tool-btn Community-post-action-btn"
            onClick={() => setShowSocialOverlay(true)}
            aria-label="Add accounts"
            title="Add accounts"
          >
            <span className="Community-post-action-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <polyline points="16 6 12 2 8 6" />
                <line x1="12" y1="2" x2="12" y2="15" />
              </svg>
            </span>
            {event.socials && event.socials.length > 0 ? `Accounts (${event.socials.length})` : "Accounts"}
          </button>
        </div>
      </div>

      <div className="Community-post-settings">
        <div className="Community-post-settings-row">
          <PostVisibilityDropdown value={event.visibility} onChange={(v) => setField("visibility", v)} />
          <CommentSettingsDropdown value={event.commentPermission} onChange={(v) => setField("commentPermission", v)} />
        </div>
        <HideEngagementDropdown
          value={{ hideLikes: event.hideLikes, hideLikeCount: event.hideLikeCount, hideComments: event.hideComments, hideCommentCount: event.hideCommentCount }}
          onChange={(key, checked) => setField(key, checked)}
        />
      </div>

      {Object.keys(errors).length > 0 && (
          <div className="Community-field">
            <p className="Community-required-note">Fields marked with <span className="Community-required">*</span> are required.</p>
          </div>
        )}

      <div className="Community-overlay-actions">
        <PrimaryButton onClick={handleSubmit}>{initialEvent ? "Update" : "Create"}</PrimaryButton>
      </div>

      {showCoverEditor !== null && event.coverPhotos[showCoverEditor] && (
        <ImageEditor
          src={event.coverPhotos[showCoverEditor]}
          initialCornerRadius={coverImageCorners[event.coverPhotos[showCoverEditor]] || 0}
          onClose={() => setShowCoverEditor(null)}
          onApply={(dataUrl, corners) => applyCoverEdit(dataUrl, showCoverEditor, corners)}
        />
      )}

      {showCoverVideoEditor && currentCover && currentCover.type === "video" && (
        <VideoEditor
          src={currentCover.src}
          initialEdits={event.coverVideoEdits}
          onClose={() => setShowCoverVideoEditor(false)}
          onApply={(edits) => {
            setEvent((prev) => {
              const media = buildCoverMedia(prev).map((m) => (m.type === "video" ? { ...m, edits } : m));
              return { ...prev, ...rebuildCoverState(media) };
            });
            setShowCoverVideoEditor(false);
          }}
        />
      )}

      {showCoverThumbOverlay && event.coverVideo && (
        <VideoThumbnailOverlay
          videos={[event.coverVideo]}
          initialThumbnails={coverThumbnails}
          onCancel={() => {
            pendingCoverThumbRef.current = null;
            setShowCoverThumbOverlay(false);
            if (initialEvent) setShowEventThumbPreview(true);
          }}
          onDone={(thumbs) => {
            const first = thumbs.find((t) => t) || null;
            setCoverThumbnails(thumbs);
            setCoverThumbnail(first);
            setShowCoverThumbOverlay(false);
            if (initialEvent) {
              setShowEventThumbPreview(true);
              return;
            }
            const pending = pendingCoverThumbRef.current;
            pendingCoverThumbRef.current = null;
            onCreate({ ...pending, coverThumbnail: first, coverThumbnails: thumbs });
          }}
        />
      )}

      {showEventThumbPreview && initialEvent && (
        <div className="Community-overlay-backdrop" role="presentation">
          <div className="Community-overlay-panel Community-thumb-confirm" role="dialog" aria-modal="true" aria-labelledby="Community-event-thumb-preview-title">
            <div className="Community-overlay-header Community-thumb-confirm-header">
              <h2 id="Community-event-thumb-preview-title" className="Community-heading-section">Thumbnail Preview</h2>
              <button type="button" className="Community-overlay-close-btn" onClick={() => { setShowEventThumbPreview(false); setShowCoverThumbOverlay(true); }} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="Community-overlay-body">
              {(coverThumbnails.find((t) => t) || coverThumbnail) ? (
                <img src={coverThumbnails.find((t) => t) || coverThumbnail} alt="Thumbnail preview" className="Community-thumb-confirm-img" />
              ) : (
                <p className="Community-thumb-saved-hint">No thumbnail selected.</p>
              )}
            </div>
            <div className="Community-overlay-actions Community-overlay-actions-center">
              <PrimaryButton onClick={() => { setShowEventThumbPreview(false); onCreate({ ...event, coverThumbnail, coverThumbnails }); }}>Accept</PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {showCoverLimit && (
        <div className="Community-overlay-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowCoverLimit(false); }} role="presentation">
          <div className="Community-overlay-panel Community-limit-overlay" role="dialog" aria-modal="true" aria-labelledby="Community-cover-limit-title">            <div className="Community-overlay-header Community-overlay-header-center">
              <h2 id="Community-cover-limit-title" className="Community-heading-section">Limit Exceeded</h2>
              <button type="button" className="Community-overlay-close-btn" onClick={() => setShowCoverLimit(false)} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="Community-overlay-body Community-overlay-body-center">
              <p className="Community-body-text">You can add a maximum of {EVENT_COVER_MAX_MEDIA} items in total.</p>
            </div>
            <div className="Community-overlay-actions Community-overlay-actions-center">
              <button type="button" className="Community-btn Community-btn-primary" onClick={() => setShowCoverLimit(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {showLocOverlay && (
        <LocationOverlay
          value={locTarget === "address" ? event.address : event.venue}
          onSave={(loc) => {
            setField(locTarget, loc);
            setShowLocOverlay(false);
          }}
          onClose={() => setShowLocOverlay(false)}
        />
      )}

      {showTagOverlay && (
        <TagPeopleOverlay
          selected={event.taggedPeople || []}
          onSave={(ids) => {
            setField("taggedPeople", ids);
            setShowTagOverlay(false);
          }}
          onClose={() => setShowTagOverlay(false)}
        />
      )}

      {showSocialOverlay && (
        <SocialLinksOverlay
          value={event.socials || []}
          onSave={(links) => {
            setField("socials", links);
            setShowSocialOverlay(false);
          }}
          onClose={() => setShowSocialOverlay(false)}
        />
      )}
    </Overlay>
  );
};

const EDIT_FILTERS = {
  Original: "",
  Studio: "saturate(1.1) contrast(1.05)",
  Spotlight: "brightness(1.15) contrast(1.1) saturate(1.05)",
  Prime: "contrast(1.08) saturate(1.15)",
  Classic: "sepia(0.25) contrast(1.05)",
  Edge: "contrast(1.3) saturate(1.2)",
  Luminate: "brightness(1.2) contrast(0.95)",
};

const EDIT_ASPECTS = ["Original", "Free", "Square", "3:4", "4:1", "16:9"];

const ROTATE_FIX_OPTIONS = [
  { label: "None", value: 0 },
  { label: "90°", value: 90 },
  { label: "180°", value: 180 },
  { label: "270°", value: 270 },
];

const RotateFixDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = ROTATE_FIX_OPTIONS.find((o) => o.value === value);

  return (
    <div className="Community-dropdown" ref={ref}>
      <button
        type="button"
        className="Community-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span>Rotation: {current ? current.label : "Custom"}</span>
        <span aria-hidden="true">▼</span>
      </button>
      {open && (
        <div className="Community-dropdown-menu" role="menu">
          {ROTATE_FIX_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="menuitemradio"
              aria-checked={value === o.value}
              className={`Community-dropdown-option ${value === o.value ? "Community-dropdown-selected" : ""}`}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              <span className="Community-dropdown-option-text">
                <span className="Community-dropdown-option-title">{o.label}</span>
              </span>
              {value === o.value && <span className="Community-dropdown-check" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const FlipDropdown = ({ flipH, flipV, onToggle }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="Community-dropdown" ref={ref}>
      <button
        type="button"
        className="Community-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span>Flip</span>
        <span aria-hidden="true">▼</span>
      </button>
      {open && (
        <div className="Community-dropdown-menu" role="menu">
          <div className="Community-editor-flip">
            <button
              type="button"
              className={flipH ? "active" : ""}
              onClick={() => onToggle("flipH")}
              title="Flip Horizontal"
              aria-pressed={flipH}
            >
              <span>Flip Horizontal</span>
              {flipH && <span className="Community-dropdown-check" aria-hidden="true">✓</span>}
            </button>
            <button
              type="button"
              className={flipV ? "active" : ""}
              onClick={() => onToggle("flipV")}
              title="Flip Vertical"
              aria-pressed={flipV}
            >
              <span>Flip Vertical</span>
              {flipV && <span className="Community-dropdown-check" aria-hidden="true">✓</span>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AspectRatioDropdown = ({ value, onChange, onOpenChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen((v) => { if (v && onOpenChange) onOpenChange(false); return false; });
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onOpenChange]);

  return (
    <div className="Community-dropdown" ref={ref}>
      <button
        type="button"
        className="Community-dropdown-trigger"
        onClick={() => setOpen((v) => { const n = !v; if (onOpenChange) onOpenChange(n); return n; })}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span>Aspect: {value}</span>
        <span aria-hidden="true">▼</span>
      </button>
      {open && (
        <div className="Community-dropdown-menu" role="menu">
          {EDIT_ASPECTS.map((a) => (
            <button
              key={a}
              type="button"
              role="menuitemradio"
              aria-checked={value === a}
              className={`Community-dropdown-option ${value === a ? "Community-dropdown-selected" : ""}`}
              onClick={() => {
                onChange(a);
                setOpen(false);
              }}
            >
              <span className="Community-dropdown-option-text">
                <span className="Community-dropdown-option-title">{a}</span>
              </span>
              {value === a && <span className="Community-dropdown-check" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ImageEditor = ({ src, onApply, onClose, initialCornerRadius = 0 }) => {
  const canvasRef = useRef(null);
  const [aspect, setAspect] = useState("Original");
  const [rotate, setRotate] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [filter, setFilter] = useState("Original");
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [saturation, setSaturation] = useState(0);
  const [vignette, setVignette] = useState(0);
  const [tab, setTab] = useState("adjust");
  const [hasCropped, setHasCropped] = useState(false);
  const [cornerRadius, setCornerRadius] = useState(initialCornerRadius);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const imgRef = useRef(null);
  const stageRef = useRef(null);
  const [stageW, setStageW] = useState(0);
  const [stageH, setStageH] = useState(0);
  const [crop, setCrop] = useState({ x: 0, y: 0, w: 1, h: 1 });
  const cropDrag = useRef(null);

  useEffect(() => {
    const measure = () => {
      const stage = stageRef.current;
      if (!stage) return;
      setStageW(stage.offsetWidth || stage.clientWidth);
      setStageH(stage.offsetHeight || stage.clientHeight);
    };
    const t = setTimeout(measure, 40);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); };
  }, [tab, aspect, rotate, zoom, flipH, flipV, filter, brightness, contrast, saturation, vignette, imgRef.current ? imgRef.current.src : ""]);

  useEffect(() => {
    if (tab !== "crop") return undefined;
    const onUp = () => { cropDrag.current = null; };
    window.addEventListener("pointerup", onUp);
    return () => window.removeEventListener("pointerup", onUp);
  }, [tab]);

  const rectFromPointer = (e) => {
    const rect = stageRef.current.getBoundingClientRect();
    return {
      rx: (e.clientX - rect.left) / rect.width,
      ry: (e.clientY - rect.top) / rect.height,
    };
  };

  const applyAspect = (x, y, w, h) => {
    const r = ratio;
    if (!r || h <= 0) return { x, y, w, h };
    if (w / h > r) { w = h * r; }
    else { h = w / r; }
    return { x, y, w, h };
  };

  const startCropDrag = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    const start = { mode, ...rectFromPointer(e), ...crop };
    cropDrag.current = start;
    const onMove = (ev) => {
      const p = rectFromPointer(ev);
      const px = p.rx, py = p.ry;
      const fixed = { x: start.x, y: start.y, w: start.w, h: start.h };
      let x = fixed.x, y = fixed.y, w = fixed.w, h = fixed.h;
      const r = ratio;

      if (mode === "move") {
        x = Math.min(Math.max(start.x + px - start.rx, 0), 1 - start.w);
        y = Math.min(Math.max(start.y + py - start.ry, 0), 1 - start.h);
        w = start.w; h = start.h;
        setCrop({ x, y, w, h });
        return;
      }

      const right = mode.includes("r");
      const left = mode.includes("l");
      const bottom = mode.includes("b");
      const top = mode.includes("t");

      if (right) w = Math.max(px - fixed.x, 0.05);
      if (left) { x = Math.min(px, fixed.x + fixed.w - 0.05); w = (fixed.x + fixed.w) - x; }
      if (bottom) h = Math.max(py - fixed.y, 0.05);
      if (top) { y = Math.min(py, fixed.y + fixed.h - 0.05); h = (fixed.y + fixed.h) - y; }

      if (r) {
        const rw = w / r;
        const rh = h * r;
        if (rw > h) {
          if (bottom && !top) h = rw;
          else if (top && !bottom) { h = rw; y = fixed.y + fixed.h - h; }
          else w = rh;
        } else {
          if (right && !left) w = rh;
          else if (left && !right) { w = rh; x = fixed.x + fixed.w - w; }
          else h = rw;
        }
      }

      x = Math.min(Math.max(x, 0), 1 - w);
      y = Math.min(Math.max(y, 0), 1 - h);

      setCrop({ x, y, w, h });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const aspectRatio = () => {
    const map = {
      Free: null,
      Square: 1,
      "3:4": 3 / 4,
      "4:1": 4,
      "16:9": 16 / 9,
    };
    return map[aspect];
  };
  const ratio = aspectRatio();

  const handleAspectChange = (name) => {
    setAspect(name);
    if (name === "Free") {
      setCrop({ x: 0.025, y: 0.025, w: 0.95, h: 0.95 });
      return;
    }
    const map = { Free: null, Square: 1, "3:4": 3 / 4, "4:1": 4, "16:9": 16 / 9 };
    const r = map[name];
    let w, h;
    if (!r) {
      w = Math.min(crop.w, 0.95);
      h = Math.min(crop.h, 0.95);
    } else {
      if (r > 1) { w = 0.9; h = w / r; }
      else { h = 0.9; w = h * r; }
    }
    const x = Math.max((1 - w) / 2, 0);
    const y = Math.max((1 - h) / 2, 0);
    setCrop({ x, y, w, h });
  };

  useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = () => { imgRef.current = img; draw(); };
  }, [src]);

  useEffect(() => {
    if (imgRef.current) draw();
  }, [rotate, zoom, flipH, flipV, filter, brightness, contrast, saturation, vignette, cornerRadius, hasCropped]);

  const draw = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;

    let w = img.naturalWidth;
    let h = img.naturalHeight;

    const maxW = 560, maxH = 480;
    const scale = Math.min(maxW / w, maxH / h, 1);
    w *= scale; h *= scale;

    canvas.width = Math.round(w);
    canvas.height = Math.round(h);
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    if (cornerRadius > 0) {
      const r = Math.max(1, Math.min(w, h) * (cornerRadius / 500));
      ctx.beginPath();
      ctx.moveTo(r, 0);
      ctx.arcTo(w, 0, w, h, r);
      ctx.arcTo(w, h, 0, h, r);
      ctx.arcTo(0, h, 0, 0, r);
      ctx.arcTo(0, 0, w, 0, r);
      ctx.closePath();
      ctx.clip();
    }
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotate * Math.PI) / 180);
    ctx.scale((flipH ? -1 : 1) * zoom, (flipV ? -1 : 1) * zoom);
    ctx.filter = [
      `brightness(${1 + brightness / 100})`,
      `contrast(${1 + contrast / 100})`,
      `saturate(${1 + saturation / 100})`,
      EDIT_FILTERS[filter],
    ].filter(Boolean).join(" ");
    const drawW = w;
    ctx.drawImage(img, -drawW / 2, -h / 2, drawW, h);

    if (vignette !== 0) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      const W = canvas.width;
      const H = canvas.height;
      const a = Math.min(Math.abs(vignette) / 120, 0.85);
      const inner = Math.min(W, H) * 0.35;
      const outer = Math.max(W, H) * 1.05;
      const corners = [[0, 0], [W, 0], [0, H], [W, H]];
      const dark = vignette > 0;
      corners.forEach(([cx, cy]) => {
        const g = ctx.createRadialGradient(cx, cy, inner, cx, cy, outer);
        g.addColorStop(0, "rgba(0,0,0,0)");
        g.addColorStop(1, dark ? `rgba(0,0,0,${a})` : `rgba(255,255,255,${a})`);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      });
    }
    ctx.restore();
  };

  const hasChanges =
    aspect !== "Free" ||
    rotate !== 0 ||
    zoom !== 1 ||
    flipH || flipV ||
    filter !== "Original" ||
    brightness !== 0 ||
    contrast !== 0 ||
    saturation !== 0 ||
    vignette !== 0 ||
    crop.x !== 0 || crop.y !== 0 || crop.w !== 1 || crop.h !== 1 ||
    cornerRadius !== 0;

  const revert = () => {
    setRotate(0);
    setZoom(1);
    setFlipH(false);
    setFlipV(false);
    setFilter("Original");
    setBrightness(0);
    setContrast(0);
    setSaturation(0);
    setVignette(0);
    setAspect("Original");
    setCrop({ x: 0, y: 0, w: 1, h: 1 });
    setCornerRadius(0);
    setHasCropped(false);
    setShowRevertConfirm(false);
  };

  const applyEdit = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let out = canvas;
    const cropped = crop.x !== 0.1 || crop.y !== 0.1 || crop.w !== 0.8 || crop.h !== 0.8;
    if (cropped) {
      const sw = canvas.width * crop.w;
      const sh = canvas.height * crop.h;
      const sx = canvas.width * crop.x;
      const sy = canvas.height * crop.y;
      const c = document.createElement("canvas");
      c.width = Math.round(sw);
      c.height = Math.round(sh);
      const ctx = c.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, c.width, c.height);
      out = c;
    }
    let dataUrl = out.toDataURL("image/png");
    if (cornerRadius > 0) {
      const w = out.width;
      const h = out.height;
      const r = Math.max(1, Math.min(w, h) * (cornerRadius / 500));
      const rounded = document.createElement("canvas");
      rounded.width = w;
      rounded.height = h;
      const rctx = rounded.getContext("2d");
      rctx.clearRect(0, 0, w, h);
      rctx.beginPath();
      rctx.moveTo(r, 0);
      rctx.arcTo(w, 0, w, h, r);
      rctx.arcTo(w, h, 0, h, r);
      rctx.arcTo(0, h, 0, 0, r);
      rctx.arcTo(0, 0, w, 0, r);
      rctx.closePath();
      rctx.clip();
      rctx.drawImage(out, 0, 0);
      out = rounded;
      dataUrl = out.toDataURL("image/png");
    }
    if (hasCropped || cropped || rotate !== 0 || flipH || flipV || zoom !== 1 || aspect !== "Original" ||
        filter !== "Original" || brightness !== 0 || contrast !== 0 || saturation !== 0 || vignette !== 0 || cornerRadius !== 0) {
      onApply(dataUrl, cornerRadius);
    }
    onClose();
  };

  return (
    <Overlay title="Edit image" onClose={onClose} panelClassName="Community-editor-panel">
      <div className="Community-editor-body">
        <div className="Community-editor-stage">
          <div className="Community-editor-canvas-wrap" ref={stageRef}>
            <canvas ref={canvasRef} className="Community-editor-canvas" />
            {tab === "crop" && imgRef.current && (
              <div className="Community-crop-wrap">
                {aspect !== "Original" && (
                  <div
                    className="Community-crop-overlay"
                    onPointerDown={(e) => { e.preventDefault(); startCropDrag(e, "move"); }}
                  >
                  <div className="Community-crop-mask-left" style={{ width: `${crop.x * 100}%`, height: "100%" }} />
                  <div className="Community-crop-mask-top" style={{ top: 0, left: `${crop.x * 100}%`, width: `${crop.w * 100}%`, height: `${crop.y * 100}%` }} />
                  <div className="Community-crop-mask-right" style={{ right: 0, top: 0, width: `${(1 - crop.x - crop.w) * 100}%`, height: "100%" }} />
                  <div className="Community-crop-mask-bottom" style={{ bottom: 0, left: `${crop.x * 100}%`, width: `${crop.w * 100}%`, height: `${(1 - crop.y - crop.h) * 100}%` }} />
                  <div className="Community-crop-box" style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.w * 100}%`, height: `${crop.h * 100}%` }}>
                    <div className="Community-crop-handle tl" onPointerDown={(e) => startCropDrag(e, "tl")} />
                    <div className="Community-crop-handle tr" onPointerDown={(e) => startCropDrag(e, "tr")} />
                    <div className="Community-crop-handle bl" onPointerDown={(e) => startCropDrag(e, "bl")} />
                    <div className="Community-crop-handle br" onPointerDown={(e) => startCropDrag(e, "br")} />
                    <div className="Community-crop-handle t" onPointerDown={(e) => startCropDrag(e, "t")} />
                    <div className="Community-crop-handle b" onPointerDown={(e) => startCropDrag(e, "b")} />
                    <div className="Community-crop-handle l" onPointerDown={(e) => startCropDrag(e, "l")} />
                    <div className="Community-crop-handle r" onPointerDown={(e) => startCropDrag(e, "r")} />
                  </div>
                </div>
                )}
            </div>
          )}
          </div>
        </div>

<div className="Community-editor-side">
          <div className="Community-editor-scroll">
            <div className="Community-editor-tabs">
            <span className="Community-editor-tabs-pill" style={{ transform: `translateX(${tab === "adjust" ? 0 : tab === "filter" ? 100 : 200}%)` }} />
            <button type="button" className={tab === "adjust" ? "active" : ""} onClick={() => setTab("adjust")}>Adjust</button>
            <button type="button" className={tab === "filter" ? "active" : ""} onClick={() => setTab("filter")}>Filter</button>
            <button type="button" className={tab === "crop" ? "active" : ""} onClick={() => setTab("crop")}>Crop</button>
          </div>

          {tab === "adjust" && (
            <div className="Community-editor-controls">
              <label className="Community-editor-row"><span>Brightness</span><input type="range" min="-100" max="100" value={brightness} onChange={(e) => setBrightness(Number(e.target.value))} /><b>{brightness}%</b></label>
              <label className="Community-editor-row"><span>Contrast</span><input type="range" min="-100" max="100" value={contrast} onChange={(e) => setContrast(Number(e.target.value))} /><b>{contrast}%</b></label>
              <label className="Community-editor-row"><span>Saturation</span><input type="range" min="-100" max="100" value={saturation} onChange={(e) => setSaturation(Number(e.target.value))} /><b>{saturation}%</b></label>
              <label className="Community-editor-row"><span>Vignette</span><input type="range" min="-100" max="100" value={vignette} onChange={(e) => setVignette(Number(e.target.value))} /><b>{vignette}%</b></label>
            </div>
          )}

          {tab === "filter" && (
            <div className="Community-editor-filters">
              {Object.keys(EDIT_FILTERS).map((f) => (
                <button key={f} type="button" className={`Community-editor-filter ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>
                  <span className="Community-editor-filter-thumb"><img src={src} alt={f} style={{ filter: EDIT_FILTERS[f] }} /></span>
                  <em>{f}</em>
                </button>
              ))}
            </div>
          )}

          {tab === "crop" && (
            <div className="Community-editor-controls">
              <span className="Community-editor-row-title">Aspect Ratio</span>
              <AspectRatioDropdown value={aspect} onChange={handleAspectChange} />
              <RotateFixDropdown value={rotate} onChange={setRotate} />
              <div className="Community-editor-rotate">
                <FlipDropdown flipH={flipH} flipV={flipV} onToggle={(which) => (which === "flipH" ? setFlipH(!flipH) : setFlipV(!flipV))} />
                <span>Rotate &amp; Zoom</span>
                <label className="Community-editor-row"><span>Rotate</span><input type="range" min="0" max="360" value={rotate} onChange={(e) => setRotate(Number(e.target.value))} /><b>{rotate}°</b></label>
                <label className="Community-editor-row"><span>Zoom</span><input type="range" min="0" max="100" step="1" value={Math.round(((zoom - 1) / 2) * 100)} onChange={(e) => setZoom(1 + (Number(e.target.value) / 100) * 2)} /><b>{Math.round(((zoom - 1) / 2) * 100)}%</b></label>
                {initialCornerRadius < 100 && <label className="Community-editor-row"><span>Corners</span><input type="range" min="0" max="100" step="1" value={cornerRadius} onChange={(e) => setCornerRadius(Number(e.target.value))} /><b>{cornerRadius}%</b></label>}
              </div>
            </div>
          )}

          </div>

          <div className="Community-editor-actions">
            <SecondaryButton onClick={() => setShowRevertConfirm(true)} disabled={!hasChanges}>Revert</SecondaryButton>
            <PrimaryButton onClick={() => setShowApplyConfirm(true)} disabled={!hasChanges}>Apply</PrimaryButton>
          </div>
        </div>
      </div>

      {showRevertConfirm && (
        <div className="Community-overlay-backdrop" role="presentation">
          <div className="Community-overlay-panel Community-limit-overlay Community-revert-overlay" role="dialog" aria-modal="true" aria-labelledby="Community-revert-title">
            <div className="Community-overlay-header">
              <h2 id="Community-revert-title" className="Community-heading-section">Revert changes?</h2>
              <button type="button" className="Community-overlay-close-btn" onClick={() => setShowRevertConfirm(false)} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="Community-overlay-body Community-revert-body">
              <p className="Community-body-text Community-revert-text">Are you sure you want to revert all the changes?</p>
            </div>
            <div className="Community-overlay-actions Community-revert-actions">
              <PrimaryButton onClick={revert}>Yes</PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {showApplyConfirm && (
        <div className="Community-overlay-backdrop" role="presentation">
          <div className="Community-overlay-panel Community-limit-overlay Community-confirm-center" role="dialog" aria-modal="true" aria-labelledby="Community-apply-title">
            <div className="Community-overlay-header">
              <h2 id="Community-apply-title" className="Community-heading-section">Save changes?</h2>
              <button type="button" className="Community-overlay-close-btn" onClick={() => setShowApplyConfirm(false)} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="Community-overlay-body">
              <p className="Community-body-text">Are you sure you want to save the changes?</p>
            </div>
            <div className="Community-overlay-actions Community-overlay-actions-center">
              <PrimaryButton onClick={applyEdit}>Yes</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </Overlay>
  );
};

const FRAME_COUNT = 12;

const VideoEditor = ({ src, onApply, onClose, initialEdits = null }) => {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const [rotate, setRotate] = useState(initialEdits ? initialEdits.rotate || 0 : 0);
  const [zoom, setZoom] = useState(initialEdits && initialEdits.zoom ? initialEdits.zoom : 1);
  const [flipH, setFlipH] = useState(initialEdits ? Boolean(initialEdits.flipH) : false);
  const [flipV, setFlipV] = useState(initialEdits ? Boolean(initialEdits.flipV) : false);
  const [filter, setFilter] = useState(initialEdits && initialEdits.filter ? initialEdits.filter : "Original");
  const [brightness, setBrightness] = useState(initialEdits ? initialEdits.brightness || 0 : 0);
  const [contrast, setContrast] = useState(initialEdits ? initialEdits.contrast || 0 : 0);
  const [saturation, setSaturation] = useState(initialEdits ? initialEdits.saturation || 0 : 0);
  const [vignette, setVignette] = useState(initialEdits ? initialEdits.vignette || 0 : 0);
  const [tab, setTab] = useState("adjust");
  const [playing, setPlaying] = useState(true);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const stageRef = useRef(null);
  const [stageW, setStageW] = useState(0);
  const [stageH, setStageH] = useState(0);
  const rafRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const measure = () => {
      const stage = stageRef.current;
      if (!stage) return;
      setStageW(stage.offsetWidth || stage.clientWidth);
      setStageH(stage.offsetHeight || stage.clientHeight);
    };
    const t = setTimeout(measure, 40);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); };
  }, [tab, rotate, zoom, flipH, flipV, filter, brightness, contrast, saturation, vignette, ready]);

  const paint = (target, fullRes) => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const cw = video.videoWidth;
    const ch = video.videoHeight;
    let outW = cw * zoom;
    let outH = ch * zoom;
    if (rotate % 180 === 90) { const tmp = outW; outW = outH; outH = tmp; }
    let fit = 1;
    if (fullRes) {
      target.width = Math.max(1, Math.round(outW));
      target.height = Math.max(1, Math.round(outH));
    } else {
      const wrap = stageRef.current;
      let bw = 580, bh = 420;
      if (wrap && wrap.clientWidth > 10 && wrap.clientHeight > 10) {
        bw = Math.max(1, Math.round(wrap.clientWidth));
        bh = Math.max(1, Math.round(wrap.clientHeight));
      }
      target.width = bw;
      target.height = bh;
      fit = Math.min(bw / outW, bh / outH);
    }
    const ctx = target.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, target.width, target.height);
    if (!fullRes) {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, target.width, target.height);
    }
    ctx.translate(target.width / 2, target.height / 2);
    ctx.rotate((rotate * Math.PI) / 180);
    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
    ctx.filter = [
      `brightness(${1 + brightness / 100})`,
      `contrast(${1 + contrast / 100})`,
      `saturate(${1 + saturation / 100})`,
      EDIT_FILTERS[filter],
    ].filter(Boolean).join(" ");
    ctx.drawImage(video, -cw / 2 * zoom * fit, -ch / 2 * zoom * fit, cw * zoom * fit, ch * zoom * fit);
    if (vignette !== 0) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      const W = target.width;
      const H = target.height;
      const a = Math.min(Math.abs(vignette) / 120, 0.85);
      const inner = Math.min(W, H) * 0.35;
      const outer = Math.max(W, H) * 1.05;
      const corners = [[0, 0], [W, 0], [0, H], [W, H]];
      const dark = vignette > 0;
      corners.forEach(([cx, cy]) => {
        const g = ctx.createRadialGradient(cx, cy, inner, cx, cy, outer);
        g.addColorStop(0, "rgba(0,0,0,0)");
        g.addColorStop(1, dark ? `rgba(0,0,0,${a})` : `rgba(255,255,255,${a})`);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      });
      ctx.restore();
    }
  };

  useEffect(() => {
    const loop = () => {
      if (canvasRef.current) {
        try { paint(canvasRef.current, false); } catch (e) { /* ignore */ }
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [rotate, zoom, flipH, flipV, filter, brightness, contrast, saturation, vignette, ready]);

  const hasChanges = rotate !== 0 || zoom !== 1 || flipH || flipV ||
    filter !== "Original" || brightness !== 0 || contrast !== 0 || saturation !== 0 ||
    vignette !== 0;

  const revert = () => {
    setRotate(0); setZoom(1); setFlipH(false); setFlipV(false); setFilter("Original");
    setBrightness(0); setContrast(0); setSaturation(0); setVignette(0);
    setShowRevertConfirm(false);
  };

  const applyEdit = () => {
    setShowApplyConfirm(false);
    onApply({
      brightness,
      contrast,
      saturation,
      vignette,
      filter,
      rotate,
      zoom,
      flipH,
      flipV,
    });
    onClose();
  };

  return (
    <Overlay title="Edit video" onClose={onClose} panelClassName="Community-editor-panel">
      <div className="Community-body-text Community-editor-video-hint">
        Preview is live. Use Apply to save the edited video.
      </div>
      <div className="Community-editor-body">
        <div className="Community-editor-stage">
          <div className="Community-editor-canvas-wrap Community-editor-video-wrap" ref={stageRef}>
            <canvas ref={canvasRef} className="Community-editor-canvas Community-editor-video-canvas" style={{ display: "none" }} />
            <video
              ref={videoRef}
              src={src}
              controls
              loop
              playsInline
              onLoadedData={() => setReady(true)}
              className="Community-editor-live-video"
              style={{
                transform: `rotate(${rotate}deg) scale(${zoom}) scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`,
                filter: [
                  `brightness(${1 + brightness / 100})`,
                  `contrast(${1 + contrast / 100})`,
                  `saturate(${1 + saturation / 100})`,
                  EDIT_FILTERS[filter],
                ].filter(Boolean).join(" "),
              }}
            />
            {vignette !== 0 && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  background: `radial-gradient(ellipse at center, rgba(0,0,0,0) 42%, rgba(${vignette > 0 ? "0,0,0" : "255,255,255"},${Math.min(Math.abs(vignette) / 120, 0.85)}) 110%)`,
                }}
              />
            )}
          </div>
        </div>

        <div className="Community-editor-side">
          <div className="Community-editor-scroll">
            <div className="Community-editor-tabs Community-editor-tabs-two">
              <span className="Community-editor-tabs-pill" style={{ transform: `translateX(${tab === "adjust" ? 0 : 100}%)` }} />
              <button type="button" className={tab === "adjust" ? "active" : ""} onClick={() => setTab("adjust")}>Adjust</button>
              <button type="button" className={tab === "filter" ? "active" : ""} onClick={() => setTab("filter")}>Filter</button>
            </div>

            {tab === "adjust" && (
              <div className="Community-editor-controls">
                <label className="Community-editor-row"><span>Brightness</span><input type="range" min="-100" max="100" value={brightness} onChange={(e) => setBrightness(Number(e.target.value))} /><b>{brightness}%</b></label>
                <label className="Community-editor-row"><span>Contrast</span><input type="range" min="-100" max="100" value={contrast} onChange={(e) => setContrast(Number(e.target.value))} /><b>{contrast}%</b></label>
                <label className="Community-editor-row"><span>Saturation</span><input type="range" min="-100" max="100" value={saturation} onChange={(e) => setSaturation(Number(e.target.value))} /><b>{saturation}%</b></label>
                <label className="Community-editor-row"><span>Vignette</span><input type="range" min="-100" max="100" value={vignette} onChange={(e) => setVignette(Number(e.target.value))} /><b>{vignette}%</b></label>
              </div>
            )}

            {tab === "filter" && (
              <div className="Community-editor-filters">
                {Object.keys(EDIT_FILTERS).map((f) => (
                  <button key={f} type="button" className={`Community-editor-filter ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>
                    <span className="Community-editor-filter-thumb" style={{ filter: EDIT_FILTERS[f] }}>
                      <video src={src} muted playsInline preload="metadata" />
                    </span>
                    <em>{f}</em>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="Community-editor-actions">
            <SecondaryButton onClick={() => setShowRevertConfirm(true)} disabled={!hasChanges}>Revert</SecondaryButton>
            <PrimaryButton onClick={() => setShowApplyConfirm(true)} disabled={!hasChanges}>Apply</PrimaryButton>
          </div>
        </div>
      </div>

      {showRevertConfirm && (
        <div className="Community-overlay-backdrop" role="presentation">
          <div className="Community-overlay-panel Community-limit-overlay Community-revert-overlay" role="dialog" aria-modal="true" aria-labelledby="Community-vrevert-title">
            <div className="Community-overlay-header">
              <h2 id="Community-vrevert-title" className="Community-heading-section">Revert changes?</h2>
              <button type="button" className="Community-overlay-close-btn" onClick={() => setShowRevertConfirm(false)} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="Community-overlay-body Community-revert-body">
              <p className="Community-body-text Community-revert-text">Are you sure you want to revert all the changes?</p>
            </div>
            <div className="Community-overlay-actions Community-revert-actions">
              <PrimaryButton onClick={revert}>Yes</PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {showApplyConfirm && (
        <div className="Community-overlay-backdrop" role="presentation">
          <div className="Community-overlay-panel Community-limit-overlay Community-confirm-center" role="dialog" aria-modal="true" aria-labelledby="Community-vapply-title">
            <div className="Community-overlay-header">
              <h2 id="Community-vapply-title" className="Community-heading-section">Save changes?</h2>
              <button type="button" className="Community-overlay-close-btn" onClick={() => setShowApplyConfirm(false)} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="Community-overlay-body">
              <p className="Community-body-text">Are you sure you want to save the changes?</p>
            </div>
            <div className="Community-overlay-actions Community-overlay-actions-center">
              <PrimaryButton onClick={applyEdit}>Yes</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </Overlay>
  );
};

const VideoThumbnailOverlay = ({ videos, initialThumbnails = [], onDone, onCancel }) => {
  const videoCount = videos.length;
  const [thumbnails, setThumbnails] = useState(() =>
    initialThumbnails && initialThumbnails.length === videos.length
      ? [...initialThumbnails]
      : Array(videos.length).fill(null)
  );
  const [step, setStep] = useState(0);
  const currentVideoIndex = Math.min(step, videoCount - 1);
  const video = videos[currentVideoIndex];
  const isLast = currentVideoIndex >= videoCount - 1;
  const [thumbnail, setThumbnail] = useState(() => {
    if (initialThumbnails && initialThumbnails.length === videos.length && initialThumbnails[0]) return initialThumbnails[0];
    return thumbnails[0] || null;
  });
  const [frameTime, setFrameTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [frames, setFrames] = useState([]);
  const [loadingFrames, setLoadingFrames] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [activeMode, setActiveMode] = useState("frame");
  const [playing, setPlaying] = useState(false);
  const [showUploadWarning, setShowUploadWarning] = useState(false);
  const [showConfirmPreview, setShowConfirmPreview] = useState(false);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const buildingRef = useRef(false);
  const framesCacheRef = useRef({});
  const buildingSrcRef = useRef(null);

  const moveTo = (index) => {
    buildingRef.current = false;
    buildingSrcRef.current = null;
    const src = videos[index];
    if (framesCacheRef.current[src]) {
      setFrames(framesCacheRef.current[src]);
      setLoadingFrames(false);
    } else {
      setFrames([]);
      setLoadingFrames(true);
    }
    setStep(index);
    setThumbnail(thumbnails[index] || null);
    setFrameTime(0);
    setShowUploadWarning(false);
    setActiveMode("frame");
  };

  const commitAndAdvance = (thumb) => {
    const next = [...thumbnails];
    next[currentVideoIndex] = thumb;
    setThumbnails(next);
    if (currentVideoIndex + 1 < videoCount) {
      moveTo(currentVideoIndex + 1);
    } else {
      onDone(next);
    }
  };

  const handleSave = () => {
    if (!thumbnail) return;
    setShowConfirmPreview(true);
  };

  const captureFrameAt = (videoEl, time) => {
    const canvas = document.createElement("canvas");
    canvas.width = videoEl.videoWidth || 640;
    canvas.height = videoEl.videoHeight || 360;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
  };

  const fitImageToVideo = (dataUrl) => {
    return new Promise((resolve) => {
      const v = videoRef.current;
      const w = v ? (v.videoWidth || 640) : 640;
      const h = v ? (v.videoHeight || 360) : 360;
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        const scale = Math.max(w / img.width, h / img.height);
        const sw = w / scale;
        const sh = h / scale;
        const sx = (img.width - sw) / 2;
        const sy = (img.height - sh) / 2;
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  };

  const handleSeek = (t) => {
    const v = videoRef.current;
    if (!v || !isFinite(v.duration) || v.duration <= 0) return;
    if (!isFinite(t)) return;
    setFrameTime(t);
    v.currentTime = t;
  };

  const onDragMove = (clientX) => {
    const bar = dragTrackRef.current;
    const v = videoRef.current;
    if (!bar || !v || !isFinite(v.duration) || v.duration <= 0) return;
    const rect = bar.getBoundingClientRect();
    if (!rect.width) return;
    const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    const t = ratio * v.duration;
    if (isFinite(t)) handleSeek(t);
  };

  const buildFrameStrip = () => {
    const v = videoRef.current;
    if (!v || !isFinite(v.duration) || v.duration <= 0) return;
    const src = v.src;
    if (framesCacheRef.current[src]) {
      setFrames(framesCacheRef.current[src]);
      buildingRef.current = false;
      buildingSrcRef.current = null;
      setLoadingFrames(false);
      return;
    }
    if (buildingRef.current && buildingSrcRef.current === src) return;
    buildingRef.current = true;
    buildingSrcRef.current = src;
    setLoadingFrames(true);
    const dur = v.duration;
    const collected = [];
    const times = [];
    for (let i = 0; i < FRAME_COUNT; i++) {
      times.push((dur * i) / (FRAME_COUNT - 1));
    }
    const finish = () => {
      if (buildingRef.current && buildingSrcRef.current === src) {
        buildingRef.current = false;
        buildingSrcRef.current = null;
      }
      framesCacheRef.current[src] = [...collected];
      setFrames([...collected]);
      setLoadingFrames(false);
    };
    const seekTo = (target) =>
      new Promise((resolve) => {
        let settled = false;
        const onSeeked = () => {
          v.removeEventListener("seeked", onSeeked);
          settled = true;
          resolve();
        };
        v.addEventListener("seeked", onSeeked);
        try {
          v.currentTime = target;
        } catch (err) {
          if (!settled) {
            v.removeEventListener("seeked", onSeeked);
            settled = true;
            resolve();
          }
        }
        setTimeout(() => {
          if (!settled && buildingSrcRef.current === src) {
            v.removeEventListener("seeked", onSeeked);
            settled = true;
            resolve();
          }
        }, 300);
      });
    (async () => {
      for (let i = 0; i < times.length; i++) {
        if (buildingSrcRef.current !== src) return;
        await seekTo(times[i]);
        if (buildingSrcRef.current !== src) return;
        try {
          const data = captureFrameAt(v, v.currentTime);
          if (data) collected.push(data);
        } catch (err) {
          /* ignore a single failed frame */
        }
      }
      finish();
    })();
  };

  const dragTrackRef = useRef(null);

  const handlePlayRequest = () => {
    const v = videoRef.current;
    if (!v) return;
    if (activeMode === "upload" && !thumbnail) {
      setShowUploadWarning(true);
      return;
    }
    v.play();
  };

  return (
    <div className="Community-overlay-backdrop" role="presentation">
      <div className="Community-overlay-panel Community-thumb-overlay" role="dialog" aria-modal="true" aria-labelledby="Community-thumb-title">
        <div className="Community-overlay-header">
          <h2 id="Community-thumb-title" className="Community-heading-section">Video thumbnail {videoCount > 1 ? `(${currentVideoIndex + 1}/${videoCount})` : ""}</h2>
          <button type="button" className="Community-overlay-close-btn" onClick={onCancel} aria-label="Close">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        {videoCount > 1 && (
          <>
            <button
              type="button"
              className="Community-thumb-nav-btn Community-thumb-nav-left"
              onClick={() => moveTo((currentVideoIndex - 1 + videoCount) % videoCount)}
              aria-label="Previous video"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              className="Community-thumb-nav-btn Community-thumb-nav-right"
              onClick={() => moveTo((currentVideoIndex + 1) % videoCount)}
              aria-label="Next video"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </>
        )}

        <div className="Community-overlay-body Community-thumb-body">
          <div className="Community-thumb-pill">
            <span
              className="Community-thumb-pill-slider"
              style={{ transform: `translateX(${activeMode === "frame" ? 0 : 100}%)` }}
            />
            <button
              type="button"
              className={`Community-thumb-pill-btn${activeMode === "frame" ? " Community-thumb-pill-active" : ""}`}
              onClick={() => { setActiveMode("frame"); const v = videoRef.current; if (v) setThumbnail(captureFrameAt(v, v.currentTime)); }}
            >
              Frame
            </button>
            <button
              type="button"
              className={`Community-thumb-pill-btn${activeMode === "upload" ? " Community-thumb-pill-active" : ""}`}
              onClick={() => setActiveMode("upload")}
            >
              Upload
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files && e.target.files[0];
                e.target.value = "";
                if (file) {
                  const reader = new FileReader();
                  reader.onload = async () => {
                    const fitted = await fitImageToVideo(reader.result);
                    setThumbnail(fitted);
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
          </div>

          {activeMode === "upload" && (
            <div className="Community-thumb-new-row">
              <button
                type="button"
                className="Community-thumb-new-btn"
                disabled={loadingFrames}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                + New
              </button>
            </div>
          )}

          <div className="Community-thumb-player">
            <video
              ref={videoRef}
              src={video}
              controls
              muted
              onLoadedMetadata={(e) => {
                setDuration(e.currentTarget.duration || 0);
                buildFrameStrip();
              }}
              onLoadedData={buildFrameStrip}
              onCanPlay={buildFrameStrip}
              onPlay={() => {
                if (activeMode === "upload" && !thumbnail) {
                  const v = videoRef.current;
                  if (v) v.pause();
                  setShowUploadWarning(true);
                  return;
                }
                setPlaying(true);
              }}
              onPause={() => setPlaying(false)}
              onSeeked={(e) => setThumbnail(captureFrameAt(e.currentTarget, e.currentTarget.currentTime))}
            />
            {!playing && thumbnail && (
              <img
                src={thumbnail}
                alt="Thumbnail poster"
                className="Community-thumb-poster-overlay"
              />
            )}
            {!playing && !loadingFrames && (
              <button
                type="button"
                className="Community-thumb-play-btn"
                aria-label="Play video"
                onClick={handlePlayRequest}
              >
                <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            )}
            {loadingFrames && (
              <div className="Community-thumb-spinner">
                <div className="Community-thumb-spinner-dot" />
                <div className="Community-thumb-spinner-dot" />
                <div className="Community-thumb-spinner-dot" />
              </div>
            )}
            {showUploadWarning && (
              <div className="Community-thumb-warning" role="alert">
                <p className="Community-thumb-warning-text">Please upload a thumbnail photo before playing the video.</p>
                <div className="Community-thumb-warning-actions">
                  <PrimaryButton onClick={() => { setShowUploadWarning(false); fileInputRef.current && fileInputRef.current.click(); }}>Upload Photo</PrimaryButton>
                </div>
              </div>
            )}
          </div>

          {activeMode === "frame" && (
            <>
              <div
                className="Community-thumb-strip"
                ref={dragTrackRef}
                onPointerDown={(e) => {
                  e.preventDefault();
                  setDragging(true);
                  e.currentTarget.setPointerCapture(e.pointerId);
                  onDragMove(e.clientX);
                }}
                onPointerMove={(e) => { if (dragging) onDragMove(e.clientX); }}
                onPointerUp={() => setDragging(false)}
              >
                {Array.from({ length: FRAME_COUNT }).map((_, i) => (
                  <div
                    key={i}
                    className={`Community-thumb-strip-cell${Math.round((frameTime / duration) * (FRAME_COUNT - 1)) === i ? " Community-thumb-strip-active" : ""}`}
                  >
                    {frames[i] ? (
                      <img src={frames[i]} alt={`Frame ${i + 1}`} />
                    ) : (
                      <span className="Community-thumb-strip-placeholder" />
                    )}
                  </div>
                ))}
              </div>

              <p className="Community-thumb-hint">Drag across the frames to pick a thumbnail.</p>
            </>
          )}
        </div>

        <div className="Community-overlay-actions Community-overlay-actions-center Community-thumb-actions">
          <PrimaryButton onClick={handleSave} disabled={!thumbnail || loadingFrames}>Save</PrimaryButton>
        </div>

        {showConfirmPreview && thumbnail && (
          <div className="Community-overlay-backdrop" role="presentation">
            <div className="Community-overlay-panel Community-thumb-confirm" role="dialog" aria-modal="true" aria-labelledby="Community-thumb-confirm-title">
              <div className="Community-overlay-header Community-thumb-confirm-header">
                <h2 id="Community-thumb-confirm-title" className="Community-heading-section">Thumbnail Preview {videoCount > 1 ? `(${currentVideoIndex + 1}/${videoCount})` : ""}</h2>
                <button type="button" className="Community-overlay-close-btn" onClick={() => setShowConfirmPreview(false)} aria-label="Close">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="Community-overlay-body">
                <img src={thumbnail} alt="Thumbnail preview" className="Community-thumb-confirm-img" />
              </div>
              <div className="Community-overlay-actions Community-overlay-actions-center">
                <PrimaryButton onClick={() => { setShowConfirmPreview(false); commitAndAdvance(thumbnail); }}>Accept</PrimaryButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const TagPeopleOverlay = ({ selected, onSave, onClose, readOnly = false }) => {
  const [pending, setPending] = useState(selected);
  const [search, setSearch] = useState("");
  const [followedMembers, setFollowedMembers] = useState(new Set());
  const tagsRef = useRef(null);

  const connections = MOCK_MEMBERS.filter((m) => m.id !== CURRENT_USER_ID);

  const scrollTags = (dir) => {
    if (tagsRef.current) {
      tagsRef.current.scrollBy({ left: dir * 120, behavior: "smooth" });
    }
  };

  const toggle = (id) => {
    setPending((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleFollowMember = (id) => {
    setFollowedMembers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (pending.length > 0 && tagsRef.current) {
      const raf = requestAnimationFrame(() => {
        if (tagsRef.current) {
          tagsRef.current.scrollTo({ left: tagsRef.current.scrollWidth, behavior: "smooth" });
        }
      });
      return () => cancelAnimationFrame(raf);
    }
    return undefined;
  }, [pending]);

  const listMembers = readOnly
    ? selected.map((id) => getMemberById(id)).filter(Boolean)
    : connections;

  const filtered = listMembers.filter((m) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const name = (m.name || "").toLowerCase();
    const company = (m.company || "").toLowerCase();
    const designation = (m.designation || "").toLowerCase();
    return name.includes(q) || company.includes(q) || designation.includes(q);
  });

  return (
    <Overlay title="People" subtitle={undefined} onClose={onClose} panelClassName="Community-tag-overlay" closeOnlyByButton>
      <div className="Community-tag-body">
        <div className="Community-tag-search">
          <input
            type="text"
            className="Community-tag-search-input"
            placeholder="Search people"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="Community-tag-search-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
        </div>
        {!readOnly && pending.length > 0 && (
          <div className="Community-tag-section">
            <h5 className="Community-tag-section-title">Tags</h5>
            {pending.length > 4 && (
              <button
                type="button"
                className="Community-tag-nav Community-tag-nav-left"
                onClick={() => scrollTags(-1)}
                aria-label="Scroll tags left"
                title="Scroll tags left"
              >&#8249;</button>
            )}
            <div className="Community-tag-selected-list" ref={tagsRef}>
              {pending.map((id) => {
                const m = getMemberById(id);
                if (!m) return null;
                return (
                  <span key={m.id} className="Community-tag-selected-chip" title={m.name}>
                    <span className="Community-member-avatar Community-tag-selected-avatar" aria-hidden="true">{m.avatar}</span>
                    <button
                      type="button"
                      className="Community-tag-selected-remove"
                      onClick={() => toggle(m.id)}
                      aria-label={`Remove ${m.name}`}
                      title="Remove tag"
                    >×</button>
                  </span>
                );
              })}
            </div>
            {pending.length > 4 && (
              <button
                type="button"
                className="Community-tag-nav Community-tag-nav-right"
                onClick={() => scrollTags(1)}
                aria-label="Scroll tags right"
                title="Scroll tags right"
              >&#8250;</button>
            )}
          </div>
        )}
        <ul className="Community-tag-list">
          {filtered.map((m, idx) => {
            const checked = (readOnly ? selected : pending).includes(m.id);
            if (!readOnly) {
              return (
                <li key={m.id} className={`Community-tag-item ${checked ? "Community-tag-item-selected" : ""}`}>
                  <label className="Community-tag-label" onClick={() => toggle(m.id)}>
                    <span className="Community-member-avatar Community-comment-avatar" aria-hidden="true">{m.avatar}</span>
                    <span className="Community-tag-item-text">
                      <span className="Community-tag-item-name">{m.name}</span>
                      <span className="Community-tag-item-company">{m.designation} · {m.company}</span>
                    </span>
                    {checked ? (
                      <span className="Community-tag-check" aria-hidden="true">✓</span>
                    ) : (
                      <span className="Community-tag-add" aria-hidden="true">+</span>
                    )}
                  </label>
                </li>
              );
            }
            const isFollowing = followedMembers.has(m.id);
            return (
              <li key={m.id} className="Community-tag-item Community-tag-item-plain">
                <span className="Community-tag-item-index" aria-hidden="true">{idx + 1}.</span>
                <span className="Community-member-avatar Community-comment-avatar" aria-hidden="true">{m.avatar}</span>
                <span className="Community-tag-item-text">
                  <span className="Community-tag-item-name">{m.name}</span>
                  <span className="Community-tag-item-company">{m.designation} · {m.company}</span>
                </span>
                <button
                  type="button"
                  className={`Community-follow-btn ${isFollowing ? "Community-following" : ""}`}
                  onClick={() => toggleFollowMember(m.id)}
                  aria-pressed={isFollowing}
                >
                  {isFollowing ? (
                    <>
                      <span className="Community-follow-tick" aria-hidden="true">✓</span>
                      Following
                    </>
                  ) : (
                    <>
                      <span className="Community-follow-plus" aria-hidden="true">+</span>
                      Follow
                    </>
                  )}
                </button>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li className="Community-tag-item Community-tag-empty">No people found</li>
          )}
        </ul>
      </div>
      <div className="Community-overlay-actions">
        <PrimaryButton onClick={() => (readOnly ? onClose() : onSave(pending))}>
          Done
        </PrimaryButton>
      </div>
    </Overlay>
  );
};

const SUGGESTED_PLACES = [
  "New York, NY",
  "Los Angeles, CA",
  "Chicago, IL",
  "Houston, TX",
  "Phoenix, AZ",
  "Philadelphia, PA",
  "San Antonio, TX",
  "San Diego, CA",
  "Dallas, TX",
  "San Jose, CA",
  "Austin, TX",
  "Jacksonville, FL",
  "Fort Worth, TX",
  "Columbus, OH",
  "Charlotte, NC",
  "San Francisco, CA",
  "Indianapolis, IN",
  "Seattle, WA",
  "Denver, CO",
  "Washington, DC",
  "Boston, MA",
  "Nashville, TN",
  "Detroit, MI",
  "Portland, OR",
  "Las Vegas, NV",
  "Miami, FL",
  "Atlanta, GA",
  "New Orleans, LA",
  "Toronto, Canada",
  "London, UK",
  "Paris, France",
  "Berlin, Germany",
  "Tokyo, Japan",
  "Sydney, Australia",
  "Mumbai, India",
  "Delhi, India",
  "Bengaluru, India",
  "Hyderabad, India",
  "Dubai, UAE",
  "Singapore",
];

const buildAddressLabel = (data) => {
  const ad = data.address || {};
  const parts = [
    ad.road,
    ad.neighbourhood,
    ad.suburb,
    ad.city || ad.town || ad.village,
    ad.state,
    ad.country,
  ].filter(Boolean);
  return parts.join(", ");
};

const LOC_PIN_SVG =
  '<svg viewBox="0 0 24 24" width="38" height="38" aria-hidden="true"><defs><radialGradient id="locPinMap3d" cx="50%" cy="32%" r="75%"><stop offset="0%" stop-color="#ff7ab8"/><stop offset="60%" stop-color="#d92776"/><stop offset="100%" stop-color="#8e1149"/></radialGradient></defs><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="url(#locPinMap3d)" stroke="#6b0d3b" stroke-width="0.8"/><ellipse cx="12" cy="9" rx="3" ry="3" fill="#ffffff"/></svg>';

export const LocationOverlay = ({ value = "", onSave, onClose }) => {
  const [query, setQuery] = useState(value);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState("");
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const pinIconRef = useRef(null);
  const fromMapRef = useRef(false);
  const trimmed = query.trim();

  const placeMarker = useCallback((lat, lng) => {
    const map = mapRef.current;
    if (!map) return;
    if (!pinIconRef.current) {
      pinIconRef.current = L.divIcon({
        className: "Community-loc-marker-icon",
        html: LOC_PIN_SVG,
        iconSize: [38, 38],
        iconAnchor: [19, 36],
      });
    }
    if (!markerRef.current) {
      markerRef.current = L.marker([lat, lng], { icon: pinIconRef.current }).addTo(map);
    } else {
      markerRef.current.setLatLng([lat, lng]);
    }
    map.setView([lat, lng], Math.max(map.getZoom(), 12));
  }, []);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    const map = L.map(mapContainerRef.current, { scrollWheelZoom: false }).setView([28.6315, 77.2167], 14);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    const onMapClick = async (e) => {
      const { lat, lng } = e.latlng;
      fromMapRef.current = true;
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`
        );
        const data = await res.json();
        setQuery(buildAddressLabel(data) || `${lat.toFixed(5)}, ${lng.toFixed(5)}`);
      } catch (err) {
        setQuery(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
      }
      placeMarker(lat, lng);
    };
    map.on("click", onMapClick);
    mapRef.current = map;

    return () => {
      map.off("click", onMapClick);
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [placeMarker]);

  useEffect(() => {
    const q = trimmed;
    if (!q) return;
    if (fromMapRef.current) {
      fromMapRef.current = false;
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(q)}&limit=1`
        );
        const data = await res.json();
        if (data && data.length > 0) {
          placeMarker(parseFloat(data[0].lat), parseFloat(data[0].lon));
        }
      } catch (err) {}
    }, 450);
    return () => clearTimeout(timer);
  }, [trimmed, placeMarker]);

  const suggestions = (() => {
    const q = trimmed.toLowerCase();
    if (!q) return [];
    const starts = [];
    const contains = [];
    SUGGESTED_PLACES.forEach((p) => {
      const pl = p.toLowerCase();
      if (pl.startsWith(q)) starts.push(p);
      else if (pl.includes(q)) contains.push(p);
    });
    return [...starts, ...contains].slice(0, 6);
  })();

  const pick = (place) => {
    setQuery(place);
    setLocateError("");
  };

  const locateMe = () => {
    if (!navigator.geolocation) {
      setLocateError("Geolocation is not supported by this browser.");
      return;
    }
    setLocating(true);
    setLocateError("");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          const data = await res.json();
          const parts = [data.city || data.locality, data.principalSubdivision, data.countryName].filter(Boolean);
          setQuery(parts.join(", "));
        } catch (e) {
          setQuery(`${latitude.toFixed(5)}, ${longitude.toFixed(5)}`);
        }
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        setLocateError("Could not get your location. Please allow location access.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  return (
    <Overlay title="Location" onClose={onClose} panelClassName="Community-location-overlay" closeOnlyByButton>
      <div className="Community-location-body">
        <div className="Community-location-search-row">
          <div className="Community-tag-search">
            <input
              type="text"
              className="Community-tag-search-input"
              placeholder="Search for a place"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setLocateError(""); }}
              autoComplete="off"
            />
            <span className="Community-tag-search-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
          </div>
          <button
            type="button"
            className="Community-location-locate-btn"
            onClick={locateMe}
            disabled={locating}
            aria-label="Locate me"
            title="Use my current location"
          >
            {locating ? (
              <span className="Community-location-locate-spinner" aria-hidden="true">⋯</span>
            ) : (
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <defs>
                  <radialGradient id="locPin3d" cx="50%" cy="32%" r="75%">
                    <stop offset="0%" stopColor="#ff7ab8" />
                    <stop offset="60%" stopColor="#d92776" />
                    <stop offset="100%" stopColor="#8e1149" />
                  </radialGradient>
                </defs>
                <path
                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                  fill="url(#locPin3d)"
                  stroke="#6b0d3b"
                  strokeWidth="0.8"
                />
                <ellipse cx="12" cy="9" rx="3" ry="3" fill="#ffffff" />
              </svg>
            )}
          </button>
        </div>
        {locateError && <p className="Community-location-locate-error">{locateError}</p>}
        {suggestions.length > 0 && (
          <ul className="Community-location-suggestions" role="listbox">
            {suggestions.map((place) => (
              <li key={place}>
                <button
                  type="button"
                  className="Community-location-suggestion"
                  onClick={() => pick(place)}
                  role="option"
                  aria-selected={trimmed.toLowerCase() === place.toLowerCase()}
                >
                  <span className="Community-location-suggestion-pin" aria-hidden="true">📍</span>
                  {place}
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="Community-location-map">
          <div ref={mapContainerRef} className="Community-location-map-canvas" />
          {trimmed && (
            <p className="Community-location-map-label">
              <span className="Community-location-map-pin" aria-hidden="true">📍</span>
              Pick a spot on the map — it will appear in the search box.
            </p>
          )}
        </div>
      </div>
      <div className="Community-overlay-actions">
        <PrimaryButton onClick={() => onSave(trimmed)} disabled={!trimmed}>
          Location
        </PrimaryButton>
      </div>
    </Overlay>
  );
};

const COUNTRY_CODES = [
  { code: "IN", name: "IN", dial: "91" },
  { code: "US", name: "US", dial: "1" },
  { code: "UK", name: "UK", dial: "44" },
  { code: "AE", name: "AE", dial: "971" },
  { code: "AU", name: "AU", dial: "61" },
  { code: "BD", name: "BD", dial: "880" },
  { code: "CA", name: "CA", dial: "1" },
  { code: "DE", name: "DE", dial: "49" },
  { code: "FR", name: "FR", dial: "33" },
  { code: "PK", name: "PK", dial: "92" },
  { code: "SA", name: "SA", dial: "966" },
  { code: "SG", name: "SG", dial: "65" },
  { code: "NZ", name: "NZ", dial: "64" },
];

export const SOCIAL_PLATFORMS = [
  {
    id: "whatsapp",
    label: "WhatsApp",
    placeholder: "Enter phone number",
    color: "#25D366",
    icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#25D366" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>,
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    placeholder: "Enter profile URL",
    color: "#0A66C2",
    icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#0A66C2" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" /></svg>,
  },
  {
    id: "facebook",
    label: "Facebook",
    placeholder: "Enter profile URL",
    color: "#1877F2",
    icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#1877F2" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>,
  },
  {
    id: "gmail",
    label: "Gmail",
    placeholder: "Enter email address",
    color: "#EA4335",
    icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#EA4335" aria-hidden="true"><path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" /></svg>,
  },
  {
    id: "twitter",
    label: "X",
    placeholder: "Enter username",
    color: "#000000",
    icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#000000" aria-hidden="true"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" /></svg>,
  },
  {
    id: "youtube",
    label: "YouTube",
    placeholder: "Enter channel link or URL",
    color: "#FF0000",
    icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="#FF0000" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>,
  },
];

export const socialPlatformById = (id) => SOCIAL_PLATFORMS.find((p) => p.id === id) || null;

export const socialHref = (s) => {
  if (s.id === "whatsapp") {
    const num = (s.value || "").replace(/[^0-9]/g, "");
    return `https://wa.me/${num}`;
  }
  if (s.id === "gmail") return `mailto:${s.value}`;
  if (/^https?:\/\//i.test(s.value)) return s.value;
  if (s.id === "twitter") return `https://x.com/${s.value.replace(/^@/, "")}`;
  return `https://${s.value}`;
};

export const PostSocials = ({ socials }) => {
  if (!socials || socials.length === 0) return null;
  return (
    <div className="Community-post-socials">
      {socials.map((s) => {
        const p = socialPlatformById(s.id);
        if (!p) return null;
        return (
          <a
            key={s.id}
            className="Community-post-social-chip"
            href={socialHref(s)}
            target="_blank"
            rel="noreferrer"
            title={`${p.label}: ${s.value}`}
          >
            {p.icon}
          </a>
        );
      })}
    </div>
  );
};

const SocialLinkAddOverlay = ({ platform, initialValue = "", onAdd, onClose }) => {
  const isWhatsApp = platform && platform.id === "whatsapp";

  const initInput = () => {
    if (!isWhatsApp || !initialValue) return "";
    const m = initialValue.match(/^\+(\d+)\s+(\d+)$/);
    return m ? m[2] : initialValue.replace(/\D/g, "").slice(0, 10);
  };
  const initCountry = () => {
    if (!isWhatsApp || !initialValue) return "IN";
    const m = initialValue.match(/^\+(\d+)\s+(\d+)$/);
    if (m && COUNTRY_CODES.some((c) => c.dial === m[1])) return COUNTRY_CODES.find((c) => c.dial === m[1]).code;
    return "IN";
  };

  const [input, setInput] = useState(initInput);
  const [country, setCountry] = useState(initCountry);

  const canAdd = isWhatsApp ? input.replace(/\D/g, "").length === 10 : input.trim().length > 0;

  const handleAdd = () => {
    if (isWhatsApp) {
      const number = input.replace(/\D/g, "");
      if (number.length !== 10) return;
      const cc = COUNTRY_CODES.find((c) => c.code === country) || COUNTRY_CODES[0];
      onAdd(`+${cc.dial} ${number}`);
    } else {
      onAdd(input.trim());
    }
  };

  return (
    <Overlay onClose={onClose} panelClassName="Community-social-add-overlay" closeOnlyByButton hideTitle>
      <div className="Community-social-add-body">
        <span className="Community-social-add-icon">{platform.icon}</span>
        {isWhatsApp && (
          <div className="Community-social-phone-row">
            <select
              className="Community-tag-search-input Community-social-country-select"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>{c.name} +{c.dial}</option>
              ))}
            </select>
            <input
              type="text"
              inputMode="numeric"
              maxLength={10}
              className="Community-tag-search-input Community-social-add-input Community-social-phone-input"
              placeholder={platform.placeholder}
              value={input}
              onChange={(e) => setInput(e.target.value.replace(/\D/g, ""))}
              autoFocus
            />
          </div>
        )}
        {!isWhatsApp && (
          <div className="Community-social-single-input">
            <input
              type="text"
              inputMode="text"
              className="Community-tag-search-input Community-social-add-input"
              placeholder={platform.placeholder}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              autoFocus
            />
          </div>
        )}
        <div className="Community-social-add-actions">
          <PrimaryButton onClick={handleAdd} disabled={!canAdd}>Add</PrimaryButton>
        </div>
      </div>
    </Overlay>
  );
};

export const SocialLinksOverlay = ({ value = [], onSave, onClose }) => {
  const [links, setLinks] = useState(value);
  const [adding, setAdding] = useState(null);

  const addLinkFor = (id, v) => {
    setLinks((prev) => {
      const exists = prev.find((l) => l.id === id);
      if (exists) return prev.map((l) => (l.id === id ? { ...l, value: v } : l));
      return [...prev, { id, value: v }];
    });
    setAdding(null);
  };

  const removeLink = (id) => {
    setLinks((prev) => prev.filter((l) => l.id !== id));
  };

  const openAdd = (p) => {
    const existing = links.find((l) => l.id === p.id);
    setAdding({ platform: p, initial: existing ? existing.value : "" });
  };

  return (
    <Overlay title="Accounts" onClose={onClose} panelClassName="Community-social-overlay" closeOnlyByButton>
      <div className="Community-social-body">
        <div className="Community-social-grid">
          {SOCIAL_PLATFORMS.map((p) => (
            <button
              key={p.id}
              type="button"
              className="Community-social-option"
              onClick={() => openAdd(p)}
              title={p.label}
            >
              <span className="Community-social-option-icon">{p.icon}</span>
            </button>
          ))}
        </div>
        {links.length > 0 && (
          <div className="Community-social-added">
            <h5 className="Community-tag-section-title">Added</h5>
            <div className="Community-social-added-list">
              {links.map((l) => {
                const p = socialPlatformById(l.id);
                if (!p) return null;
                return (
                  <div key={l.id} className="Community-social-added-item">
                    <span className="Community-social-option-icon">{p.icon}</span>
                    <span className="Community-social-added-text">{l.value}</span>
                    <button
                      type="button"
                      className="Community-social-added-action Community-social-added-edit"
                      onClick={() => openAdd(p)}
                      aria-label={`Edit ${p.label}`}
                      title="Edit link"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="Community-social-added-action Community-social-added-remove"
                      onClick={() => removeLink(l.id)}
                      aria-label={`Remove ${p.label}`}
                      title="Remove link"
                    >×</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div className="Community-overlay-actions">
        <button
          type="button"
          className="Community-btn Community-btn-primary"
          onClick={() => { onSave(links); onClose(); }}
        >
          Done
        </button>
      </div>
      {adding && (
        <SocialLinkAddOverlay
          platform={adding.platform}
          initialValue={adding.initial}
          onAdd={(v) => addLinkFor(adding.platform.id, v)}
          onClose={() => setAdding(null)}
        />
      )}
    </Overlay>
  );
};

const CreatePostModal = ({ onClose, onCreate, initialMode, initialPhotos = [], initialVideo = null, initialVideos = [], initialPost = null, initialYoutubeOpen = false }) => {
  const [text, setText] = useState(initialPost ? initialPost.text || "" : "");
  const [title, setTitle] = useState(initialPost ? initialPost.title || "" : "");
  const [titleColor, setTitleColor] = useState(initialPost ? initialPost.titleColor || "#000000" : "#000000");
  const [titleSize, setTitleSize] = useState(initialPost ? initialPost.titleSize || 20 : 20);
  const [titleWeight, setTitleWeight] = useState(initialPost ? initialPost.titleWeight || 700 : 700);
  const [titleItalic, setTitleItalic] = useState(initialPost ? Boolean(initialPost.titleItalic) : false);
  const [titleUnderline, setTitleUnderline] = useState(initialPost ? Boolean(initialPost.titleUnderline) : false);
  const [showTitleFont, setShowTitleFont] = useState(false);
  const [textColor, setTextColor] = useState(initialPost ? initialPost.textColor || "#1a1a1a" : "#1a1a1a");
  const [textSize, setTextSize] = useState(initialPost ? initialPost.textSize || 16 : 16);
  const [textWeight, setTextWeight] = useState(initialPost ? initialPost.textWeight || 400 : 400);
  const [textItalic, setTextItalic] = useState(initialPost ? Boolean(initialPost.textItalic) : false);
  const [textUnderline, setTextUnderline] = useState(initialPost ? Boolean(initialPost.textUnderline) : false);
  const [showDescFont, setShowDescFont] = useState(false);
  const [visibility, setVisibility] = useState(initialPost ? initialPost.visibility : POST_VISIBILITY.ANYONE);
  const [commentPermission, setCommentPermission] = useState(
    initialPost
      ? initialPost.hideComments
        ? COMMENT_PERMISSION.OFF
        : initialPost.commentPermission || COMMENT_PERMISSION.ANYONE
      : COMMENT_PERMISSION.ANYONE
  );
  const [emojiEnabled, setEmojiEnabled] = useState(initialPost ? initialPost.emojiEnabled : true);
  const [hideLikes, setHideLikes] = useState(initialPost ? Boolean(initialPost.hideLikes) : false);
  const [hideComments, setHideComments] = useState(initialPost ? Boolean(initialPost.hideComments) : false);
  const [hideLikeCount, setHideLikeCount] = useState(initialPost ? Boolean(initialPost.hideLikeCount) : false);
  const [hideCommentCount, setHideCommentCount] = useState(initialPost ? Boolean(initialPost.hideCommentCount) : false);
  const [photos, setPhotos] = useState(initialPost ? initialPost.photos || [] : initialPhotos);
  const [videos, setVideos] = useState(initialPost ? (initialPost.videos || (initialPost.video ? [initialPost.video] : [])) : (initialVideos.length ? initialVideos : (initialVideo ? [initialVideo] : [])));
  const [gif, setGif] = useState(initialPost ? initialPost.gif || null : null);
  const [youtube, setYoutube] = useState(() =>
    initialPost ? normalizeYoutube(initialPost.youtube) : []
  );
  const [youtubeOpen, setYoutubeOpen] = useState(Boolean(initialYoutubeOpen));
  const [youtubeLink, setYoutubeLink] = useState("");
  const [youtubeError, setYoutubeError] = useState("");
  const [taggedPeople, setTaggedPeople] = useState(initialPost ? initialPost.taggedPeople || [] : []);
  const [showTagOverlay, setShowTagOverlay] = useState(false);
  const [showLocationOverlay, setShowLocationOverlay] = useState(false);
  const [location, setLocation] = useState(initialPost ? initialPost.location || "" : "");
  const [socials, setSocials] = useState(initialPost ? initialPost.socials || [] : []);
  const [showSocialOverlay, setShowSocialOverlay] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showLimitOverlay, setShowLimitOverlay] = useState(false);
  const [showThumbnailOverlay, setShowThumbnailOverlay] = useState(false);
  const [videoThumbnail, setVideoThumbnail] = useState(initialPost ? initialPost.videoThumbnail || null : null);
  const [videoThumbnails, setVideoThumbnails] = useState(initialPost && initialPost.videoThumbnails && initialPost.videoThumbnails.length ? initialPost.videoThumbnails : []);
  const pendingVideoRef = useRef();
  const addMediaInputRef = useRef(null);
  const thumbnailPendingPostRef = useRef(false);
  const [showEditThumbPreview, setShowEditThumbPreview] = useState(false);
  const [mode, setMode] = useState(initialMode || "post");
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const thumbStripRef = useRef(null);
  const dragScrollState = useRef({ raf: null, clientX: 0 });

  const stopDragScroll = () => {
    if (dragScrollState.current.raf) {
      cancelAnimationFrame(dragScrollState.current.raf);
      dragScrollState.current.raf = null;
    }
  };

  const startDragScroll = () => {
    if (dragScrollState.current.raf) return;
    const loop = () => {
      const strip = thumbStripRef.current;
      const { clientX } = dragScrollState.current;
      if (strip) {
        const rect = strip.getBoundingClientRect();
        const edge = 52;
        let speed = 0;
        if (clientX < rect.left + edge) speed = -12;
        else if (clientX > rect.right - edge) speed = 12;
        if (speed !== 0) {
          strip.scrollLeft += speed;
          dragScrollState.current.raf = requestAnimationFrame(loop);
          return;
        }
      }
      dragScrollState.current.raf = null;
    };
    dragScrollState.current.raf = requestAnimationFrame(loop);
  };
  const [showEditor, setShowEditor] = useState(false);
  const [showVideoEditor, setShowVideoEditor] = useState(false);
  const [videoEdits, setVideoEdits] = useState(() =>
    initialPost && initialPost.videoEdits && initialPost.videoEdits.length ? [...initialPost.videoEdits] : []
  );
  const [imageCorners, setImageCorners] = useState({});
  const isEditing = Boolean(initialPost);
  const [mediaOrder, setMediaOrder] = useState(() => [
    ...photos.map((p) => ({ type: "image", src: p })),
    ...videos.map((v) => ({ type: "video", src: v })),
    ...normalizeYoutube(initialPost && initialPost.youtube).map((v) => ({ type: "youtube", src: v.embed, url: v.url, thumb: v.thumb })),
  ]);
  const editBySrc = {};
  videos.forEach((v, i) => { editBySrc[v] = videoEdits[i] || null; });

  const mediaItems = mediaOrder.map((m) => {
    if (m.type === "image") return { type: "image", src: m.src };
    if (m.type === "youtube") return { type: "youtube", src: m.src, url: m.url, thumb: m.thumb };
    return { type: "video", src: m.src, edits: editBySrc[m.src] || null };
  });
  const mediaTotal = mediaItems.length;
  const currentMedia = mediaItems[Math.min(previewIndex, Math.max(0, mediaTotal - 1))];

  useEffect(() => {
    if (mediaTotal === 0 || !thumbStripRef.current) return;
    const strip = thumbStripRef.current;
    const active = strip.querySelector(".Community-post-preview-thumb-active");
    if (!active) return;
    strip.scrollLeft = active.offsetLeft - strip.offsetWidth / 2 + active.offsetWidth / 2;
  }, [previewIndex, mediaTotal]);

  const prevMedia = () => setPreviewIndex((i) => (i - 1 + mediaTotal) % mediaTotal);
  const nextMedia = () => setPreviewIndex((i) => (i + 1) % mediaTotal);

  const removeMediaAt = (i) => {
    const item = mediaOrder[i];
    if (!item) return;
    const nextOrder = mediaOrder.filter((_, idx) => idx !== i);
    setMediaOrder(nextOrder);
    setPhotos(nextOrder.filter((m) => m.type === "image").map((m) => m.src));
    setVideos(nextOrder.filter((m) => m.type === "video").map((m) => m.src));
    if (item.type === "youtube") {
      setYoutube((prev) => {
        const idx = prev.findIndex((y) => y.url === item.url && y.embed === item.src);
        return idx === -1 ? prev : prev.filter((_, k) => k !== idx);
      });
    }
    setVideoEdits((prev) => prev.filter((_, idx) => videos[idx] !== item.src));
    setPreviewIndex(0);
  };

  const reorderMedia = (from, to) => {
    if (from === to) return;
    setMediaOrder((prev) => {
      const list = [...prev];
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      return list;
    });
    setPreviewIndex(to);
  };

  const totalMedia = photos.length + videos.length + youtube.length;
  const MEDIA_LIMIT = POST_MAX_MEDIA;

  const openAddPicker = () => {
    if (totalMedia >= MEDIA_LIMIT) {
      setShowLimitOverlay(true);
      return;
    }
    addMediaInputRef.current && addMediaInputRef.current.click();
  };

  const handleAddMedia = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const newPhotos = [];
    const newVideos = [];
    files.forEach((f) => {
      const name = (f.name || "").toLowerCase();
      const isVideo =
        (f.type && f.type.startsWith("video/")) ||
        /\.(mp4|webm|ogg|ogv|mov|m4v|avi|wmv|mkv|flv|f4v|3gp|3g2|mpeg|mpg|ts|m2ts|vob|rm|rmvb|asf|divx|mxf|wtv|dvr-ms)$/.test(name);
      if (isVideo) {
        newVideos.push(URL.createObjectURL(f));
      } else {
        newPhotos.push(URL.createObjectURL(f));
      }
    });
    if (videos.length >= POST_MAX_VIDEOS && newVideos.length > 0) {
      setShowLimitOverlay(true);
      return;
    }
    const videoSlots = POST_MAX_VIDEOS - videos.length;
    if (newVideos.length > videoSlots) {
      setShowLimitOverlay(true);
      return;
    }
    const finalVideos = newVideos.slice(0, videoSlots);
    const available = MEDIA_LIMIT - totalMedia;
    if (newPhotos.length + finalVideos.length > available) {
      setShowLimitOverlay(true);
      return;
    }
    setPhotos((prev) => [...prev, ...newPhotos].slice(0, MEDIA_LIMIT));
    setVideos((prev) => [...prev, ...finalVideos].slice(0, MEDIA_LIMIT));
    setMediaOrder((prev) => [
      ...prev,
      ...newPhotos.map((p) => ({ type: "image", src: p })),
      ...finalVideos.map((v) => ({ type: "video", src: v })),
    ]);
  };

  const addYoutube = (value) => {
    const parsed = parseYoutubeLink(value);
    if (!parsed) {
      setYoutubeError("Enter a valid YouTube link.");
      return;
    }
    if (youtube.length >= YT_MAX) {
      setShowLimitOverlay(true);
      return;
    }
    if (youtube.some((y) => y.embed === parsed.embed)) {
      setYoutubeError("This YouTube video is already added.");
      return;
    }
    if (totalMedia >= MEDIA_LIMIT) {
      setShowLimitOverlay(true);
      return;
    }
    setYoutube((prev) => [...prev, parsed]);
    setMediaOrder((prev) => [
      ...prev,
      { type: "youtube", src: parsed.embed, url: parsed.url, thumb: parsed.thumb },
    ]);
    setYoutubeLink("");
    setYoutubeError("");
    setYoutubeOpen(false);
    setPreviewIndex(mediaOrder.length);
  };

  const insertEmoji = (emoji) => {
    setText((prev) => prev + emoji);
    setShowEmoji(false);
  };

  const mediaDerived = () => {
    const imgs = [];
    const vids = [];
    mediaOrder.forEach((m) => {
      if (m.type === "image") imgs.push(m.src);
      else if (m.type === "video") vids.push(m.src);
    });
    const edits = vids.map((src) => videoEdits[videos.indexOf(src)] || null);
    return {
      imgs,
      vids,
      edits,
      youtube,
      mediaOrder: mediaOrder.map((m) => (
        m.type === "youtube"
          ? { type: "youtube", embed: m.src, url: m.url || "", thumb: m.thumb || "" }
          : { type: m.type, src: m.src }
      )),
    };
  };

  const buildPayload = (thumbs) => {
    const { imgs, vids, edits, mediaOrder: order } = mediaDerived();
    const first = thumbs.find((t) => t) || null;
    return {
      text,
      textColor,
      textSize,
      textWeight,
      textItalic,
      textUnderline,
      title,
      titleColor,
      titleSize,
      titleWeight,
      titleItalic,
      titleUnderline,
      visibility,
      commentPermission,
      emojiEnabled,
      hideLikes,
      hideComments,
      hideLikeCount,
      hideCommentCount,
      photos: imgs,
      videos: vids,
      videoEdits: edits,
      videoThumbnail: first,
      videoThumbnails: thumbs,
      youtube,
      mediaOrder: order,
      gif,
      taggedPeople,
      location,
      socials,
      event: null,
    };
  };

  const handlePost = () => {
    if (!text.trim() && photos.length === 0 && videos.length === 0 && youtube.length === 0) return;
    const { vids } = mediaDerived();
    if (vids.length > 0) {
      if (isEditing) {
        setShowEditThumbPreview(true);
      } else {
        thumbnailPendingPostRef.current = true;
        setShowThumbnailOverlay(true);
      }
      return;
    }
    onCreate(buildPayload(videoThumbnails));
  };

  const handleEventCreated = (eventData) => {
    onCreate({
      event: eventData,
      text: "",
      visibility,
      commentPermission,
      emojiEnabled,
      hideLikes,
      hideComments,
      hideLikeCount,
      hideCommentCount,
      photos: [],
      videos: [],
      gif: null,
    });
    setShowCreateEvent(false);
    onClose();
  };

  return (
    <Overlay title={isEditing ? "Edit post" : "Create a post"} onClose={onClose} panelClassName="Community-overlay-edit-post" closeOnlyByButton>
      <div className="Community-post-create-layout">
        <div className="Community-post-create-meta">
          <div className="Community-avatar" aria-hidden="true">
            {getMemberById(CURRENT_USER_ID)?.avatar}
          </div>
          <div className="Community-card-header-info">
            <p className="Community-poster-name">{getMemberById(CURRENT_USER_ID)?.name}</p>
            <p className="Community-company-name">{getMemberById(CURRENT_USER_ID)?.company}</p>
          </div>
        </div>
        <div className="Community-post-create-row">
          <div className={`Community-post-create-preview${(!isEditing && !mediaTotal && !gif) ? " Community-post-create-preview--empty" : ""}${(isEditing && !mediaTotal && !gif) ? " Community-post-create-preview--edit-empty" : ""}`}>
            <div className="Community-post-preview-top">
            <div className="Community-post-preview-add">
              <div className={`Community-post-preview-add-group${isEditing ? " Community-post-preview-add-group--editing" : ""}`}>
                {!isEditing && (mediaTotal > 0 || gif) && (
                  <button
                    type="button"
                    className="Community-post-preview-plus"
                    onClick={openAddPicker}
                    aria-label="Add media"
                    title="Add media"
                  >
                    +
                  </button>
                )}
                {currentMedia && (currentMedia.type === "image" || currentMedia.type === "video") && (
                  <button
                    type="button"
                    className="Community-post-preview-edit"
                    onClick={() => {
                      if (currentMedia.type === "image") setShowEditor(true);
                      else setShowVideoEditor(true);
                    }}
                    aria-label={currentMedia.type === "image" ? "Edit image" : "Edit video"}
                    title={currentMedia.type === "image" ? "Edit image" : "Edit video"}
                  >
                    ✏️
                  </button>
                )}
                {mediaTotal > 0 && !isEditing && (
                  <button
                    type="button"
                    className="Community-post-preview-bin"
                    onClick={() => removeMediaAt(Math.min(previewIndex, mediaTotal - 1))}
                    aria-label="Remove current media"
                    title="Remove current media"
                  >
                    🗑️
                  </button>
                )}
                <input
                  ref={addMediaInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="Community-file-input"
                  onChange={handleAddMedia}
                />
              </div>
            </div>
          </div>
          {!mediaTotal && !gif && (
            <div className="Community-image-placeholder Community-post-preview-media-placeholder">
              <p className="Community-body-text">{isEditing ? "No post" : "Upload photo or video"}</p>
            </div>
          )}
          {!isEditing && !mediaTotal && !gif && (
            <div className="Community-upload-inline Community-post-preview-upload-inline">
              <SecondaryButton onClick={openAddPicker}>Upload</SecondaryButton>
            </div>
          )}
          {mediaTotal > 0 && (
            <PostPreviewMedia mediaItems={mediaItems} onSelectMedia={(i) => setPreviewIndex(i)} currentIndex={Math.min(previewIndex, mediaTotal - 1)} onRemoveMedia={removeMediaAt} />
          )}
          {gif && <img src={gif.preview || gif.media} alt="Preview GIF" className="Community-post-gif" />}
          <span className="Community-post-preview-count">{totalMedia}/{MEDIA_LIMIT}</span>
          {taggedPeople.length > 0 && (
          <button
            type="button"
            className="Community-post-tag-btn Community-post-tag-btn-bottom"
            onClick={() => setShowTagOverlay(true)}
            aria-label="People"
            title="People"
          >
            <svg className="Community-post-tag-icon" viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
              <path d="M13.1 3.2a2 2 0 0 1 1.42.59l5.69 5.69a2 2 0 0 1 0 2.82L12.4 20.5a2 2 0 0 1-2.82 0L4.61 15.2a2 2 0 0 1-.59-1.73L4.55 8.9a3 3 0 0 1 .43-1.35l2.6-4.1a2 2 0 0 1 1.67-.95l3.2-.27a2 2 0 0 1 .65 0z" fill="#861b68" />
              <circle cx="8.3" cy="8.3" r="2.1" fill="#ffffff" />
              <path d="M13.1 3.2a2 2 0 0 1 1.42.59l5.69 5.69a2 2 0 0 1 0 2.82L12.4 20.5a2 2 0 0 1-2.82 0L4.61 15.2a2 2 0 0 1-.59-1.73l.53-4.57a3 3 0 0 1 .43-1.35l2.6-4.1a2 2 0 0 1 1.67-.95l3.2-.27a2 2 0 0 1 .65 0z" fill="#ffffff" fillOpacity="0.18" />
            </svg>
            <span className="Community-post-tag-count">{taggedPeople.length}</span>
          </button>
          )}
        </div>

        <div className="Community-post-create-form">
          <div className="Community-post-composer-wrap">
            <label className="Community-label Community-post-label-lg" htmlFor="Community-post-title">Title</label>
            <div className="Community-title-color-wrap">
              <input
                id="Community-post-title"
                type="text"
                className="Community-input Community-post-title-input"
                placeholder="Add a title"
                value={title}
                onChange={(e) => {
                  const v = e.target.value;
                  if (isOverWordLimit(v, POST_TITLE_MAX_WORDS, title)) return;
                  setTitle(v);
                }}
                style={fontRenderStyle({ color: titleColor, weight: titleWeight, size: titleSize, italic: titleItalic, underline: titleUnderline })}
              />
              <FontTool
                value={title}
                color={titleColor}
                setColor={setTitleColor}
                weight={titleWeight}
                setWeight={setTitleWeight}
                size={titleSize}
                setSize={setTitleSize}
                italic={titleItalic}
                setItalic={setTitleItalic}
                underline={titleUnderline}
                setUnderline={setTitleUnderline}
                maxSize={25}
                open={showTitleFont}
                setOpen={setShowTitleFont}
              />
            </div>
            <div className="Community-char-counter">{(title.trim() ? title.trim().split(/\s+/).filter(Boolean).length : 0)} / {POST_TITLE_MAX_WORDS} words</div>
            <label className="Community-label Community-post-label-lg" htmlFor="Community-post-desc">Description</label>
            <div className="Community-post-desc-wrap">
              <textarea
                id="Community-post-desc"
                className="Community-textarea Community-post-composer"
                placeholder="What do you want to talk about?"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={POST_MAX_TEXT}
                style={fontRenderStyle({ color: textColor, weight: textWeight, size: textSize, italic: textItalic, underline: textUnderline })}
              />
              <FontTool
                value={text}
                color={textColor}
                setColor={setTextColor}
                weight={textWeight}
                setWeight={setTextWeight}
                size={textSize}
                setSize={setTextSize}
                italic={textItalic}
                setItalic={setTextItalic}
                underline={textUnderline}
                setUnderline={setTextUnderline}
                maxSize={20}
                open={showDescFont}
                setOpen={setShowDescFont}
                showButton={false}
              />
            <div className="Community-post-toolbar">
              <div className="Community-post-toolbar-btns">
                <button type="button" className="Community-comment-tool-btn" onClick={() => setShowEmoji((v) => !v)} aria-label="Add emoji" title="Add emoji">😊</button>
                <button type="button" className="Community-comment-tool-btn Community-font-toolbar-btn" onClick={() => setShowDescFont((v) => !v)} aria-label="Text style" title="Text style">
                  <svg viewBox="0 0 512 512" width="18" height="18" aria-hidden="true">
                    <defs>
                      <linearGradient id="abFontGradientToolbar2" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#861b68" />
                        <stop offset="100%" stopColor="#e0245e" />
                      </linearGradient>
                    </defs>
                    <path fill="url(#abFontGradientToolbar2)" d="M488.1 434.6H462L316.1 30.2C311.2 15.7 297.6 6 282.3 6h-52.6c-15.3 0-28.9 9.7-33.8 24.2L50 434.6H23.9c-9.9 0-17.9 8-17.9 17.9v35.7C6 498 14 506 23.9 506h142.9c9.9 0 17.9-8 17.9-17.9v-35.7c0-9.9-8-17.9-17.9-17.9h-21.9l26-71.4h170.3l26 71.4h-21.9c-9.9 0-17.9 8-17.9 17.9v35.7c0 9.9 8 17.9 17.9 17.9h142.9c9.9 0 17.9-8 17.9-17.9v-35.7c-.1-9.8-8.1-17.8-18-17.8zM203.4 273.9 256 129.3l52.6 144.5H203.4v.1z" />
                  </svg>
                </button>
              </div>
              <span className="Community-post-toolbar-count">{text.length}/{POST_MAX_TEXT}</span>
            </div>
            </div>

          {showEmoji && (
            <div className="Community-emoji-palette">
              {EmojiPalette.map((e) => (
                <button key={e} type="button" className="Community-emoji-btn" onClick={() => insertEmoji(e)}>
                  {e}
                </button>
              ))}
            </div>
          )}

          </div>

          <div className="Community-post-actions-row">
            <button
              type="button"
              className="Community-comment-tool-btn Community-post-action-btn"
              onClick={() => setShowTagOverlay(true)}
              aria-label="People"
              title="People"
            >
              <span className="Community-post-action-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path d="M13.1 3.2a2 2 0 0 1 1.42.59l5.69 5.69a2 2 0 0 1 0 2.82L12.4 20.5a2 2 0 0 1-2.82 0L4.61 15.2a2 2 0 0 1-.59-1.73l.53-4.57a3 3 0 0 1 .43-1.35l2.6-4.1a2 2 0 0 1 1.67-.95l3.2-.27a2 2 0 0 1 .65 0z" fill="#861b68" stroke="#5a1145" strokeWidth="0.3" />
                  <circle cx="8.3" cy="8.3" r="2.1" fill="#ffffff" />
                </svg>
              </span>
              People
              {taggedPeople.length > 0 && (
                <span className="Community-post-action-arrow" aria-hidden="true">&#8250;</span>
              )}
            </button>

            <button
              type="button"
              className="Community-comment-tool-btn Community-post-action-btn"
              onClick={() => setShowLocationOverlay(true)}
              aria-label="Location"
              title="Location"
            >
              <span className="Community-post-action-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </span>
              Location
            </button>

            <button
              type="button"
              className="Community-comment-tool-btn Community-post-action-btn"
              onClick={() => setShowSocialOverlay(true)}
              aria-label="Add accounts"
              title="Add accounts"
            >
              <span className="Community-post-action-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
              </span>
              {socials.length > 0 ? `Accounts (${socials.length})` : "Accounts"}
            </button>

            {!isEditing && (
              <>
                <button
                  type="button"
                  className={`Community-comment-tool-btn Community-post-action-btn${youtube.length > 0 ? " Community-post-action-btn--active" : ""}`}
                  onClick={() => { setYoutubeOpen((v) => !v); setYoutubeError(""); }}
                  aria-label="Add YouTube video"
                  aria-expanded={youtubeOpen}
                  title="Add YouTube video"
                >
                  <span className="Community-post-action-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FF0000" />
                    </svg>
                  </span>
                  <span className="Community-post-action-text">YouTube</span>
                  {youtube.length > 0 && <span className="Community-post-action-arrow" aria-hidden="true">&#8250;</span>}
                </button>
              </>
            )}
          </div>

          {youtubeOpen && !isEditing && (
            <div className="Community-post-youtube-row">
              <div className="Community-post-youtube-row-inner">
                <input
                  type="url"
                  className="Community-post-youtube-input"
                  placeholder="Paste YouTube link"
                  value={youtubeLink}
                  onChange={(e) => {
                    const v = e.target.value;
                    setYoutubeLink(v);
                    if (parseYoutubeLink(v)) addYoutube(v);
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    if (parseYoutubeLink(pasted)) {
                      e.preventDefault();
                      addYoutube(pasted);
                    } else {
                      setYoutubeError("Enter a valid YouTube link.");
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addYoutube(youtubeLink); } }}
                />
              </div>
              {youtubeError && <p className="Community-post-youtube-error">{youtubeError}</p>}
            </div>
          )}

          {location && (
            <div className="Community-post-preview-location">
              <svg className="Community-post-preview-location-pin" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <defs>
                  <radialGradient id="locPin3d" cx="50%" cy="32%" r="75%">
                    <stop offset="0%" stopColor="#ff7ab8" />
                    <stop offset="60%" stopColor="#d92776" />
                    <stop offset="100%" stopColor="#8e1149" />
                  </radialGradient>
                </defs>
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="url(#locPin3d)" stroke="#6b0d3b" strokeWidth="0.8" />
                <ellipse cx="12" cy="9" rx="3" ry="3" fill="#ffffff" />
              </svg>
              <span className="Community-post-preview-location-text">{location}</span>
              <button
                type="button"
                className="Community-post-preview-location-clear"
                onClick={() => setLocation("")}
                aria-label="Remove location"
                title="Remove location"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}

          {mediaTotal > 1 && (
            <p className="Community-post-preview-drag-hint">Drag items to change the posting order.</p>
          )}

          {mediaTotal > 0 && (
            <div className="Community-post-preview-thumbs" ref={thumbStripRef}>
              {mediaItems.map((m, i) => (
                <div
                  key={i}
                  draggable
                  onDragStart={() => { setDragFrom(i); }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(i);
                    dragScrollState.current.clientX = e.clientX;
                    if (!dragScrollState.current.raf) startDragScroll();
                  }}
                  onDrop={(e) => { e.preventDefault(); stopDragScroll(); if (dragFrom !== null) reorderMedia(dragFrom, i); setDragFrom(null); setDragOver(null); }}
                  onDragEnd={() => { stopDragScroll(); setDragFrom(null); setDragOver(null); }}
                  className={`Community-post-preview-thumb${i === Math.min(previewIndex, mediaTotal - 1) ? " Community-post-preview-thumb-active" : ""}${dragFrom === i ? " Community-post-preview-thumb-dragging" : ""}${dragOver === i && dragFrom !== null && dragFrom !== i ? " Community-post-preview-thumb-drop" : ""}`}
                  onClick={() => setPreviewIndex(i)}
                >
                  {m.type === "video"
                    ? <video src={m.src} muted className="Community-post-preview-thumb-media" />
                    : m.type === "youtube"
                      ? <img src={m.thumb} alt={`YouTube ${i + 1}`} className="Community-post-preview-thumb-media" />
                      : <img src={m.src} alt={`Preview ${i + 1}`} className="Community-post-preview-thumb-media" />}
                  <span className="Community-post-preview-thumb-order">{i + 1}</span>
                  <button
                    type="button"
                    className="Community-media-remove"
                    onClick={(e) => { e.stopPropagation(); removeMediaAt(i); }}
                    aria-label="Remove media"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          {gif && (
            <div className="Community-post-gif-wrap">
              <img src={gif.preview || gif.media} alt="Selected GIF" className="Community-post-gif" />
              <button type="button" className="Community-media-remove" onClick={() => setGif(null)} aria-label="Remove GIF">×</button>
            </div>
          )}

          <div className="Community-post-settings">
            <div className="Community-post-settings-row">
              <PostVisibilityDropdown value={visibility} onChange={setVisibility} />
              <CommentSettingsDropdown value={commentPermission} onChange={setCommentPermission} />
            </div>
            <HideEngagementDropdown
              value={{ hideLikes, hideLikeCount, hideComments, hideCommentCount }}
              editMode={isEditing}
              onChange={(key, checked) => {
                if (key === "hideLikes") setHideLikes(checked);
                else if (key === "hideLikeCount") setHideLikeCount(checked);
                else if (key === "hideComments") setHideComments(checked);
                else if (key === "hideCommentCount") setHideCommentCount(checked);
              }}
            />
          </div>

          <div className="Community-overlay-actions Community-post-actions">
            <PrimaryButton onClick={handlePost} disabled={!text.trim() && photos.length === 0 && videos.length === 0 && youtube.length === 0}>
              {isEditing ? "Save Changes" : "Post"}
            </PrimaryButton>
          </div>
        </div>
        </div>
      </div>

      {showLimitOverlay && (
        <div className="Community-overlay-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowLimitOverlay(false); }} role="presentation">
          <div className="Community-overlay-panel Community-limit-overlay" role="dialog" aria-modal="true" aria-labelledby="Community-limit-title">
            <div className="Community-overlay-header">
              <h2 id="Community-limit-title" className="Community-heading-section">Limit Exceeded</h2>
              <button type="button" className="Community-overlay-close-btn" onClick={() => setShowLimitOverlay(false)} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="Community-overlay-body">
              <p className="Community-body-text">You can add a maximum of {MEDIA_LIMIT} photos or videos in total.</p>
            </div>
            <div className="Community-overlay-actions Community-overlay-actions-center">
              <PrimaryButton onClick={() => setShowLimitOverlay(false)}>OK</PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {showCreateEvent && (
        <CreateEventModal onClose={() => setShowCreateEvent(false)} onCreate={handleEventCreated} />
      )}

      {showTagOverlay && (
        <TagPeopleOverlay
          selected={taggedPeople}
          onSave={(ids) => {
            setTaggedPeople(ids);
            setShowTagOverlay(false);
          }}
          onClose={() => setShowTagOverlay(false)}
        />
      )}

      {showLocationOverlay && (
        <LocationOverlay
          value={location}
          onSave={(loc) => {
            setLocation(loc);
            setShowLocationOverlay(false);
          }}
          onClose={() => setShowLocationOverlay(false)}
        />
      )}

      {showSocialOverlay && (
        <SocialLinksOverlay
          value={socials}
          onSave={(links) => {
            setSocials(links);
            setShowSocialOverlay(false);
          }}
          onClose={() => setShowSocialOverlay(false)}
        />
      )}

      {showEditor && currentMedia && currentMedia.type === "image" && (
        <ImageEditor
          src={currentMedia.src}
          initialCornerRadius={imageCorners[currentMedia.src] || 0}
          onClose={() => setShowEditor(false)}
          onApply={(dataUrl, corners) => {
            setImageCorners((prev) => ({ ...prev, [dataUrl]: corners }));
            const pIdx = photos.indexOf(currentMedia.src);
            if (pIdx !== -1) {
              setPhotos((prev) => {
                const next = prev.slice();
                next[pIdx] = dataUrl;
                return next;
              });
            }
            setMediaOrder((prev) =>
              prev.map((mm) => (mm.type === "image" && mm.src === currentMedia.src ? { ...mm, src: dataUrl } : mm))
            );
            setShowEditor(false);
          }}
        />
      )}

      {showVideoEditor && currentMedia && currentMedia.type === "video" && (
        <VideoEditor
          src={currentMedia.src}
          initialEdits={currentMedia.edits}
          onClose={() => setShowVideoEditor(false)}
          onApply={(edits) => {
            const vidIdx = videos.indexOf(currentMedia.src);
            if (vidIdx !== -1) {
              setVideoEdits((prev) => {
                const next = prev.slice();
                next[vidIdx] = edits;
                return next;
              });
            }
            setShowVideoEditor(false);
          }}
        />
      )}

      {showThumbnailOverlay && (
        <VideoThumbnailOverlay
          videos={videos}
          initialThumbnails={videoThumbnails}
          onCancel={() => {
            const wasPending = thumbnailPendingPostRef.current;
            thumbnailPendingPostRef.current = false;
            setShowThumbnailOverlay(false);
            if (isEditing) {
              setShowEditThumbPreview(true);
            } else if (wasPending) {
              setShowEditThumbPreview(false);
            }
          }}
          onDone={(thumbs) => {
            const first = thumbs.find((t) => t) || null;
            setVideoThumbnails(thumbs);
            setVideoThumbnail(first);
            const shouldPost = thumbnailPendingPostRef.current;
            thumbnailPendingPostRef.current = false;
            setShowThumbnailOverlay(false);
            if (isEditing) {
              setShowEditThumbPreview(true);
              return;
            }
            if (shouldPost) onCreate(buildPayload(thumbs));
          }}
        />
      )}

      {showEditThumbPreview && isEditing && (
        <div className="Community-overlay-backdrop" role="presentation">
          <div className="Community-overlay-panel Community-thumb-confirm" role="dialog" aria-modal="true" aria-labelledby="Community-thumb-edit-preview-title">
            <div className="Community-overlay-header Community-thumb-confirm-header">
              <h2 id="Community-thumb-edit-preview-title" className="Community-heading-section">Thumbnail Preview</h2>
              <button type="button" className="Community-overlay-close-btn" onClick={() => { setShowEditThumbPreview(false); setShowThumbnailOverlay(true); }} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="Community-overlay-body">
              {(videoThumbnails.find((t) => t) || videoThumbnail) ? (
                <img src={videoThumbnails.find((t) => t) || videoThumbnail} alt="Thumbnail preview" className="Community-thumb-confirm-img" />
              ) : (
                <p className="Community-thumb-saved-hint">No thumbnail selected.</p>
              )}
            </div>
            <div className="Community-overlay-actions Community-overlay-actions-center">
              <PrimaryButton onClick={() => { setShowEditThumbPreview(false); onCreate(buildPayload(videoThumbnails)); }}>Accept</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </Overlay>
  );
};

const VideoWithPoster = ({ src, edits, className = "", poster, controls = false, autoPlay = false }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (autoPlay && videoRef.current) {
      const p = videoRef.current.play();
      if (p && p.catch) p.catch(() => {});
    }
  }, [autoPlay]);

  const showPosterOverlay = !isPlaying;
  const hasPoster = !!poster;

  if (edits) {
    return (
      <div style={{ position: "relative", width: "100%" }}>
        <EditedVideo
          src={src}
          edits={edits}
          className={className}
          poster={poster}
          controls={controls}
          autoPlay={autoPlay}
          videoRef={videoRef}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          onPlaying={() => setIsPlaying(true)}
        />
        {hasPoster && showPosterOverlay && (
          <div
            className="Community-thumb-poster-overlay"
            onClick={(e) => { e.stopPropagation(); const v = videoRef.current; if (v) { v.play(); } }}
            style={{ position: "absolute", inset: 0, zIndex: 2, cursor: controls ? "pointer" : "default" }}
          >
            <img src={poster} alt="Video thumbnail" className={`Community-post-photo ${className}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            <span className="Community-post-play-triangle" aria-hidden="true" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <video
        ref={videoRef}
        className={className}
        src={src}
        poster={poster || undefined}
        muted
        autoPlay={autoPlay}
        playsInline
        preload={poster ? "metadata" : undefined}
        controls={controls}
        onPlay={() => { setIsPlaying(true); }}
        onPlaying={() => { setIsPlaying(true); }}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      />
      {hasPoster && showPosterOverlay && (
        <div
          className="Community-thumb-poster-overlay"
          onClick={(e) => { e.stopPropagation(); const v = videoRef.current; if (v) { v.play(); } }}
          style={{ position: "absolute", inset: 0, zIndex: 2, cursor: controls ? "pointer" : "default" }}
        >
          <img src={poster} alt="Video thumbnail" className={`Community-post-photo ${className}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          <span className="Community-post-play-triangle" aria-hidden="true" />
        </div>
      )}
    </div>
  );
};

const EditedVideo = ({ src, edits, className = "", poster, controls = false, autoPlay = false, videoRef, onPlay, onPause, onEnded, onPlaying }) => {
  const clipRef = useRef(null);
  const [meta, setMeta] = useState(null);
  const [box, setBox] = useState(null);

  useEffect(() => {
    const el = clipRef.current;
    if (!el) return undefined;
    const measure = () => {
      const b = el.getBoundingClientRect();
      if (b.width > 0 && b.height > 0) setBox({ w: b.width, h: b.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const c = edits.crop && edits.crop.w !== undefined ? edits.crop : { x: 0, y: 0, w: 1, h: 1 };
  const rot90 = ((edits.rotate % 180) + 180) % 180 === 90;
  const baseAspect = meta && meta.vw ? (meta.vw * c.w) / (meta.vh * c.h) : 1;
  const outputAspect = rot90 ? 1 / baseAspect : baseAspect;

  const filter = [
    edits.brightness ? `brightness(${1 + edits.brightness / 100})` : "",
    edits.contrast ? `contrast(${1 + edits.contrast / 100})` : "",
    edits.saturation ? `saturate(${1 + edits.saturation / 100})` : "",
    edits.filter && edits.filter !== "Original" ? EDIT_FILTERS[edits.filter] : "",
  ].filter(Boolean).join(" ");

  let cropTransform = "";
  if (meta && box && meta.vw && meta.vh) {
    const effW = rot90 ? box.h : box.w;
    const effH = rot90 ? box.w : box.h;
    if (c.w < 0.999 || c.h < 0.999 || c.x > 0.001 || c.y > 0.001) {
      const k = Math.max(effW / meta.vw, effH / meta.vh);
      const imgW = k * meta.vw;
      const imgH = k * meta.vh;
      const cx = (effW - imgW) / 2 + imgW * (c.x + c.w / 2);
      const cy = (effH - imgH) / 2 + imgH * (c.y + c.h / 2);
      const f = effW / (imgW * c.w);
      cropTransform = `translate(${(effW / 2 - f * cx).toFixed(2)}px, ${(effH / 2 - f * cy).toFixed(2)}px) scale(${f.toFixed(4)})`;
    }
  }

  const rotScale = rot90 ? Math.max(1, (box ? Math.max(box.w / box.h, box.h / box.w) : 1)) : 1;
  const wrapTransform = [
    edits.flipH ? "scaleX(-1)" : "",
    edits.flipV ? "scaleY(-1)" : "",
    edits.rotate ? `rotate(${edits.rotate}deg)` : "",
    rotScale !== 1 ? `scale(${rotScale.toFixed(4)})` : "",
    edits.zoom && edits.zoom !== 1 ? `scale(${edits.zoom})` : "",
  ].filter(Boolean).join(" ") || undefined;

  const vignette = edits.vignette || 0;
  const vignetteBg = vignette
    ? {
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        background: `radial-gradient(ellipse at center, rgba(0,0,0,0) 42%, rgba(${vignette > 0 ? "0,0,0" : "255,255,255"},${Math.min(Math.abs(vignette) / 120, 0.85)}) 110%)`,
      }
    : null;

  return (
    <div
      ref={clipRef}
      className={`Community-edited-video ${className}`}
      style={{ width: "100%", aspectRatio: `${outputAspect}`, position: "relative", overflow: "hidden", display: "block" }}
    >
      <div style={{ position: "absolute", inset: 0, transform: wrapTransform, transformOrigin: "50% 50%" }}>
        <video
          ref={videoRef}
          src={src}
          controls={controls}
          poster={poster}
          muted
          autoPlay={autoPlay}
          playsInline
          preload={poster ? "metadata" : undefined}
          onLoadedMetadata={(e) => setMeta({ vw: e.currentTarget.videoWidth, vh: e.currentTarget.videoHeight })}
          onPlay={onPlay}
          onPlaying={onPlaying}
          onPause={onPause}
          onEnded={onEnded}
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: "cover",
            transform: cropTransform || undefined,
            transformOrigin: "0 0",
            filter: filter || undefined,
          }}
        />
        {vignetteBg && <div style={vignetteBg} />}
      </div>
    </div>
  );
};

const PostPreviewMedia = ({ mediaItems, onSelectMedia, currentIndex }) => {
  const count = mediaItems.length;

  if (count === 0) return null;

  const current = mediaItems[Math.min(currentIndex, Math.max(0, count - 1))];
  const prev = () => onSelectMedia((currentIndex - 1 + count) % count);
  const next = () => onSelectMedia((currentIndex + 1) % count);

  return (
    <div className="Community-post-preview-media">
      <div className="Community-post-media-carousel">
        <div className="Community-post-media-img">
          {current.type === "video"
            ? current.edits
              ? <EditedVideo src={current.src} edits={current.edits} controls />
              : <video src={current.src} controls muted />
            : current.type === "youtube"
              ? <iframe src={current.src} className="Community-post-youtube-embed" title="YouTube preview" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
              : <img src={current.src} alt="Preview" />}
        </div>
        {count > 1 && (
          <>
            <button type="button" className="Community-post-nav Community-post-nav-left" onClick={prev} aria-label="Previous media">‹</button>
            <button type="button" className="Community-post-nav Community-post-nav-right" onClick={next} aria-label="Next media">›</button>
          </>
        )}
      </div>
    </div>
  );
};

const PostMediaGallery = ({ items, onOpenMedia }) => {
  const count = items.length;
  const [twoIndex, setTwoIndex] = useState(0);

  if (count === 0) return null;

  const open = (index) => onOpenMedia(index);

  const twoPrev = (e) => { e.stopPropagation(); setTwoIndex((i) => (i - 1 + count) % count); };
  const twoNext = (e) => { e.stopPropagation(); setTwoIndex((i) => (i + 1) % count); };

  const renderCell = (item, index, className, interactive) => {
    if (item.type === "youtube") {
      return (
        <span className="Community-post-grid-cell-media">
          <iframe src={item.src} className={`Community-post-youtube-embed ${className}`} title={`YouTube video ${index + 1}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
        </span>
      );
    }
    if (item.type === "video") {
      return (
        <span className="Community-post-grid-cell-media">
          <VideoWithPoster
            src={item.src}
            edits={item.edits}
            className={className}
            poster={item.poster}
            controls={!!interactive}
          />
        </span>
      );
    }
    return <img src={item.src} alt={`Post media ${index + 1}`} className={className} />;
  };

  if (count === 1) {
    if (items[0].type === "youtube") {
      return (
        <div className="Community-post-video-wrap">
          <iframe src={items[0].src} className="Community-post-youtube-embed" title="YouTube video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
        </div>
      );
    }
    if (items[0].type === "video") {
      return (
        <div className="Community-post-video-wrap">
          <button type="button" className="Community-post-grid-cell" onClick={() => open(0)}>
            <span className="Community-post-grid-cell-media">
              <VideoWithPoster src={items[0].src} edits={items[0].edits} poster={items[0].poster} />
            </span>
          </button>
        </div>
      );
    }
    return (
      <div className="Community-post-photos Community-post-photos-single">
        <button type="button" className="Community-post-grid-cell" onClick={() => open(0)}>
          {renderCell(items[0], 0, "Community-post-photo", true)}
        </button>
      </div>
    );
  }

  if (count === 2) {
    return (
      <div className="Community-post-photos Community-post-photos-two">
        <div className="Community-post-media-carousel">
          <button type="button" className="Community-post-grid-cell" onClick={() => open(twoIndex)}>
            {renderCell(items[twoIndex], twoIndex, "Community-post-photo", true)}
          </button>
          <button type="button" className="Community-post-nav Community-post-nav-left" onClick={twoPrev} aria-label="Previous media">‹</button>
          <button type="button" className="Community-post-nav Community-post-nav-right" onClick={twoNext} aria-label="Next media">›</button>
        </div>
      </div>
    );
  }

  const total = Math.min(count, 4);
  const remaining = count - 4;
  const showOverlay = count > 4;
  const visible = [0, 1, 2, 3].slice(0, total);
  const thumbCount = visible.length - 1;

  return (
    <div className={`Community-post-photos Community-post-photos-stacked Community-post-thumbs-${thumbCount}`}>
      <div className="Community-post-grid-row Community-post-grid-row-primary">
        <button type="button" className="Community-post-grid-cell" onClick={() => open(0)}>
          {renderCell(items[0], 0, "Community-post-photo", true)}
        </button>
      </div>
      <div className="Community-post-grid-row Community-post-grid-row-thumbs">
        {visible.slice(1).map((i) => {
          const overlayIndex = i === visible[visible.length - 1] && showOverlay;
          return (
            <button
              type="button"
              key={i}
              className="Community-post-grid-cell"
              onClick={() => (showOverlay && i === visible[visible.length - 1] ? open(0) : open(i))}
            >
              {renderCell(items[i], i, "Community-post-photo", false)}
              {overlayIndex && (
                <span className="Community-post-grid-more">+{remaining}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ExpandedPostViewer = ({
  post,
  items,
  initialIndex,
  onClose,
  controlProps,
  isOwner,
  onOpenViews,
  onOpenShare,
  hideLikeCount,
  hideCommentCount,
  onOpenTag,
}) => {
  const [index, setIndex] = useState(initialIndex);
  const total = items.length;
  const event = post.event;
  const inPerson = event && event.eventType === EVENT_TYPE.IN_PERSON;
  const [commentsOpen, setCommentsOpen] = useState(() =>
    typeof window === "undefined" ? true : window.innerWidth >= 900
  );
  const commentsRef = useRef(null);
  const taggedPeople = ((event && event.taggedPeople && event.taggedPeople.length) ? event.taggedPeople : post.taggedPeople) || [];

  useEffect(() => {
    if (commentsOpen && commentsRef.current) {
      commentsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [commentsOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + total) % total);
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % total);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, total]);

  const prev = (e) => { e.stopPropagation(); setIndex((i) => (i - 1 + total) % total); };
  const next = (e) => { e.stopPropagation(); setIndex((i) => (i + 1) % total); };

  return (
    <div className="Community-expanded-viewer" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="Community-expanded-viewer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="Community-expanded-media">
          {items[index] && items[index].type === "video" ? (
            <VideoWithPoster
              key={`video-${index}`}
              className="Community-expanded-media-img"
              src={items[index].src}
              poster={items[index].poster}
              controls
              autoPlay
            />
          ) : items[index] && items[index].type === "youtube" ? (
            <iframe
              key={`youtube-${index}`}
              className="Community-expanded-media-img Community-expanded-youtube"
              src={items[index].src}
              title={`YouTube video ${index + 1}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          ) : (
            <img
              className="Community-expanded-media-img"
              src={items[index].src}
              alt={`Post media ${index + 1}`}
              loading={index > 0 ? "eager" : undefined}
            />
          )}
          <button type="button" className="Community-expanded-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          {total > 1 && (
            <>
              <button type="button" className="Community-expanded-nav Community-expanded-nav-left" onClick={prev} aria-label="Previous media">‹</button>
              <button type="button" className="Community-expanded-nav Community-expanded-nav-right" onClick={next} aria-label="Next media">›</button>
            </>
          )}
          {taggedPeople.length > 0 && (
            <button
              type="button"
              className="Community-post-tag-btn Community-post-tag-btn-bottom"
              onClick={(e) => {
                e.stopPropagation();
                if (onOpenTag) onOpenTag();
              }}
              aria-label="People"
              title="People"
            >
              <svg className="Community-post-tag-icon" viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
                <path d="M13.1 3.2a2 2 0 0 1 1.42.59l5.69 5.69a2 2 0 0 1 0 2.82L12.4 20.5a2 2 0 0 1-2.82 0L4.61 15.2a2 2 0 0 1-.59-1.73L4.55 8.9a3 3 0 0 1 .43-1.35l2.6-4.1a2 2 0 0 1 1.67-.95l3.2-.27a2 2 0 0 1 .65 0z" fill="#861b68" />
                <circle cx="8.3" cy="8.3" r="2.1" fill="#ffffff" />
                <path d="M13.1 3.2a2 2 0 0 1 1.42.59l5.69 5.69a2 2 0 0 1 0 2.82L12.4 20.5a2 2 0 0 1-2.82 0L4.61 15.2a2 2 0 0 1-.59-1.73l.53-4.57a3 3 0 0 1 .43-1.35l2.6-4.1a2 2 0 0 1 1.67-.95l3.2-.27a2 2 0 0 1 .65 0z" fill="#ffffff" fillOpacity="0.18" />
              </svg>
              <span className="Community-post-tag-count">{taggedPeople.length}</span>
            </button>
          )}
        </div>
        <div className="Community-expanded-details">
          <div className="Community-expanded-details-head">
            <div className="Community-card-header">
              <div className="Community-avatar" aria-hidden="true">
                {(getMemberById(post.posterId) || {}).avatar || post.posterName.slice(0, 2).toUpperCase()}
              </div>
              <div className="Community-card-header-info">
                <div className="Community-card-header-name-row">
                  <p className="Community-poster-name">{post.posterName}</p>
                  <p className="Community-posted-time">{formatTimeAgo(post.createdAt)}</p>
                </div>
                <p className="Community-company-name">{post.company}</p>
              </div>
              <PostCardControls {...controlProps} />
              <button type="button" className="Community-expanded-close Community-expanded-close-head" onClick={onClose} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
          <div className="Community-expanded-details-scroll">
            {post.event && (
              <>
                {post.event.title && (
                  <h3 className="Community-heading-sub Community-event-card-title" style={fontRenderStyle({ color: post.event.titleColor, weight: post.event.titleWeight, size: post.event.titleSize, italic: post.event.titleItalic, underline: post.event.titleUnderline, fontFamily: post.event.titleFontFamily })}>{post.event.title}</h3>
                )}
                {post.event.name && (
                  <h3 className="Community-heading-sub Community-event-name" style={fontRenderStyle({ color: post.event.nameColor, weight: post.event.nameWeight, size: post.event.nameSize, italic: post.event.nameItalic, underline: post.event.nameUnderline, fontFamily: post.event.nameFontFamily })}>{post.event.name}</h3>
                )}
                {post.event.description && (
                  <p className="Community-body-text Community-event-description" style={fontRenderStyle({ color: post.event.descColor, weight: post.event.descWeight, size: post.event.descSize, italic: post.event.descItalic, underline: post.event.descUnderline })}>{post.event.description}</p>
                )}
                {post.title && <h3 className="Community-heading-section Community-card-title Community-post-title" style={fontRenderStyle({ color: post.titleColor, weight: post.titleWeight, size: post.titleSize, italic: post.titleItalic, underline: post.titleUnderline })}>{post.title}</h3>}
                {post.text && <p className="Community-body-text Community-post-text" style={fontRenderStyle({ color: post.textColor, weight: post.textWeight, size: post.textSize, italic: post.textItalic, underline: post.textUnderline })}>{post.text}</p>}
                <div className="Community-event-meta">
                  {inPerson ? (
                    <>
                      {event.venue && <p className="Community-event-meta-row"><span className="Community-event-meta-emoji" aria-hidden="true">🏛️</span> {event.venue}</p>}
                      {event.address && (
                        <p className="Community-event-meta-row">
                          <svg viewBox="0 0 24 24" width="18" height="18" className="Community-event-meta-icon" aria-hidden="true">
                            <defs>
                              <radialGradient id="locPinExpandedAddress" cx="50%" cy="32%" r="75%">
                                <stop offset="0%" stopColor="#ff7ab8" />
                                <stop offset="60%" stopColor="#d92776" />
                                <stop offset="100%" stopColor="#8e1149" />
                              </radialGradient>
                            </defs>
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="url(#locPinExpandedAddress)" stroke="#6b0d3b" stroke-width="0.8" />
                            <ellipse cx="12" cy="9" rx="3" ry="3" fill="#ffffff" />
                          </svg>
                          {event.address}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="Community-event-meta-row"><span className="Community-event-meta-emoji" aria-hidden="true">🎥</span> Online event</p>
                      {event.externalLink && (
                        <p className="Community-event-meta-row">
                          <span className="Community-event-meta-emoji" aria-hidden="true">🔗</span> <a href={event.externalLink} target="_blank" rel="noreferrer" className="Community-event-link">{event.externalLink}</a>
                        </p>
                      )}
                    </>
                  )}
                  <p className="Community-event-meta-row"><span className="Community-event-meta-emoji" aria-hidden="true">📅</span> {formatDateLabel(event.startDate, event.startTime)}</p>
                  {event.hasEndDate && event.endDate && (
                    <p className="Community-event-meta-row"><span className="Community-event-meta-emoji" aria-hidden="true">⏰</span> Ends {formatDateLabel(event.endDate, event.endTime)}</p>
                  )}
                  {event.timezone && <p className="Community-event-meta-row"><span className="Community-event-meta-emoji" aria-hidden="true">🕒</span> {event.timezone}</p>}
                  {inPerson && event.externalLink && (
                    <p className="Community-event-meta-row">
                      <span className="Community-event-meta-emoji" aria-hidden="true">🔗</span> <a href={event.externalLink} target="_blank" rel="noreferrer" className="Community-event-link">{event.externalLink}</a>
                    </p>
                  )}
                </div>
              </>
            )}
            {!post.event && (
              <>
                {post.title && <h3 className="Community-heading-section Community-card-title Community-post-title" style={fontRenderStyle({ color: post.titleColor, weight: post.titleWeight, size: post.titleSize, italic: post.titleItalic, underline: post.titleUnderline })}>{post.title}</h3>}
                {post.location && <p className="Community-post-location-line">📍 {post.location}</p>}
                {post.text && <p className="Community-body-text Community-post-text" style={fontRenderStyle({ color: post.textColor, weight: post.textWeight, size: post.textSize, italic: post.textItalic, underline: post.textUnderline })}>{post.text}</p>}
              </>
            )}
<PostSocials socials={(event && event.socials) || post.socials} />
            <PostEngagementBar post={post} isOwner={isOwner} onOpenComments={() => { if (window.innerWidth < 900) setCommentsOpen((v) => !v); }} onOpenViews={onOpenViews} onOpenShare={onOpenShare} hideLikeCount={hideLikeCount} hideCommentCount={hideCommentCount} />
            {commentsOpen && (
              <div ref={commentsRef}>
                <CommentsInline entity={post} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PostCard = ({ post, onOpenComments, followingPosters, savedPosts, archivedPosts, onToggleFollow, onToggleSave, onToggleArchive, onCopyLink, onHide, onReport, onEdit, hiddenLikeCounts, onToggleHideLikes, hiddenCommentCounts, onToggleHideCommentCounts, onToggleLikesOff, onToggleCommentsOff, onDelete, onOpenViews, onOpenShare }) => {
  const poster = getMemberById(post.posterId);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showTagOverlay, setShowTagOverlay] = useState(false);
  const event = post.event;
  const inPerson = event && event.eventType === EVENT_TYPE.IN_PERSON;
  const taggedMembers = (((event && event.taggedPeople && event.taggedPeople.length) ? event.taggedPeople : post.taggedPeople) || [])
    .map((id) => getMemberById(id))
    .filter(Boolean);
  const ytVideos = normalizeYoutube(post.youtube);
  const controlProps = {
    post,
    isOwner: post.posterId === CURRENT_USER_ID,
    isFollowing: followingPosters.has(post.posterId),
    isSaved: savedPosts.has(post.id),
    isArchived: archivedPosts.has(post.id),
    onToggleFollow: () => onToggleFollow(post),
    onToggleSave: () => onToggleSave(post),
    onToggleArchive: () => onToggleArchive(post),
    onCopyLink: () => onCopyLink(post),
    onHide: () => onHide(post),
    onReport: () => onReport(post),
    onEdit: () => onEdit(post),
    isLikeCountHidden: hiddenLikeCounts.has(post.id),
    onToggleHideLikes: () => onToggleHideLikes(post.id),
    isCommentCountHidden: hiddenCommentCounts.has(post.id),
    onToggleHideCommentCounts: () => onToggleHideCommentCounts(post.id),
    isLikesOff: Boolean(post.hideLikes),
    onToggleLikesOff: () => onToggleLikesOff(post),
    isCommentsOff: Boolean(post.hideComments),
    onToggleCommentsOff: () => onToggleCommentsOff(post),
    onDelete: () => onDelete(post),
  };

  const postCardRef = useRef(null);
  useEffect(() => {
    if (!commentsOpen) return;
    const onPointerDown = (e) => {
      if (postCardRef.current && !postCardRef.current.contains(e.target)) setCommentsOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [commentsOpen]);

  return (
    <article className={`Community-card${event ? " Community-event-post-card" : ""}`} ref={postCardRef}>
      <header className="Community-card-header">
        <div className="Community-avatar" aria-hidden="true">
          {poster ? poster.avatar : post.posterName.slice(0, 2).toUpperCase()}
        </div>
        <div className="Community-card-header-info">
          <div className="Community-card-header-name-row">
            <p className="Community-poster-name">{post.posterName}</p>
            <p className="Community-posted-time">{formatTimeAgo(post.createdAt)}</p>
          </div>
          <p className="Community-company-name">{post.company}</p>
        </div>
        {taggedMembers.length > 0 && (
          <button
            type="button"
            className="Community-post-tags-chip-btn"
            onClick={() => setShowTagOverlay(true)}
            aria-label="View tagged people"
            title="View tagged people"
            aria-expanded={showTagOverlay}
          >
            <svg className="Community-post-tag-icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path d="M13.1 3.2a2 2 0 0 1 1.42.59l5.69 5.69a2 2 0 0 1 0 2.82L12.4 20.5a2 2 0 0 1-2.82 0L4.61 15.2a2 2 0 0 1-.59-1.73L4.55 8.9a3 3 0 0 1 .43-1.35l2.6-4.1a2 2 0 0 1 1.67-.95l3.2-.27a2 2 0 0 1 .65 0z" fill="#861b68" />
              <circle cx="8.3" cy="8.3" r="2.1" fill="#ffffff" />
              <path d="M13.1 3.2a2 2 0 0 1 1.42.59l5.69 5.69a2 2 0 0 1 0 2.82L12.4 20.5a2 2 0 0 1-2.82 0L4.61 15.2a2 2 0 0 1-.59-1.73l.53-4.57a3 3 0 0 1 .43-1.35l2.6-4.1a2 2 0 0 1 1.67-.95l3.2-.27a2 2 0 0 1 .65 0z" fill="#ffffff" fillOpacity="0.18" />
            </svg>
            <span className="Community-post-tags-chip-count">{taggedMembers.length}</span>
          </button>
        )}
        <PostCardControls {...controlProps} />
      </header>

      {event ? (
        <>
          {event.gif && (
            <div className="Community-post-gif-wrap">
              <img src={event.gif.preview || event.gif.media} alt="Event GIF" className="Community-post-gif" />
            </div>
          )}
          {(buildCoverMedia(event).length > 0) ? (
            <PostMediaGallery
              items={buildCoverMedia(event).map((m) => (m.type === "video" ? { ...m, poster: event.coverThumbnail || undefined } : m))}
              onOpenMedia={(index) => setLightboxIndex(index)}
            />
          ) : null}
          <div className="Community-event-card-body">
            {event.title && (
              <h3 className="Community-heading-sub Community-event-card-title" style={fontRenderStyle({ color: event.titleColor, weight: event.titleWeight, size: event.titleSize, italic: event.titleItalic, underline: event.titleUnderline, fontFamily: event.titleFontFamily })}>{event.title}</h3>
            )}
            <h3 className="Community-heading-sub Community-event-name" style={fontRenderStyle({ color: event.nameColor, weight: event.nameWeight, size: event.nameSize, italic: event.nameItalic, underline: event.nameUnderline, fontFamily: event.nameFontFamily })}>{event.name}</h3>
            {event.description && (
              <p className="Community-body-text Community-event-description" style={fontRenderStyle({ color: event.descColor, weight: event.descWeight, size: event.descSize, italic: event.descItalic, underline: event.descUnderline })}>{event.description}</p>
            )}
            {post.title && <h3 className="Community-heading-section Community-card-title Community-post-title" style={fontRenderStyle({ color: post.titleColor, weight: post.titleWeight, size: post.titleSize, italic: post.titleItalic, underline: post.titleUnderline })}>{post.title}</h3>}
            {post.text && <p className="Community-body-text Community-post-text" style={fontRenderStyle({ color: post.textColor, weight: post.textWeight, size: post.textSize, italic: post.textItalic, underline: post.textUnderline })}>{post.text}</p>}
            <div className="Community-event-meta">
              {inPerson ? (
                <>
                  {event.venue && <p className="Community-event-meta-row"><span className="Community-event-meta-emoji" aria-hidden="true">🏛️</span> {event.venue}</p>}
                  {event.address && (
                    <p className="Community-event-meta-row">
                      <svg viewBox="0 0 24 24" width="18" height="18" className="Community-event-meta-icon" aria-hidden="true">
                        <defs>
                          <radialGradient id="locPinInputAddress" cx="50%" cy="32%" r="75%">
                            <stop offset="0%" stopColor="#ff7ab8" />
                            <stop offset="60%" stopColor="#d92776" />
                            <stop offset="100%" stopColor="#8e1149" />
                          </radialGradient>
                        </defs>
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="url(#locPinInputAddress)" stroke="#6b0d3b" stroke-width="0.8" />
                        <ellipse cx="12" cy="9" rx="3" ry="3" fill="#ffffff" />
                      </svg>
                      {event.address}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="Community-event-meta-row"><span className="Community-event-meta-emoji" aria-hidden="true">🎥</span> Online event</p>
                  {event.externalLink && (
                    <p className="Community-event-meta-row">
                      <span className="Community-event-meta-emoji" aria-hidden="true">🔗</span> <a href={event.externalLink} target="_blank" rel="noreferrer" className="Community-event-link">{event.externalLink}</a>
                    </p>
                  )}
                </>
              )}
              <p className="Community-event-meta-row"><span className="Community-event-meta-emoji" aria-hidden="true">📅</span> {formatDateLabel(event.startDate, event.startTime)}</p>
              {event.hasEndDate && event.endDate && (
                <p className="Community-event-meta-row"><span className="Community-event-meta-emoji" aria-hidden="true">⏰</span> Ends {formatDateLabel(event.endDate, event.endTime)}</p>
              )}
              {event.timezone && <p className="Community-event-meta-row"><span className="Community-event-meta-emoji" aria-hidden="true">🕒</span> {event.timezone}</p>}
              {inPerson && event.externalLink && (
                <p className="Community-event-meta-row">
                  <span className="Community-event-meta-emoji" aria-hidden="true">🔗</span> <a href={event.externalLink} target="_blank" rel="noreferrer" className="Community-event-link">{event.externalLink}</a>
                </p>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          {post.title && <h3 className="Community-heading-section Community-card-title Community-post-title" style={fontRenderStyle({ color: post.titleColor, weight: post.titleWeight, size: post.titleSize, italic: post.titleItalic, underline: post.titleUnderline })}>{post.title}</h3>}
          {post.location && <p className="Community-post-location-line">📍 {post.location}</p>}
          {post.text && <p className="Community-body-text Community-post-text" style={fontRenderStyle({ color: post.textColor, weight: post.textWeight, size: post.textSize, italic: post.textItalic, underline: post.textUnderline })}>{post.text}</p>}
          {(post.photos && post.photos.length > 0) || (post.videos && post.videos.length > 0) || post.video || ytVideos.length > 0 ? (
            <PostMediaGallery
              items={(() => {
                const arr = [];
                if (post.mediaOrder && post.mediaOrder.length > 0) {
                  post.mediaOrder.forEach((m) => {
                    if (m.type === "image") arr.push({ type: "photo", src: m.src });
                    else if (m.type === "video") arr.push({ type: "video", src: m.src, poster: post.videoThumbnail, edits: post.videoEdits && post.videoEdits[0] ? post.videoEdits[0] : null });
                    else if (m.type === "youtube") arr.push({ type: "youtube", src: m.embed || m.src, url: m.url || "", thumb: m.thumb || "" });
                  });
                  return arr;
                }
                if (post.photos) post.photos.forEach((src) => arr.push({ type: "photo", src }));
                const vs = post.videos || [];
                const v = vs[0] || post.video;
                if (v) {
                  arr.push({
                    type: "video",
                    src: v,
                    poster: post.videoThumbnail,
                    edits: post.videoEdits && post.videoEdits[0] ? post.videoEdits[0] : null,
                  });
                }
                ytVideos.forEach((y) => arr.push({ type: "youtube", src: y.embed, url: y.url, thumb: y.thumb }));
                return arr;
              })()}
              onOpenMedia={(index) => setLightboxIndex(index)}
            />
          ) : null}
          {post.gif && (
            <div className="Community-post-gif-wrap">
              <img src={post.gif.preview || post.gif.media} alt="Post GIF" className="Community-post-gif" />
            </div>
          )}
        </>
      )}

      <PostSocials socials={(post.event && post.event.socials) || post.socials} />
      <PostEngagementBar post={post} isOwner={post.posterId === CURRENT_USER_ID} onOpenComments={() => setCommentsOpen((v) => !v)} onOpenViews={onOpenViews} onOpenShare={onOpenShare} hideLikeCount={hiddenLikeCounts.has(post.id)} hideCommentCount={hiddenCommentCounts.has(post.id)} />
      {commentsOpen && !post.hideComments && post.commentPermission !== COMMENT_PERMISSION.OFF && <CommentsInline entity={post} />}
      {lightboxIndex !== null && (() => {
        const viewerMedia = event
          ? buildCoverMedia(event).map((m) => (m.type === "video" ? { ...m, poster: event.coverThumbnail || undefined } : m))
          : (() => {
              const arr = [];
              if (post.mediaOrder && post.mediaOrder.length > 0) {
                post.mediaOrder.forEach((m) => {
                  if (m.type === "image") arr.push({ type: "photo", src: m.src });
                  else if (m.type === "video") arr.push({ type: "video", src: m.src, poster: post.videoThumbnail });
                  else if (m.type === "youtube") arr.push({ type: "youtube", src: m.embed || m.src, url: m.url || "" });
                });
                return arr;
              }
              if (post.photos) post.photos.forEach((src) => arr.push({ type: "photo", src }));
              const v = (post.videos && post.videos[0]) || post.video;
              if (v) arr.push({ type: "video", src: v, poster: post.videoThumbnail });
              ytVideos.forEach((y) => arr.push({ type: "youtube", src: y.embed, url: y.url }));
              return arr;
            })();
        if (!viewerMedia.length) return null;
        return (
          <ExpandedPostViewer
            post={post}
            items={viewerMedia}
            initialIndex={Math.min(lightboxIndex, viewerMedia.length - 1)}
            onClose={() => setLightboxIndex(null)}
            controlProps={controlProps}
            isOwner={post.posterId === CURRENT_USER_ID}
            onOpenViews={onOpenViews}
            onOpenShare={onOpenShare}
            onOpenTag={() => setShowTagOverlay(true)}
hideLikeCount={hiddenLikeCounts.has(post.id)}
            hideCommentCount={hiddenCommentCounts.has(post.id)}
          />
        );
      })()}
      {showTagOverlay && (
        <TagPeopleOverlay
          selected={((event && event.taggedPeople && event.taggedPeople.length) ? event.taggedPeople : post.taggedPeople) || []}
          readOnly
          onSave={() => setShowTagOverlay(false)}
          onClose={() => setShowTagOverlay(false)}
        />
      )}
    </article>
  );
};

const PeopleViewedSection = ({ ad, onUnlockClick }) => {
  const viewers = MOCK_VIEWERS[ad.id] || [];
  const [followedViewers, setFollowedViewers] = useState(new Set());

  const toggleFollowViewer = (memberId) => {
    setFollowedViewers((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  const renderViewer = (v) => {
    const member = getMemberById(v.id) || { name: v.name, avatar: "" };
    const initials = member.avatar || v.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
    const isFollowing = followedViewers.has(v.id);
    return (
      <li key={v.id} className="Community-viewer-item Community-reaction-item">
        <span className="Community-member-avatar Community-comment-avatar" aria-hidden="true">{initials}</span>
        <div className="Community-reaction-info">
          <div className="Community-reaction-name-row">
            <span className="Community-viewer-name">{v.name}</span>
            <span className="Community-reaction-time">{v.viewedAt}</span>
          </div>
          {member.company && <span className="Community-reaction-company">{member.company}</span>}
        </div>
        <button
          type="button"
          className={`Community-follow-btn ${isFollowing ? "Community-following" : ""}`}
          onClick={() => toggleFollowViewer(v.id)}
        >
          {isFollowing ? (
            <>
              <span className="Community-follow-tick" aria-hidden="true">✓</span>
              Following
            </>
          ) : (
            <>
              <span className="Community-follow-plus" aria-hidden="true">+</span>
              Follow
            </>
          )}
        </button>
      </li>
    );
  };

  if (viewers.length === 0) {
    return <EmptyState title="No People Viewed Yet" message="Viewer information will appear when members view your advertisement." />;
  }

  const freeViewers = viewers.slice(0, FREE_VIEWER_COUNT);
  const remainingViewers = viewers.slice(FREE_VIEWER_COUNT);
  const isUnlocked = ad.viewerAccess === "unlocked";

  return (
    <div className="Community-people-viewed">
      <h4 className="Community-heading-sub">People Viewed ({viewers.length})</h4>
      <ul className="Community-viewer-list">
        {freeViewers.map(renderViewer)}
      </ul>

      {remainingViewers.length > 0 && !isUnlocked && (
        <div className="Community-locked-viewers">
          <p className="Community-subheading">More people viewed this advertisement</p>
          <div className="Community-blur-block" aria-hidden="true">
            {remainingViewers.map((v) => (
              <div key={v.id} className="Community-blur-line" />
            ))}
          </div>
          <p className="Community-body-text Community-locked-copy">
            Unlock to see everyone who viewed your advertisement.
          </p>
          <p className="Community-unlock-amount">Amount: &#8377;{UNLOCK_PRICE_INR}</p>
          <PrimaryButton onClick={() => onUnlockClick(ad)}>Unlock Viewers</PrimaryButton>
        </div>
      )}

      {remainingViewers.length > 0 && isUnlocked && (
        <ul className="Community-viewer-list">
          {remainingViewers.map(renderViewer)}
        </ul>
      )}
    </div>
  );
};


export const CommentsInline = ({ entity }) => {
  const [sortOrder, setSortOrder] = useState("recent");
  const [newComment, setNewComment] = useState("");
  const [selectedGif, setSelectedGif] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showGif, setShowGif] = useState(false);
  const [comments, setComments] = useState(() => MOCK_COMMENTS_BY_AD[entity.id] || MOCK_COMMENTS_BY_POST[entity.id] || entity.defaultComments || []);
  const [likedComments, setLikedComments] = useState(new Set());
  const [hiddenComments, setHiddenComments] = useState(new Set());
  const [followedCommenters, setFollowedCommenters] = useState(new Set());
  const [menuCommentId, setMenuCommentId] = useState(null);
  const [reportedComment, setReportedComment] = useState(null);
  const [replyOpenId, setReplyOpenId] = useState(null);
  const [replyTextById, setReplyTextById] = useState({});
  const [replyEmojiOpenId, setReplyEmojiOpenId] = useState(null);
  const [replyGifOpenId, setReplyGifOpenId] = useState(null);
  const [replyGifById, setReplyGifById] = useState({});
  const [visibleComments, setVisibleComments] = useState(10);
  const isOwnerPost = entity.posterId === CURRENT_USER_ID;
  const toggleCommentMenu = (id) => setMenuCommentId((prev) => (prev === id ? null : id));

  const commentsRef = useRef(null);
  useEffect(() => {
    if (!menuCommentId) return;
    const onPointerDown = (e) => {
      if (commentsRef.current && !commentsRef.current.contains(e.target)) setMenuCommentId(null);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [menuCommentId]);

  const toggleFollowCommenter = (memberId) => {
    setFollowedCommenters((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  const toggleCommentHidden = (id) => {
    setHiddenComments((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setMenuCommentId(null);
  };

  const reportComment = (comment) => {
    setReportedComment(comment);
    setMenuCommentId(null);
  };

  const deleteComment = (id) => {
    setComments((prev) => prev.filter((c) => c.id !== id && c.parentId !== id));
    setMenuCommentId(null);
    const isSession = String(id).startsWith("c-new-") || String(id).startsWith("c-reply-");
    if (isSession) commentStore.removeDelta(entity.id);
    else commentStore.markDeleted(id);
  };

  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  };

  const toggleCommentLike = (id) => {
    setLikedComments((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openReply = (id) => {
    setReplyOpenId(id);
    setReplyTextById((prev) => ({ ...prev, [id]: prev[id] || "" }));
  };

  const updateReplyText = (id, text) => {
    setReplyTextById((prev) => ({ ...prev, [id]: text }));
  };

  const insertReplyEmoji = (id, e) => {
    if (((replyTextById[id] || "") + e).length > COMMENT_MAX_WORDS) return;
    setReplyTextById((prev) => ({ ...prev, [id]: (prev[id] || "") + e }));
    setReplyEmojiOpenId((prev) => (prev === id ? null : prev));
  };

  const submitReply = (parent) => {
    const text = (replyTextById[parent.id] || "").trim();
    const gif = replyGifById[parent.id];
    if (!text && !gif) return;
    setComments((prev) => [
      { id: `c-reply-${Date.now()}`, memberId: CURRENT_USER_ID, name: currentUser.name, text, gif, replyToName: parent.name, parentId: parent.id, timestamp: "now" },
      ...prev,
    ]);
    commentStore.add(entity.id);
    setReplyTextById((prev) => ({ ...prev, [parent.id]: "" }));
    setReplyGifById((prev) => ({ ...prev, [parent.id]: null }));
    setReplyEmojiOpenId(null);
    setReplyGifOpenId(null);
    setReplyOpenId(null);
  };

  const sorted = useMemo(() => {
    const list = [...comments];
    if (sortOrder === "recent") {
      list.sort((a, b) => {
        const ha = parseInt(a.timestamp, 10);
        const hb = parseInt(b.timestamp, 10);
        if (!isNaN(ha) && !isNaN(hb)) return ha - hb;
        return 0;
      });
    } else {
      list.reverse();
    }
    return list;
  }, [comments, sortOrder]);

  const buildThread = (flat) => {
    const byParent = {};
    flat.forEach((c) => {
      const key = c.parentId || "root";
      (byParent[key] = byParent[key] || []).push(c);
    });
    const build = (parents) =>
      (parents || []).map((c) => ({
        ...c,
        children: build(byParent[c.id] || []),
      }));
    return build(byParent.root || []);
  };

  const thread = useMemo(() => buildThread(sorted), [sorted]);

  const currentUser = getMemberById(CURRENT_USER_ID);

  const handleAdd = () => {
    const text = newComment.trim();
    if (!text && !selectedGif) return;
    setComments((prev) => [
      { id: `c-new-${Date.now()}`, memberId: CURRENT_USER_ID, name: currentUser.name, text, gif: selectedGif, timestamp: "now" },
      ...prev,
    ]);
    commentStore.add(entity.id);
    setNewComment("");
    setSelectedGif(null);
    setShowEmoji(false);
    setShowGif(false);
  };

  const insertEmoji = (e) => {
    const words = (newComment + e).trim().split(/\s+/).filter(Boolean);
    if (words.length > COMMENT_MAX_WORDS) return;
    setNewComment((prev) => prev + e);
    setShowEmoji(false);
  };

  const replyComposer = (c) => (
    <div className="Community-comment-replybox">
      <div className="Community-comment-replybox-box">
        <div className="Community-comment-replybox-textrow">
          <span className="Community-comment-reply-mention"><strong>{c.name}</strong></span>
          <textarea
            className="Community-comment-reply-input"
            placeholder="Write a reply..."
            rows={1}
            wrap="soft"
            maxLength={COMMENT_MAX_WORDS}
            value={replyTextById[c.id] || ""}
            onChange={(e) => {
              const v = e.target.value;
              const words = v.trim().split(/\s+/).filter(Boolean);
              if (words.length > COMMENT_MAX_WORDS) return;
              updateReplyText(c.id, v);
              autoGrow(e.target);
              if (replyGifOpenId === c.id) setReplyGifOpenId(null);
            }}
          />
        </div>
        <div className="Community-comment-replybox-footer">
          <div className="Community-comment-reply-pill-tools">
            <button type="button" className="Community-comment-tool-btn" onClick={() => { setReplyEmojiOpenId((prev) => (prev === c.id ? null : c.id)); setReplyGifOpenId(null); }} aria-label="Add emoji">
              😊
            </button>
            <button type="button" className="Community-comment-tool-btn" onClick={() => { setReplyGifOpenId((prev) => (prev === c.id ? null : c.id)); setReplyEmojiOpenId(null); }} aria-label="Add GIF">
              <svg className="Community-tool-btn-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
                <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <span className="Community-comment-word-count">{(replyTextById[c.id] || "").length} / {COMMENT_MAX_WORDS}</span>
          <button
            type="button"
            className={`Community-comment-send-btn${!((replyTextById[c.id] || "").trim()) && !replyGifById[c.id] ? " Community-comment-send-btn--disabled" : ""}`}
            onClick={() => submitReply(c)}
            disabled={!((replyTextById[c.id] || "").trim()) && !replyGifById[c.id]}
            aria-label="Send reply"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="#ffffff" aria-hidden="true">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="#ffffff" />
            </svg>
          </button>
        </div>
      </div>
      {replyGifById[c.id] && (
        <div className="Community-comment-gif-preview">
          <img src={replyGifById[c.id].preview || replyGifById[c.id].media} alt="Selected GIF" />
          <button type="button" className="Community-comment-gif-remove" onClick={() => setReplyGifById((prev) => ({ ...prev, [c.id]: null }))} aria-label="Remove GIF">×</button>
        </div>
      )}
      {replyEmojiOpenId === c.id && (
        <div className="Community-emoji-palette">
          {EmojiPalette.map((e) => (
            <button key={e} type="button" className="Community-emoji-btn" onClick={() => insertReplyEmoji(c.id, e)}>{e}</button>
          ))}
        </div>
      )}
      {replyGifOpenId === c.id && (
        <GifPicker
          onSelect={(g) => {
            setReplyGifById((prev) => ({ ...prev, [c.id]: g }));
            setReplyGifOpenId(null);
          }}
          onClose={() => setReplyGifOpenId(null)}
        />
      )}
    </div>
  );

  const renderNode = (node) => {
    const c = node;
    const isMine = c.memberId === CURRENT_USER_ID;
    const canModerate = isMine || isOwnerPost;
    const menuOpen = menuCommentId === c.id;
    const hasReplies = node.children.length > 0;
    const isCommentHidden = hiddenComments.has(c.id);

    if (isCommentHidden && !canModerate) return null;

    if (isCommentHidden) {
      return (
        <li key={c.id} className="Community-comment-item Community-comment-item-hidden">
          <div className="Community-comment-hidden-card">
            <p className="Community-comment-hidden-note">🚫 This {c.parentId ? "reply" : "comment"} was hidden.</p>
            <div className="Community-comment-hidden-card-main">
              <div className="Community-member-avatar Community-comment-avatar" aria-hidden="true">
                {getMemberById(c.memberId)?.avatar || c.name.slice(0, 2).toUpperCase()}
              </div>
              <div className="Community-comment-hidden-body">
                <div className="Community-comment-meta">
                  <span className="Community-poster-name">{c.name}</span>
                  <span className="Community-posted-time">{c.timestamp}</span>
                </div>
                {(getMemberById(c.memberId)?.company || c.company) && (
                  <p className="Community-company-name">{getMemberById(c.memberId)?.company || c.company}</p>
                )}
                {c.replyToName && (
                  <span className="Community-comment-replyto">Replying to <strong>{c.replyToName}</strong></span>
                )}
                <p className="Community-body-text Community-comment-text">{c.text}</p>
                {c.gif && (
                  <img src={c.gif.preview || c.gif.media} alt="Comment GIF" className="Community-comment-gif" loading="lazy" />
                )}
                <div className="Community-comment-hidden-actions">
                  <button type="button" className="Community-comment-action-btn" onClick={() => toggleCommentHidden(c.id)}>
                    Unhide
                  </button>
                  <button type="button" className="Community-comment-action-btn Community-comment-menu-option-report" onClick={() => deleteComment(c.id)}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </li>
      );
    }

    return (
      <li key={c.id} className="Community-comment-item">
        <div className="Community-comment-item-main">
          <div className="Community-member-avatar Community-comment-avatar" aria-hidden="true">
            {getMemberById(c.memberId)?.avatar || c.name.slice(0, 2).toUpperCase()}
          </div>
          <div className="Community-comment-body">
            <div className="Community-comment-meta">
              <span className="Community-poster-name">{c.name}</span>
              <span className="Community-posted-time">{c.timestamp}</span>
              <span className="Community-comment-meta-right">
                {entity.posterId !== c.memberId && (
                  <button
                    type="button"
                    className={`Community-follow-btn ${followedCommenters.has(c.memberId) ? "Community-following" : ""}`}
                    onClick={() => toggleFollowCommenter(c.memberId)}
                  >
                    {followedCommenters.has(c.memberId) ? (
                      <>
                        <span className="Community-follow-tick" aria-hidden="true">✓</span>
                        Following
                      </>
                    ) : (
                      <>
                        <span className="Community-follow-plus" aria-hidden="true">+</span>
                        Follow
                      </>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  className={`Community-comment-meta-like ${likedComments.has(c.id) ? "Community-comment-action-liked" : ""}`}
                  onClick={() => toggleCommentLike(c.id)}
                  aria-label={likedComments.has(c.id) ? "Unlike comment" : "Like comment"}
                  aria-pressed={likedComments.has(c.id)}
                >
                  <span aria-hidden="true" className="Community-reaction-btn-icon Community-heart-icon">{likedComments.has(c.id) ? "❤️" : "♡"}</span>
                </button>
              </span>
            </div>
            {(getMemberById(c.memberId)?.company || c.company) && (
              <p className="Community-company-name">{getMemberById(c.memberId)?.company || c.company}</p>
            )}
            {c.replyToName && (
              <span className="Community-comment-replyto">Replying to <strong>{c.replyToName}</strong></span>
            )}
            <p className="Community-body-text Community-comment-text">{c.text}</p>
            {c.gif && (
              <img src={c.gif.preview || c.gif.media} alt="Comment GIF" className="Community-comment-gif" loading="lazy" />
            )}
            <div className="Community-comment-actions">
              <button
                type="button"
                className={`Community-comment-action-btn Community-comment-action-like-mobile ${likedComments.has(c.id) ? "Community-comment-action-liked" : ""}`}
                onClick={() => toggleCommentLike(c.id)}
                aria-label={likedComments.has(c.id) ? "Unlike comment" : "Like comment"}
                aria-pressed={likedComments.has(c.id)}
              >
                <span aria-hidden="true" className="Community-reaction-btn-icon Community-heart-icon">{likedComments.has(c.id) ? "❤️" : "♡"}</span>
              </button>
              <button
                type="button"
                className="Community-comment-action-btn"
                onClick={() => openReply(c.id)}
                aria-label={`Reply to ${c.name}`}
              >
                <svg className="Community-engagement-btn-icon Community-comment-empty-icon" viewBox="0 0 24 24" width="25" height="25" fill="none" aria-hidden="true">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="Community-comment-action-label">Reply</span>
              </button>
            </div>
            {replyOpenId === c.id && replyComposer(c)}
          </div>
          <div className="Community-comment-menu">
            <button
              type="button"
              className="Community-comment-menu-btn"
              onClick={() => toggleCommentMenu(c.id)}
              aria-label="More options"
              aria-expanded={menuOpen}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                <circle cx="12" cy="5" r="1.6" />
                <circle cx="12" cy="12" r="1.6" />
                <circle cx="12" cy="19" r="1.6" />
              </svg>
            </button>
            {menuOpen && (
              <div className="Community-comment-menu-dropdown">
                {canModerate && (
                  <button type="button" className="Community-comment-menu-option" onClick={() => toggleCommentHidden(c.id)}>
                    {hiddenComments.has(c.id) ? "Unhide comment" : "Hide comment"}
                  </button>
                )}
                {canModerate && (
                  <button type="button" className="Community-comment-menu-option Community-comment-menu-option-report" onClick={() => deleteComment(c.id)}>
                    Delete comment
                  </button>
                )}
                <button type="button" className="Community-comment-menu-option Community-comment-menu-option-report" onClick={() => reportComment(c)}>
                  Report comment
                </button>
              </div>
            )}
          </div>
        </div>
        {hasReplies && (
          <div className="Community-comment-thread">
            <ul className="Community-comment-replies">
              {node.children.map(renderNode)}
            </ul>
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="Community-comments-inline" ref={commentsRef}>
      <CommentSortDropdown value={sortOrder} onChange={setSortOrder} />

      <div className="Community-comment-composer">
        <div className="Community-member-avatar Community-comment-avatar" aria-hidden="true">
          {currentUser.avatar}
        </div>
        <div className="Community-comment-composer-main">
          <div className="Community-comment-inputbox">
<textarea
            className="Community-comment-input"
            placeholder="Write a comment..."
            rows={1}
            maxLength={COMMENT_MAX_WORDS}
            value={newComment}
            onChange={(e) => {
              const words = e.target.value.trim().split(/\s+/).filter(Boolean);
              if (words.length > COMMENT_MAX_WORDS) return;
              setNewComment(e.target.value);
              autoGrow(e.target);
              if (showGif) setShowGif(false);
            }}
          />
            <div className="Community-comment-inputbox-footer">
              <div className="Community-comment-inputbox-tools">
                <button type="button" className="Community-comment-tool-btn" onClick={() => { setShowEmoji((v) => !v); setShowGif(false); }} aria-label="Add emoji">
                  😊
                </button>
                <button type="button" className="Community-comment-tool-btn" onClick={() => { setShowGif((v) => !v); setShowEmoji(false); }} aria-label="Add GIF">
                  <svg className="Community-tool-btn-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
                    <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                    <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              <span className="Community-comment-word-count">{newComment.length} / {COMMENT_MAX_WORDS}</span>
              <button
                type="button"
                className={`Community-comment-send-btn${!newComment.trim() && !selectedGif ? " Community-comment-send-btn--disabled" : ""}`}
                onClick={handleAdd}
                disabled={!newComment.trim() && !selectedGif}
                aria-label="Send reply"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="#ffffff" aria-hidden="true">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="#ffffff"></path>
                </svg>
              </button>
            </div>
          </div>
          {selectedGif && (
            <div className="Community-comment-gif-preview">
              <img src={selectedGif.preview || selectedGif.media} alt="Selected GIF" />
              <button type="button" className="Community-comment-gif-remove" onClick={() => setSelectedGif(null)} aria-label="Remove GIF">
                ×
              </button>
            </div>
          )}

          {showEmoji && (
            <div className="Community-emoji-palette">
              {EmojiPalette.map((e) => (
                <button key={e} type="button" className="Community-emoji-btn" onClick={() => insertEmoji(e)}>
                  {e}
                </button>
              ))}
            </div>
          )}

          {showGif && <GifPicker onSelect={(g) => { setSelectedGif(g); setShowGif(false); }} onClose={() => setShowGif(false)} />}
        </div>
      </div>

      {thread.length === 0 ? (
        <EmptyState title="No Comments Yet" message="Be the first to comment on this advertisement." />
      ) : (
        <>
          <ul className="Community-comment-list">
            {thread.slice(0, visibleComments).map(renderNode)}
          </ul>
          {thread.length > visibleComments && (
            <div className="Community-comment-more-wrap">
              <button
                type="button"
                className="Community-comment-more-btn"
                onClick={() => setVisibleComments((v) => v + 10)}
              >
                See {thread.length - visibleComments} more comments
              </button>
            </div>
          )}
        </>
      )}

      {reportedComment && (
        <ReportPostModal
          post={{ id: reportedComment.id, posterId: reportedComment.memberId }}
          reporterUserId={CURRENT_USER_ID}
          onClose={() => setReportedComment(null)}
          onSubmitReport={() => setReportedComment(null)}
        />
      )}
    </div>
  );
};

const CommentsOverlay = ({ entity, onClose }) => {
  return (
    <Overlay title="Comments" subtitle={entity.title || (entity.text ? entity.text.slice(0, 60) : "")} onClose={onClose} wide>
      <CommentsInline entity={entity} />
      <div className="Community-overlay-actions">
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
      </div>
    </Overlay>
  );
};

const ShareOverlay = ({ ad, onClose }) => {
  const [copied, setCopied] = useState(false);
  const link = `https://ihn.example.com/community/${ad.id}`;

  const handleCopy = () => {
    navigator.clipboard?.writeText(link).then(() => setCopied(true)).catch(() => setCopied(true));
  };

  return (
    <Overlay title="Share Advertisement" subtitle={ad.title} onClose={onClose}>
      <p className="Community-body-text">
        Share this advertisement with the community or your network.
      </p>
      <div className="Community-share-link-row">
        <input
          type="text"
          readOnly
          className="Community-input Community-share-link-input"
          value={link}
          onFocus={(e) => e.target.select()}
        />
        <PrimaryButton onClick={handleCopy}>{copied ? "Copied!" : "Copy Link"}</PrimaryButton>
      </div>
      <div className="Community-share-platforms">
        <a className="Community-share-platform" target="_blank" rel="noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`}>📘 Facebook</a>
        <a className="Community-share-platform" target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(link)}&text=${encodeURIComponent(ad.title)}`}>🐦 Twitter</a>
        <a className="Community-share-platform" target="_blank" rel="noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`}>💼 LinkedIn</a>
        <a className="Community-share-platform" href={`mailto:?subject=${encodeURIComponent(ad.title)}&body=${encodeURIComponent(`${ad.title}\n${link}`)}`}>✉️ Email</a>
      </div>
      <div className="Community-overlay-actions">
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
      </div>
    </Overlay>
  );
};

const ViewsOverlay = ({ ad, onClose, onUnlockClick, myReaction, initialTab }) => {
  const [tab, setTab] = useState(initialTab || "all");
  const [followedMembers, setFollowedMembers] = useState(new Set());
  const mobileTabsRef = useRef(null);
  const [indicator, setIndicator] = useState(null);

  const selectTab = (value) => {
    setTab(value);
    const bar = mobileTabsRef.current;
    const btn = bar?.querySelector(`[data-tab="${value}"]`);
    btn?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  };

  const measureTabIndicator = () => {
    const bar = mobileTabsRef.current;
    const activeBtn = bar?.querySelector(".Community-toggle-active");
    if (bar && activeBtn) {
      const barRect = bar.getBoundingClientRect();
      const btnRect = activeBtn.getBoundingClientRect();
      setIndicator({
        left: btnRect.left - barRect.left + bar.scrollLeft,
        width: btnRect.width,
      });
    } else {
      setIndicator(null);
    }
  };

  useEffect(() => {
    measureTabIndicator();
    window.addEventListener("resize", measureTabIndicator);
    return () => window.removeEventListener("resize", measureTabIndicator);
  }, [tab]);
  const reactions = otherReactions(((MOCK_REACTIONS_BY_AD[ad.id] || []).length
    ? MOCK_REACTIONS_BY_AD[ad.id]
    : (MOCK_REACTIONS_BY_POST[ad.id] || [])));

  const currentUser = getMemberById(CURRENT_USER_ID);
  const myReactionEntry = myReaction
    ? [{ id: `me-${ad.id}`, memberId: CURRENT_USER_ID, name: currentUser?.name || "You", type: myReaction, time: "just now" }]
    : [];
  const displayReactions = sortReactionsByRecency([...myReactionEntry, ...reactions]);

  const toggleFollowMember = (memberId) => {
    setFollowedMembers((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  const reactionTabs = [
    { value: "like", label: "👍 Like" },
    { value: "celebrate", label: "👏 Celebrate" },
    { value: "support", label: "🤝 Support" },
    { value: "love", label: "❤️ Love" },
    { value: "insightful", label: "💡 Insightful" },
    { value: "funny", label: "😂 Funny" },
  ];

  let content;
  if (tab === "all") {
    content = displayReactions.length === 0 ? null : (
      <div>
        <p className="Community-label">Reactions ({displayReactions.length})</p>
        <ul className="Community-viewer-list">
          {displayReactions.map((r) => (() => {
              const member = getMemberById(r.memberId) || { name: r.name, avatar: "" };
              const initials = member.avatar || r.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
              return (
                <li key={r.id} className="Community-viewer-item Community-reaction-item">
                  <span className="Community-reaction-avatar">
                  <span className="Community-member-avatar Community-comment-avatar" aria-hidden="true">{initials}</span>
                  <span className="Community-reaction-badge" aria-hidden="true">{r.type === "like" ? "👍" : REACTION_ICONS[r.type]}</span>
                </span>
                  <div className="Community-reaction-info">
                    <div className="Community-reaction-name-row">
                      <span className="Community-viewer-name">{r.name}</span>
                      <span className="Community-reaction-time">{r.time}</span>
                    </div>
                    {member.company && <span className="Community-reaction-company">{member.company}</span>}
                  </div>
                  {ad.posterId !== r.memberId && (
                    <button
                      type="button"
                      className={`Community-follow-btn ${followedMembers.has(r.memberId) ? "Community-following" : ""}`}
                      onClick={() => toggleFollowMember(r.memberId)}
                    >
                      {followedMembers.has(r.memberId) ? (
                        <>
                          <span className="Community-follow-tick" aria-hidden="true">✓</span>
                          Following
                        </>
                      ) : (
                        <>
                          <span className="Community-follow-plus" aria-hidden="true">+</span>
                          Follow
                        </>
                      )}
                    </button>
                  )}
                </li>
              );
            })())}
        </ul>
      </div>
    );
  } else if (tab === "viewed") {
    content = (
      <div>
        <PeopleViewedSection ad={ad} onUnlockClick={onUnlockClick} />
      </div>
    );
  } else {
    const filtered = displayReactions.filter((r) => r.type === tab);
    content = (
      <div>
        <p className="Community-label">{REACTION_LABELS[tab]} ({filtered.length})</p>
        <ul className="Community-viewer-list">
          {filtered.length === 0 ? (
            <li className="Community-viewer-item"><span className="Community-viewer-name">No {REACTION_LABELS[tab].toLowerCase()} reactions yet</span></li>
          ) : (
            filtered.map((r) => (() => {
              const member = getMemberById(r.memberId) || { name: r.name, avatar: "" };
              const initials = member.avatar || r.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
              return (
                <li key={r.id} className="Community-viewer-item Community-reaction-item">
                  <span className="Community-reaction-avatar">
                  <span className="Community-member-avatar Community-comment-avatar" aria-hidden="true">{initials}</span>
                  <span className="Community-reaction-badge" aria-hidden="true">{r.type === "like" ? "👍" : REACTION_ICONS[r.type]}</span>
                </span>
                  <div className="Community-reaction-info">
                    <div className="Community-reaction-name-row">
                      <span className="Community-viewer-name">{r.name}</span>
                      <span className="Community-reaction-time">{r.time}</span>
                    </div>
                    {member.company && <span className="Community-reaction-company">{member.company}</span>}
                  </div>
                  {ad.posterId !== r.memberId && (
                    <button
                      type="button"
                      className={`Community-follow-btn ${followedMembers.has(r.memberId) ? "Community-following" : ""}`}
                      onClick={() => toggleFollowMember(r.memberId)}
                    >
                      {followedMembers.has(r.memberId) ? (
                        <>
                          <span className="Community-follow-tick" aria-hidden="true">✓</span>
                          Following
                        </>
                      ) : (
                        <>
                          <span className="Community-follow-plus" aria-hidden="true">+</span>
                          Follow
                        </>
                      )}
                    </button>
                  )}
                </li>
              );
            })())
          )}
        </ul>
      </div>
    );
  }

  return (
    <Overlay title="Insights" onClose={onClose} wide closeOnlyByButton panelClassName="Community-overlay-insights">
      <div className="Community-views-tabs" ref={mobileTabsRef}>
        <button
          type="button"
          data-tab="all"
          className={`Community-toggle-btn Community-toggle-btn-reaction Community-mobile-tab ${tab === "all" ? "Community-toggle-active" : ""}`}
          onClick={() => selectTab("all")}
        >
          <span className="Community-mobile-tab-label">All</span>
          <span className="Community-mobile-count">{displayReactions.length}</span>
        </button>
        {reactionTabs.filter((t) => displayReactions.some((r) => r.type === t.value)).map((t) => {
          const icon = t.value === "like" ? "👍" : REACTION_ICONS[t.value];
          const count = displayReactions.filter((r) => r.type === t.value).length;
          return (
            <button
              key={t.value}
              type="button"
              data-tab={t.value}
              className={`Community-toggle-btn Community-toggle-btn-reaction Community-mobile-tab ${tab === t.value ? "Community-toggle-active" : ""}`}
              onClick={() => selectTab(t.value)}
            >
              <span className="Community-toggle-reaction-icon">{icon}</span>
              <span className="Community-mobile-count">{count}</span>
            </button>
          );
        })}
        {ad.posterId === CURRENT_USER_ID && (
          <button
            type="button"
            data-tab="viewed"
            className={`Community-toggle-btn Community-toggle-btn-reaction Community-mobile-tab ${tab === "viewed" ? "Community-toggle-active" : ""}`}
            onClick={() => selectTab("viewed")}
          >
            <span className="Community-toggle-reaction-icon">👁️</span>
            <span className="Community-mobile-count">{(MOCK_VIEWERS[ad.id] || []).length}</span>
          </button>
        )}
        <span
          className="Community-views-tab-indicator"
          aria-hidden="true"
          style={indicator ? { transform: `translateX(${indicator.left}px)`, width: indicator.width } : { opacity: 0 }}
        />
      </div>
      {content}
    </Overlay>
  );
};


/* ============================================================
   Community right sidebar (Profile)
   ============================================================ */

const ProfileCard = ({ currentUser, posts, archivedPosts }) => {
  const navigate = useNavigate();
  const myPosts = posts.filter(
    (p) => p.posterId === CURRENT_USER_ID && !archivedPosts.has(p.id)
  );
  return (
    <section className="Community-panel-block Community-sidebar-card Community-profile-card">
      <div className="Community-profile-head">
        <div className="Community-profile-avatar" aria-hidden="true">
          {currentUser.avatar}
        </div>
        <p className="Community-profile-name">{currentUser.name}</p>
        <span className="Community-profile-membership">Premium Member</span>
        <p className="Community-profile-role">
          {currentUser.designation}
          {currentUser.company ? ` · ${currentUser.company}` : ""}
        </p>
        {currentUser.location && <p className="Community-profile-loc"><span aria-hidden="true">📍</span> {currentUser.location}</p>}
      </div>
      <button
        type="button"
        className="Community-myposts-btn"
        onClick={() => navigate("/community/my-posts")}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polygon points="12 2 2 7 12 12 22 7 12 2" />
          <polyline points="2 12 12 17 22 12" />
          <polyline points="2 17 12 22 22 17" />
        </svg>
        My Posts
      </button>
    </section>
  );
};

const RightPanel = ({ posts, hiddenPosts, archivedPosts }) => {
  const currentUser = getMemberById(CURRENT_USER_ID) || MOCK_MEMBERS[0];
  const visiblePosts = useMemo(() => posts.filter((p) => !hiddenPosts.has(p.id)), [posts, hiddenPosts]);

  return (
    <aside className="Community-right-panel">
      <ProfileCard currentUser={currentUser} posts={visiblePosts} archivedPosts={archivedPosts} />
    </aside>
  );
};

const Community = () => {
  const [posts, setPosts] = useState(() => getCommunityState().posts);
  const [searchQuery, setSearchQuery] = useState("");

  const [isPostOpen, setIsPostOpen] = useState(false);
  const [showPostLimitOverlay, setShowPostLimitOverlay] = useState(false);
  const pendingLimitFilesRef = useRef(null);
  const [postInitialMode, setPostInitialMode] = useState("post");
  const [postInitialPhotos, setPostInitialPhotos] = useState([]);
  const [postInitialVideo, setPostInitialVideo] = useState(null);
  const [postInitialVideos, setPostInitialVideos] = useState([]);
  const [postInitialYoutubeOpen, setPostInitialYoutubeOpen] = useState(false);
  const [isEventOpen, setIsEventOpen] = useState(false);
  const [commentsAd, setCommentsAd] = useState(null);
  const [shareAd, setShareAd] = useState(null);
  const [viewsAd, setViewsAd] = useState(null);

  const [followingPosters, setFollowingPosters] = useState(() => getCommunityState().followingPosters);
  const [savedPosts, setSavedPosts] = useState(() => getCommunityState().savedPosts);
  const [archivedPosts, setArchivedPosts] = useState(() => getCommunityState().archivedPosts);
  const [hiddenPosts, setHiddenPosts] = useState(() => getCommunityState().hiddenPosts);
  const [hiddenLikeCounts, setHiddenLikeCounts] = useState(() => getCommunityState().hiddenLikeCounts);
  const [hiddenCommentCounts, setHiddenCommentCounts] = useState(() => getCommunityState().hiddenCommentCounts);
  const [reportPost, setReportPost] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [toast, setToast] = useState(null);

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    publishCommunityState({
      posts,
      followingPosters,
      savedPosts,
      archivedPosts,
      hiddenPosts,
      hiddenLikeCounts,
      hiddenCommentCounts,
      likedPosts: getCommunityState().likedPosts,
    });
  }, [posts, followingPosters, savedPosts, archivedPosts, hiddenPosts, hiddenLikeCounts, hiddenCommentCounts]);

  const handleCreatePost = useCallback((postData) => {
    setPosts((prev) => {
      const nextId = Math.max(1000, ...prev.map((p) => p.id)) + 1;
      const newPost = buildPost({
        id: nextId,
        text: postData.text,
        textColor: postData.textColor || "#1a1a1a",
        textSize: postData.textSize || 16,
        textWeight: postData.textWeight || 400,
        textItalic: Boolean(postData.textItalic),
        textUnderline: Boolean(postData.textUnderline),
        title: postData.title || "",
        titleColor: postData.titleColor || "#000000",
        titleSize: postData.titleSize || 20,
        titleWeight: postData.titleWeight || 600,
        titleItalic: Boolean(postData.titleItalic),
        titleUnderline: Boolean(postData.titleUnderline),
        visibility: postData.visibility,
        commentPermission: postData.commentPermission,
        emojiEnabled: postData.emojiEnabled,
        hideLikes: Boolean(postData.hideLikes),
        hideComments: Boolean(postData.hideComments),
        hideLikeCount: Boolean(postData.hideLikeCount),
        hideCommentCount: Boolean(postData.hideCommentCount),
        photos: postData.photos || [],
        videos: postData.videos || [],
        videoEdits: postData.videoEdits || [],
        videoThumbnail:
          (postData.videoThumbnails && postData.videoThumbnails.find((t) => t)) ||
          postData.videoThumbnail ||
          null,
        videoThumbnails: postData.videoThumbnails || [],
        youtube: postData.youtube || null,
        gif: postData.gif || null,
        taggedPeople: postData.taggedPeople || [],
        location: postData.location || "",
        socials: postData.socials || [],
        event: postData.event ? { ...postData.event, id: nextId } : null,
      });
      return [newPost, ...prev];
    });
  }, []);

  const applyPendingLimitFiles = useCallback(() => {
    const pending = pendingLimitFilesRef.current;
    if (!pending) {
      setShowPostLimitOverlay(false);
      return;
    }
    pendingLimitFilesRef.current = null;
    setShowPostLimitOverlay(false);
    setPostInitialMode("post");
    setPostInitialYoutubeOpen(false);
    const cap = pending.type === "photo" ? POST_MAX_MEDIA : POST_MAX_VIDEOS;
    const urls = pending.files.slice(0, cap).map((f) => URL.createObjectURL(f));
    if (pending.type === "photo") {
      setPostInitialVideo(null);
      setPostInitialVideos([]);
      setPostInitialPhotos(urls);
    } else {
      setPostInitialPhotos([]);
      setPostInitialVideo(null);
      setPostInitialVideos(urls);
    }
    setIsPostOpen(true);
  }, []);

  const cancelPendingLimitFiles = useCallback(() => {
    pendingLimitFilesRef.current = null;
    setShowPostLimitOverlay(false);
  }, []);

  const handleCreateEventPost = useCallback((eventData) => {
    setPosts((prev) => {
      const nextId = Math.max(1000, ...prev.map((p) => p.id)) + 1;
      const newPost = buildPost({
        id: nextId,
        text: "",
        event: { ...eventData, id: nextId },
        visibility: eventData.visibility || POST_VISIBILITY.ANYONE,
        commentPermission: eventData.commentPermission || COMMENT_PERMISSION.ANYONE,
        emojiEnabled: eventData.emojiEnabled !== false,
        hideLikes: Boolean(eventData.hideLikes),
        hideLikeCount: Boolean(eventData.hideLikeCount),
        hideComments: Boolean(eventData.hideComments),
        hideCommentCount: Boolean(eventData.hideCommentCount),
      });
      return [newPost, ...prev];
    });
  }, []);

  const showToast = useCallback((message, opts = {}) => {
    setToast({
      message,
      undoLabel: opts.undoLabel,
      onUndo: opts.onUndo,
      id: Date.now(),
    });
  }, []);

  const handleUpdatePost = useCallback((postId, data) => {
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? {
              ...p,
              text: data.text,
              textColor: data.textColor || "#1a1a1a",
              textSize: data.textSize || 16,
              textWeight: data.textWeight || 400,
              textItalic: Boolean(data.textItalic),
              textUnderline: Boolean(data.textUnderline),
              title: data.title || "",
              titleColor: data.titleColor || "#000000",
              titleSize: data.titleSize || 20,
              titleWeight: data.titleWeight || 600,
              titleItalic: Boolean(data.titleItalic),
              titleUnderline: Boolean(data.titleUnderline),
              visibility: data.visibility,
              commentPermission: data.commentPermission,
              emojiEnabled: data.emojiEnabled,
              hideLikes: Boolean(data.hideLikes),
              hideComments: Boolean(data.hideComments),
              hideLikeCount: Boolean(data.hideLikeCount),
              hideCommentCount: Boolean(data.hideCommentCount),
              photos: data.photos || [],
              videos: data.videos || [],
              videoEdits: data.videoEdits || [],
              videoThumbnail: data.videoThumbnail || null,
              videoThumbnails: data.videoThumbnails || [],
              mediaOrder: data.mediaOrder || p.mediaOrder,
              gif: data.gif || null,
              youtube: data.youtube || null,
              taggedPeople: data.taggedPeople || [],
              location: data.location || "",
              socials: data.socials || [],
            }
          : p
      )
    );
    setEditingPost(null);
    showToast("Post updated");
  }, [showToast]);

  const handleUpdateEvent = useCallback((postId, eventData) => {
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId && p.event
          ? {
              ...p,
              event: { ...eventData, id: postId },
              visibility: eventData.visibility || p.visibility,
              commentPermission: eventData.commentPermission || p.commentPermission,
              emojiEnabled: eventData.emojiEnabled !== false,
              hideLikes: Boolean(eventData.hideLikes),
              hideLikeCount: Boolean(eventData.hideLikeCount),
              hideComments: Boolean(eventData.hideComments),
              hideCommentCount: Boolean(eventData.hideCommentCount),
            }
          : p
      )
    );
    setEditingPost(null);
    showToast("Event updated");
  }, [showToast]);

  const handleToggleHideLikes = useCallback((id) => {
    setHiddenLikeCounts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        showToast("Likes count shown");
      } else {
        next.add(id);
        showToast("Likes count hidden");
      }
      return next;
    });
  }, [showToast]);

  const handleToggleHideCommentCounts = useCallback((id) => {
    setHiddenCommentCounts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        showToast("Comment count shown");
      } else {
        next.add(id);
        showToast("Comment count hidden");
      }
      return next;
    });
  }, [showToast]);

  const handleToggleLikesOff = useCallback((post) => {
    setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, hideLikes: !p.hideLikes } : p)));
    showToast(post.hideLikes ? "Likes turned on" : "Likes turned off");
  }, [showToast]);

  const handleToggleCommentsOff = useCallback((post) => {
    setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, hideComments: !p.hideComments } : p)));
    showToast(post.hideComments ? "Comments turned on" : "Comments turned off");
  }, [showToast]);

  const handleDeletePost = useCallback((post) => {
    setPosts((prev) => prev.filter((p) => p.id !== post.id));
    showToast("Post deleted");
  }, [showToast]);

  const handleToggleFollow = useCallback(
    (post) => {
      setFollowingPosters((prev) => {
        const next = new Set(prev);
        if (next.has(post.posterId)) {
          next.delete(post.posterId);
          showToast(`Unfollowed ${post.posterName}`);
        } else {
          next.add(post.posterId);
          showToast(`Following ${post.posterName}`);
        }
        return next;
      });
    },
    [showToast]
  );

  const handleToggleSave = useCallback(
    (post) => {
      setSavedPosts((prev) => {
        const next = new Set(prev);
        if (next.has(post.id)) {
          next.delete(post.id);
          showToast("Post removed from saved posts");
        } else {
          next.add(post.id);
          showToast("Post saved");
        }
        return next;
      });
    },
    [showToast]
  );

  const handleToggleArchive = useCallback(
    (post) => {
      setArchivedPosts((prev) => {
        const next = new Set(prev);
        if (next.has(post.id)) {
          next.delete(post.id);
          showToast("Post unarchived");
        } else {
          next.add(post.id);
          showToast("Post archived");
        }
        return next;
      });
    },
    [showToast]
  );

  const copyTextToClipboard = useCallback((url) => {
    const done = () => showToast("Link copied");
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(done);
    } else {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (_) {}
      done();
    }
  }, [showToast]);

  const handleCopyLink = useCallback((post) => {
    copyTextToClipboard(`${window.location.origin}/community/post/${post.id}`);
  }, [copyTextToClipboard]);

  const handleSharePost = useCallback((post) => {
    shareCurrentPage(post, { onCopied: () => showToast("Link copied") });
  }, [showToast]);

  const handleHide = useCallback((post) => {
    setHiddenPosts((prev) => new Set(prev).add(post.id));
    showToast("Post hidden", {
      undoLabel: "Undo",
      onUndo: () => {
        setHiddenPosts((prev) => {
          const next = new Set(prev);
          next.delete(post.id);
          return next;
        });
        setToast(null);
      },
    });
  }, [showToast]);

  const handleSubmitReport = useCallback((report) => {
    setReportPost(null);
    showToast("Report submitted");
  }, [showToast]);

  const visiblePosts = useMemo(
    () => posts.filter((p) => !hiddenPosts.has(p.id) && !archivedPosts.has(p.id)),
    [posts, hiddenPosts, archivedPosts]
  );

  const mixFeed = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? visiblePosts.filter(
          (p) =>
            (p.text || "").toLowerCase().includes(q) ||
            (p.title || "").toLowerCase().includes(q) ||
            (p.event && p.event.name ? p.event.name.toLowerCase().includes(q) : false) ||
            (p.posterName || "").toLowerCase().includes(q) ||
            (p.company || "").toLowerCase().includes(q)
        )
      : visiblePosts;
    const sorted = [...filtered].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return sorted.map((item) => ({ kind: "post", item }));
  }, [visiblePosts, searchQuery]);

  return (
    <div className="Community-page">
      <div className="Community-container">
        <header className="Community-page-header">
          <div className="Community-center-heading-wrap">
            <h1 className="Community-heading-main">Community</h1>
          </div>

          <div className="Community-controls-row">
            <div className="Community-search-pill">
              <input
                type="text"
                className="Community-search-pill-input"
                placeholder="Search Advertisements"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button
                type="button"
                className="Community-search-pill-btn"
                onClick={() => {}}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            </div>
          </div>
        </header>

        <div className="Community-layout">
          <main className="Community-main-feed">
            <SharePostCard
              onStartPost={() => {
                setPostInitialMode("post");
                setPostInitialPhotos([]);
                setPostInitialVideo(null);
                setPostInitialVideos([]);
                setIsPostOpen(true);
              }}
              onAddPhoto={(files) => {
                if (files.length > POST_MAX_MEDIA) {
                  pendingLimitFilesRef.current = { type: "photo", files };
                  setShowPostLimitOverlay(true);
                  return;
                }
                setPostInitialMode("post");
                setPostInitialVideo(null);
                setPostInitialVideos([]);
                const urls = files.slice(0, POST_MAX_MEDIA).map((f) => URL.createObjectURL(f));
                setPostInitialPhotos(urls);
                setIsPostOpen(true);
              }}
              onAddVideo={(files) => {
                if (files.length > POST_MAX_VIDEOS) {
                  pendingLimitFilesRef.current = { type: "video", files: files.slice(0, POST_MAX_VIDEOS) };
                  setShowPostLimitOverlay(true);
                  return;
                }
                setPostInitialMode("post");
                setPostInitialPhotos([]);
                setPostInitialVideos(files.slice(0, POST_MAX_VIDEOS).map((f) => URL.createObjectURL(f)));
                setPostInitialVideo(null);
                setIsPostOpen(true);
              }}
              onAddEvent={() => setIsEventOpen(true)}
            />

            {mixFeed.length === 0 ? (
              <EmptyState
                title="No Posts Found"
                message="There are no posts in the feed yet."
              />
            ) : (
              <div className="Community-feed-grid">
                {mixFeed.map(({ item }) => (
                  <PostCard
                    key={item.id}
                    post={item}
                    onOpenComments={setCommentsAd}
                    followingPosters={followingPosters}
                    savedPosts={savedPosts}
                    archivedPosts={archivedPosts}
                    onToggleFollow={handleToggleFollow}
                    onToggleSave={handleToggleSave}
                    onToggleArchive={handleToggleArchive}
                    onCopyLink={handleCopyLink}
                    onHide={handleHide}
                    onReport={setReportPost}
                    onEdit={setEditingPost}
                    hiddenLikeCounts={hiddenLikeCounts}
                    onToggleHideLikes={handleToggleHideLikes}
                    hiddenCommentCounts={hiddenCommentCounts}
                    onToggleHideCommentCounts={handleToggleHideCommentCounts}
                    onToggleLikesOff={handleToggleLikesOff}
                    onToggleCommentsOff={handleToggleCommentsOff}
                    onDelete={handleDeletePost}
                    onOpenViews={(post, tab, mvReaction) => setViewsAd({ ad: post, tab, myReaction: mvReaction })}
                    onOpenShare={handleSharePost}
                  />
                ))}
              </div>
            )}
          </main>

          <RightPanel
            posts={posts}
            hiddenPosts={hiddenPosts}
            archivedPosts={archivedPosts}
          />
        </div>
      </div>

      {isPostOpen && (
        <CreatePostModal
          initialMode={postInitialMode}
          initialPhotos={postInitialPhotos}
          initialVideo={postInitialVideo}
          initialVideos={postInitialVideos}
          initialYoutubeOpen={postInitialYoutubeOpen}
          onClose={() => {
            setIsPostOpen(false);
            setPostInitialPhotos([]);
            setPostInitialVideo(null);
            setPostInitialVideos([]);
            setPostInitialYoutubeOpen(false);
          }}
          onCreate={(data) => {
            handleCreatePost(data);
            setIsPostOpen(false);
            setPostInitialPhotos([]);
            setPostInitialVideo(null);
            setPostInitialVideos([]);
            setPostInitialYoutubeOpen(false);
          }}
        />
      )}

      {showPostLimitOverlay && (
        <div className="Community-overlay-backdrop" role="presentation">
          <div className="Community-overlay-panel Community-limit-overlay" role="dialog" aria-modal="true" aria-labelledby="Community-post-limit-title">
            <div className="Community-overlay-header Community-overlay-header-center">
              <h2 id="Community-post-limit-title" className="Community-heading-section">Limit Exceeded</h2>
              <button type="button" className="Community-overlay-close-btn" onClick={cancelPendingLimitFiles} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="Community-overlay-body Community-overlay-body-center">
              <p className="Community-body-text">You can add a maximum of {POST_MAX_MEDIA} items in total.</p>
            </div>
            <div className="Community-overlay-actions Community-overlay-actions-center">
              <PrimaryButton onClick={applyPendingLimitFiles}>OK</PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {editingPost && (
        editingPost.event ? (
          <CreateEventModal
            initialEvent={editingPost.event}
            onClose={() => setEditingPost(null)}
            onCreate={(data) => handleUpdateEvent(editingPost.id, data)}
          />
        ) : (
          <CreatePostModal
            initialPost={editingPost}
            onClose={() => setEditingPost(null)}
            onCreate={(data) => handleUpdatePost(editingPost.id, data)}
          />
        )
      )}

      {isEventOpen && (
        <CreateEventModal
          onClose={() => setIsEventOpen(false)}
          onCreate={(eventData) => {
            handleCreateEventPost(eventData);
            setIsEventOpen(false);
          }}
        />
      )}

      {commentsAd && <CommentsOverlay entity={commentsAd} onClose={() => setCommentsAd(null)} />}

      {shareAd && <ShareOverlay ad={shareAd} onClose={() => setShareAd(null)} />}

      {viewsAd && <ViewsOverlay ad={viewsAd.ad} onClose={() => setViewsAd(null)} initialTab={viewsAd.tab} myReaction={viewsAd.myReaction} />}

      {reportPost && (
        <ReportPostModal
          post={reportPost}
          reporterUserId={CURRENT_USER_ID}
          onClose={() => setReportPost(null)}
          onSubmitReport={handleSubmitReport}
        />
      )}

      {toast && (
        <Toast
          key={toast.id}
          message={toast.message}
          undoLabel={toast.undoLabel}
          onUndo={toast.onUndo}
          onDismiss={() => setToast(null)}
        />
      )}

      <CommunityBottomNav
        activeTab={null}
        onCreate={() => {
          setPostInitialMode("post");
          setPostInitialPhotos([]);
          setPostInitialVideo(null);
          setPostInitialVideos([]);
          setPostInitialYoutubeOpen(false);
          setIsPostOpen(true);
        }}
      />
    </div>
  );
};

/* ============================================================
   Bottom navigation bar (Archive / Saved / My Posts / Liked)
   shown only on mobile & tablet
   ============================================================ */
const CommunityBottomNav = ({ activeTab, onCreate }) => {
  const navigate = useNavigate();

  const items = [
    {
      key: "posts",
      label: "My Posts",
      path: "/community/my-posts",
      icon: <span role="img" aria-label="My Posts" style={{ fontSize: "26px" }}>🗂️</span>,
    },
    {
      key: "liked",
      label: "Liked",
      path: "/community/my-posts/liked",
      icon: <span role="img" aria-label="Liked" style={{ fontSize: "26px" }}>❤️</span>,
    },
    {
      key: "create",
      label: "Create",
      path: "/community/create",
      icon: <span role="img" aria-label="Create" style={{ fontSize: "26px" }}>➕</span>,
    },
    {
      key: "saved",
      label: "Saved",
      path: "/community/my-posts/saved",
      icon: <span role="img" aria-label="Saved" style={{ fontSize: "26px" }}>⭐</span>,
    },
    {
      key: "archive",
      label: "Archive",
      path: "/community/my-posts/archive",
      icon: <span role="img" aria-label="Archive" style={{ fontSize: "26px" }}>📦</span>,
    },
  ];

  return (
    <nav className="Community-bottom-nav" aria-label="Community shortcuts">
      {items.map((item) => (
        <button
          type="button"
          key={item.key}
          className={`Community-bottom-nav-btn ${
            activeTab === item.key ? "Community-bottom-nav-btn--active" : ""
          }`}
          onClick={() => {
            if (item.key === "create" && onCreate) onCreate();
            else navigate(item.path);
          }}
          aria-label={item.label}
        >
          {item.icon}
          <span className="Community-bottom-nav-label">{item.label}</span>
        </button>
      ))}
    </nav>
  );
};

/* ============================================================
   My Posts grid page (independent of the Community feed layout)
   ============================================================ */
const postPreviewText = (post) => post.event?.name || post.title || post.text || "Post";

const MyPostTile = ({ post, archived, onOpen }) => {
  const ytVideos = normalizeYoutube(post.youtube);
  const media = (() => {
    if (post.photos && post.photos.length > 0) return { type: "photo", src: post.photos[0] };
    const v = (post.videos && post.videos[0]) || post.video;
    if (v) return { type: "video", src: v, poster: post.videoThumbnail || undefined };
    if (ytVideos.length > 0) return { type: "youtube", src: getYoutubeThumb(ytVideos[0].embed), embed: ytVideos[0].embed };
    if (post.gif) return { type: "gif", src: post.gif.preview || post.gif.media };
    if (post.event) {
      const cover = buildCoverMedia(post.event)[0];
      if (cover) {
        if (cover.type === "youtube") return { type: "youtube", src: cover.thumb, embed: cover.src };
        return { type: cover.type, src: cover.src, poster: post.event.coverThumbnail || undefined };
      }
      if (post.event.gif) return { type: "gif", src: post.event.gif.preview || post.event.gif.media };
    }
    return null;
  })();
  const poster = getMemberById(post.posterId);
  const preview = postPreviewText(post);
  const mediaCount = (() => {
    if (post.photos && post.photos.length > 0)
      return post.photos.length + ((post.videos && post.videos.length) || 0) + ytVideos.length;
    if (post.videos && post.videos.length > 0) return post.videos.length + ytVideos.length;
    if (ytVideos.length > 0) return ytVideos.length;
    if (post.event) return buildCoverMedia(post.event).length;
    return 0;
  })();

  return (
    <button type="button" className="Community-myposts-tile" onClick={onOpen} aria-label={`Open post: ${preview}`}>
      {mediaCount > 1 && (
        <img className="Community-myposts-tile-multi" src={CommunityMultipleImg} alt={`Post with ${mediaCount} images`} />
      )}
      {media ? (
        media.type === "photo" || media.type === "gif" ? (
          <img className="Community-myposts-tile-media" src={media.src} alt="" loading="lazy" />
        ) : media.type === "youtube" ? (
          <div className="Community-myposts-tile-media Community-myposts-tile-video">
            <img className="Community-myposts-tile-media" src={media.src} alt="" loading="lazy" />
            <span className="Community-myposts-tile-play" aria-hidden="true">▶</span>
          </div>
        ) : (
          <div className="Community-myposts-tile-media Community-myposts-tile-video">
            {media.poster ? <img className="Community-myposts-tile-media" src={media.poster} alt="" loading="lazy" /> : null}
            <span className="Community-myposts-tile-play" aria-hidden="true">▶</span>
          </div>
        )
      ) : (
        <div className="Community-myposts-tile-text">
          <p className="Community-myposts-tile-text-body">{preview}</p>
        </div>
      )}
      <span className="Community-myposts-tile-footer">
        <span className="Community-myposts-tile-footer-head">
          <span className="Community-myposts-tile-title">{post.event?.title || post.title || preview}</span>
          <span className="Community-myposts-tile-footer-rule" aria-hidden="true" />
        </span>
        <span className="Community-myposts-tile-footer-row">
          <span className="Community-myposts-tile-avatar" aria-hidden="true">
            {poster ? poster.avatar : post.posterName.slice(0, 2).toUpperCase()}
          </span>
          <span className="Community-myposts-tile-meta">
            <span className="Community-myposts-tile-name">
              {post.posterName}
            </span>
            <span className="Community-myposts-tile-time-dot" aria-hidden="true">·</span>
            <span className="Community-myposts-tile-time">{formatTimeAgo(post.createdAt)}</span>
            {archived && <span className="Community-myposts-tile-archived" aria-hidden="true">📁</span>}
          </span>
        </span>
      </span>
    </button>
  );
};

export const MyPostsPage = () => {
  const navigate = useNavigate();
  const store = useCommunityState();
  const { tab: tabParam } = useParams();
  const [tab, setTab] = useState(() =>
    ["posts", "archive", "saved", "liked"].includes(tabParam) ? tabParam : "posts"
  );

  useEffect(() => {
    const effective = ["posts", "archive", "saved", "liked"].includes(tabParam) ? tabParam : "posts";
    if (effective !== tab) {
      setTab(effective);
    }
  }, [tabParam]);

  const changeTab = (nextTab) => {
    setTab(nextTab);
    navigate(nextTab === "posts" ? "/community/my-posts" : `/community/my-posts/${nextTab}`);
  };
  const [selectedPost, setSelectedPost] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [shareAd, setShareAd] = useState(null);
  const [viewsAd, setViewsAd] = useState(null);
  const [toast, setToast] = useState(null);
  const [isPostOpen, setIsPostOpen] = useState(false);
  const [postInitialPhotos, setPostInitialPhotos] = useState([]);
  const [postInitialVideo, setPostInitialVideo] = useState(null);
  const [postInitialVideos, setPostInitialVideos] = useState([]);
  const [postInitialYoutubeOpen, setPostInitialYoutubeOpen] = useState(false);

  const myPosts = useMemo(
    () =>
      store.posts
        .filter((p) => p.posterId === CURRENT_USER_ID && !store.archivedPosts.has(p.id))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [store.posts, store.archivedPosts]
  );

  const archivedPosts = useMemo(
    () =>
      store.posts
        .filter((p) => p.posterId === CURRENT_USER_ID && store.archivedPosts.has(p.id))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [store.posts, store.archivedPosts]
  );

  const savedPosts = useMemo(
    () =>
      store.posts
        .filter((p) => store.savedPosts.has(p.id))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [store.posts, store.savedPosts]
  );

  const likedPosts = useMemo(
    () =>
      store.posts
        .filter((p) => store.likedPosts.has(p.id))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [store.posts, store.likedPosts]
  );

  const displayedPosts =
    tab === "posts" ? myPosts : tab === "archive" ? archivedPosts : tab === "saved" ? savedPosts : likedPosts;

  const showToast = useCallback((message, opts = {}) => {
    setToast({ message, undoLabel: opts.undoLabel, onUndo: opts.onUndo, id: Date.now() });
  }, []);

  const handleToggleSave = useCallback(
    (post) => {
      const wasSaved = store.savedPosts.has(post.id);
      updateCommunityState((s) => {
        const next = new Set(s.savedPosts);
        if (next.has(post.id)) next.delete(post.id);
        else next.add(post.id);
        return { ...s, savedPosts: next };
      });
      showToast(wasSaved ? "Post removed from saved posts" : "Post saved");
    },
    [store.savedPosts, showToast]
  );

  const handleToggleArchive = useCallback(
    (post) => {
      const wasArchived = store.archivedPosts.has(post.id);
      updateCommunityState((s) => {
        const next = new Set(s.archivedPosts);
        if (next.has(post.id)) next.delete(post.id);
        else next.add(post.id);
        return { ...s, archivedPosts: next };
      });
      showToast(wasArchived ? "Post unarchived" : "Post archived");
    },
    [store.archivedPosts, showToast]
  );

  const handleToggleFollow = useCallback(
    (post) => {
      const wasFollowing = store.followingPosters.has(post.posterId);
      updateCommunityState((s) => {
        const next = new Set(s.followingPosters);
        if (next.has(post.posterId)) next.delete(post.posterId);
        else next.add(post.posterId);
        return { ...s, followingPosters: next };
      });
      showToast(wasFollowing ? `Unfollowed ${post.posterName}` : `Following ${post.posterName}`);
    },
    [store.followingPosters, showToast]
  );

  const handleToggleHideLikes = useCallback(
    (id) => {
      const wasHidden = store.hiddenLikeCounts.has(id);
      updateCommunityState((s) => {
        const next = new Set(s.hiddenLikeCounts);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return { ...s, hiddenLikeCounts: next };
      });
      showToast(wasHidden ? "Like count shown" : "Like count hidden");
    },
    [store.hiddenLikeCounts, showToast]
  );

  const handleToggleHideCommentCounts = useCallback(
    (id) => {
      const wasHidden = store.hiddenCommentCounts.has(id);
      updateCommunityState((s) => {
        const next = new Set(s.hiddenCommentCounts);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return { ...s, hiddenCommentCounts: next };
      });
      showToast(wasHidden ? "Comment count shown" : "Comment count hidden");
    },
    [store.hiddenCommentCounts, showToast]
  );

  const handleToggleLikesOff = useCallback(
    (post) => {
      updateCommunityState((s) => ({
        ...s,
        posts: s.posts.map((p) => (p.id === post.id ? { ...p, hideLikes: !p.hideLikes } : p)),
      }));
      setSelectedPost((sel) => (sel && sel.id === post.id ? { ...sel, hideLikes: !sel.hideLikes } : sel));
      showToast(post.hideLikes ? "Likes turned on" : "Likes turned off");
    },
    [showToast]
  );

  const handleToggleCommentsOff = useCallback(
    (post) => {
      updateCommunityState((s) => ({
        ...s,
        posts: s.posts.map((p) => (p.id === post.id ? { ...p, hideComments: !p.hideComments } : p)),
      }));
      setSelectedPost((sel) => (sel && sel.id === post.id ? { ...sel, hideComments: !sel.hideComments } : sel));
      showToast(post.hideComments ? "Comments turned on" : "Comments turned off");
    },
    [showToast]
  );

  const copyTextToClipboard = useCallback((url) => {
    const done = () => showToast("Link copied");
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(done);
    } else {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (_) {}
      done();
    }
  }, [showToast]);

  const handleCopyLink = useCallback((post) => {
    copyTextToClipboard(`${window.location.origin}/community/post/${post.id}`);
  }, [copyTextToClipboard]);

  const handleSharePost = useCallback(
    (post) => shareCurrentPage(post, { onCopied: () => showToast("Link copied") }),
    [showToast]
  );

  const handleHide = useCallback((post) => {
    updateCommunityState((s) => {
      const next = new Set(s.hiddenPosts);
      next.add(post.id);
      return { ...s, hiddenPosts: next };
    });
    setSelectedPost(null);
  }, []);

  const handleDelete = useCallback(
    (post) => {
      updateCommunityState((s) => ({ ...s, posts: s.posts.filter((p) => p.id !== post.id) }));
      setSelectedPost(null);
      showToast("Post deleted");
    },
    [showToast]
  );

  const handleEdit = useCallback(() => {
    if (selectedPost) setEditingPost(selectedPost);
  }, [selectedPost]);

  const handleUpdatePost = useCallback((postId, data) => {
    const updated = {
      text: data.text,
      textColor: data.textColor || "#1a1a1a",
      textSize: data.textSize || 16,
      textWeight: data.textWeight || 400,
      textItalic: Boolean(data.textItalic),
      textUnderline: Boolean(data.textUnderline),
      title: data.title || "",
      titleColor: data.titleColor || "#000000",
      titleSize: data.titleSize || 20,
      titleWeight: data.titleWeight || 600,
      titleItalic: Boolean(data.titleItalic),
      titleUnderline: Boolean(data.titleUnderline),
      visibility: data.visibility,
      commentPermission: data.commentPermission,
      emojiEnabled: data.emojiEnabled,
      hideLikes: Boolean(data.hideLikes),
      hideComments: Boolean(data.hideComments),
      hideLikeCount: Boolean(data.hideLikeCount),
      hideCommentCount: Boolean(data.hideCommentCount),
      photos: data.photos || [],
      videos: data.videos || [],
      videoEdits: data.videoEdits || [],
      videoThumbnail: data.videoThumbnail || null,
      videoThumbnails: data.videoThumbnails || [],
      ...(data.mediaOrder ? { mediaOrder: data.mediaOrder } : {}),
      gif: data.gif || null,
      youtube: data.youtube || null,
      taggedPeople: data.taggedPeople || [],
      location: data.location || "",
      socials: data.socials || [],
    };
    updateCommunityState((s) => ({
      ...s,
      posts: s.posts.map((p) => (p.id === postId ? { ...p, ...updated } : p)),
    }));
    setSelectedPost((sel) => (sel && sel.id === postId ? { ...sel, ...updated } : sel));
    setEditingPost(null);
    showToast("Post updated");
  }, [showToast]);

  const handleUpdateEvent = useCallback((postId, eventData) => {
    updateCommunityState((s) => ({
      ...s,
      posts: s.posts.map((p) =>
        p.id === postId && p.event
          ? {
              ...p,
              event: { ...eventData, id: postId },
              visibility: eventData.visibility || p.visibility,
              commentPermission: eventData.commentPermission || p.commentPermission,
              emojiEnabled: eventData.emojiEnabled !== false,
              hideLikes: Boolean(eventData.hideLikes),
              hideLikeCount: Boolean(eventData.hideLikeCount),
              hideComments: Boolean(eventData.hideComments),
              hideCommentCount: Boolean(eventData.hideCommentCount),
            }
          : p
      ),
    }));
    setSelectedPost((sel) =>
      sel && sel.id === postId ? { ...sel, event: { ...eventData, id: postId } } : sel
    );
    setEditingPost(null);
    showToast("Event updated");
  }, [showToast]);

  const openPost = (post) => setSelectedPost(post);

  const handleCreatePost = useCallback(
    (postData) => {
      updateCommunityState((s) => {
        const nextId = Math.max(1000, ...s.posts.map((p) => p.id)) + 1;
        const newPost = buildPost({
          id: nextId,
          text: postData.text,
          textColor: postData.textColor || "#1a1a1a",
          textSize: postData.textSize || 16,
          textWeight: postData.textWeight || 400,
          textItalic: Boolean(postData.textItalic),
          textUnderline: Boolean(postData.textUnderline),
          title: postData.title || "",
          titleColor: postData.titleColor || "#000000",
          titleSize: postData.titleSize || 20,
          titleWeight: postData.titleWeight || 600,
          titleItalic: Boolean(postData.titleItalic),
          titleUnderline: Boolean(postData.titleUnderline),
          visibility: postData.visibility,
          commentPermission: postData.commentPermission,
          emojiEnabled: postData.emojiEnabled,
          hideLikes: Boolean(postData.hideLikes),
          hideComments: Boolean(postData.hideComments),
          hideLikeCount: Boolean(postData.hideLikeCount),
          hideCommentCount: Boolean(postData.hideCommentCount),
          photos: postData.photos || [],
          videos: postData.videos || [],
          videoEdits: postData.videoEdits || [],
          videoThumbnail:
            (postData.videoThumbnails && postData.videoThumbnails.find((t) => t)) ||
            postData.videoThumbnail ||
            null,
          videoThumbnails: postData.videoThumbnails || [],
          youtube: postData.youtube || null,
          gif: postData.gif || null,
          taggedPeople: postData.taggedPeople || [],
          location: postData.location || "",
          socials: postData.socials || [],
          event: postData.event ? { ...postData.event, id: nextId } : null,
        });
        return { ...s, posts: [newPost, ...s.posts] };
      });
      setIsPostOpen(false);
      setPostInitialPhotos([]);
      setPostInitialVideo(null);
      setPostInitialVideos([]);
      setPostInitialYoutubeOpen(false);
      showToast("Post published");
    },
    [showToast]
  );

  return (
    <div className="Community-page">
      <div className="Community-container">
        <div className="Community-myposts-backbar">
          <button
            type="button"
            className="Community-myposts-back-btn"
            onClick={() => navigate("/community")}
            aria-label="Back to Community"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div className="Community-myposts-tabs" role="tablist" aria-label="My posts">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "posts"}
            className={`Community-myposts-tab ${tab === "posts" ? "Community-myposts-tab-active" : ""}`}
            onClick={() => changeTab("posts")}
          >
            My
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "archive"}
            className={`Community-myposts-tab ${tab === "archive" ? "Community-myposts-tab-active" : ""}`}
            onClick={() => changeTab("archive")}
          >
            Archived
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "saved"}
            className={`Community-myposts-tab ${tab === "saved" ? "Community-myposts-tab-active" : ""}`}
            onClick={() => changeTab("saved")}
          >
            Saved
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "liked"}
            className={`Community-myposts-tab ${tab === "liked" ? "Community-myposts-tab-active" : ""}`}
            onClick={() => changeTab("liked")}
          >
            Liked
          </button>
        </div>
        </div>

        <header className="Community-page-header">
          <div className="Community-center-heading-wrap Community-myposts-heading">
            <div className="Community-myposts-title-row">
              <h1 className="Community-heading-main">
                {tab === "posts"
                  ? "My Posts"
                  : tab === "archive"
                  ? "Archive Photos"
                  : tab === "saved"
                  ? "Saved Posts"
                  : "Liked Posts"}
              </h1>
            </div>
          </div>
        </header>

        {displayedPosts.length === 0 ? (
          <EmptyState
            title={tab === "posts" ? "No Posts Found" : tab === "archive" ? "No Archived Posts" : tab === "saved" ? "No Saved Posts" : "No Liked Posts"}
            message={
              tab === "posts"
                ? "You have not posted anything yet."
                : tab === "archive"
                ? "Archived posts will appear here."
                : tab === "saved"
                ? "Posts you save will appear here."
                : "Posts you react to will appear here."
            }
          />
        ) : (
          <div className="Community-myposts-grid">
            {displayedPosts.map((post) => (
              <MyPostTile
                key={post.id}
                post={post}
                archived={tab === "posts" && store.archivedPosts.has(post.id)}
                onOpen={() => openPost(post)}
              />
            ))}
          </div>
        )}
      </div>

      {selectedPost && (() => {
        const event = selectedPost.event;
        const viewerMedia = event
          ? buildCoverMedia(event).map((m) => (m.type === "video" ? { ...m, poster: event.coverThumbnail || undefined } : m))
          : (() => {
              const arr = [];
              if (selectedPost.photos) selectedPost.photos.forEach((src) => arr.push({ type: "photo", src }));
              const v = (selectedPost.videos && selectedPost.videos[0]) || selectedPost.video;
              if (v) arr.push({ type: "video", src: v, poster: selectedPost.videoThumbnail });
              normalizeYoutube(selectedPost.youtube).forEach((y) => arr.push({ type: "youtube", src: y.embed, url: y.url }));
              return arr;
            })();
        const selectedControlProps = {
          post: selectedPost,
          isOwner: selectedPost.posterId === CURRENT_USER_ID,
          isFollowing: store.followingPosters.has(selectedPost.posterId),
          isSaved: store.savedPosts.has(selectedPost.id),
          isArchived: store.archivedPosts.has(selectedPost.id),
          onToggleFollow: () => handleToggleFollow(selectedPost),
          onToggleSave: () => handleToggleSave(selectedPost),
          onToggleArchive: () => handleToggleArchive(selectedPost),
          onCopyLink: () => handleCopyLink(selectedPost),
          onHide: () => handleHide(selectedPost),
          onReport: () => {},
          onEdit: handleEdit,
          isLikeCountHidden: store.hiddenLikeCounts.has(selectedPost.id),
          onToggleHideLikes: () => handleToggleHideLikes(selectedPost.id),
          isCommentCountHidden: store.hiddenCommentCounts.has(selectedPost.id),
          onToggleHideCommentCounts: () => handleToggleHideCommentCounts(selectedPost.id),
          isLikesOff: Boolean(selectedPost.hideLikes),
          onToggleLikesOff: () => handleToggleLikesOff(selectedPost),
          isCommentsOff: Boolean(selectedPost.hideComments),
          onToggleCommentsOff: () => handleToggleCommentsOff(selectedPost),
          onDelete: () => handleDelete(selectedPost),
        };
        if (!viewerMedia.length) {
          return (
            <Overlay
              title="Post"
              onClose={() => setSelectedPost(null)}
              wide
              panelClassName="Community-myposts-post-panel"
            >
              <PostCard
                post={selectedPost}
                onOpenComments={() => {}}
                followingPosters={store.followingPosters}
                savedPosts={store.savedPosts}
                archivedPosts={store.archivedPosts}
                onToggleFollow={handleToggleFollow}
                onToggleSave={handleToggleSave}
                onToggleArchive={handleToggleArchive}
                onCopyLink={handleCopyLink}
                onHide={handleHide}
                onReport={() => {}}
                onEdit={handleEdit}
                hiddenLikeCounts={store.hiddenLikeCounts}
                onToggleHideLikes={handleToggleHideLikes}
                hiddenCommentCounts={store.hiddenCommentCounts}
                onToggleHideCommentCounts={handleToggleHideCommentCounts}
                onToggleLikesOff={handleToggleLikesOff}
                onToggleCommentsOff={handleToggleCommentsOff}
                onDelete={handleDelete}
                onOpenViews={(post, tab, myReaction) => setViewsAd({ ad: post, tab, myReaction })}
                onOpenShare={handleSharePost}
              />
            </Overlay>
          );
        }
        return (
          <ExpandedPostViewer
            post={selectedPost}
            items={viewerMedia}
            initialIndex={0}
            onClose={() => setSelectedPost(null)}
            controlProps={selectedControlProps}
            isOwner={selectedPost.posterId === CURRENT_USER_ID}
            onOpenViews={(tab, myReaction) => setViewsAd({ ad: selectedPost, tab, myReaction })}
            onOpenShare={handleSharePost}
            hideLikeCount={store.hiddenLikeCounts.has(selectedPost.id)}
            hideCommentCount={store.hiddenCommentCounts.has(selectedPost.id)}
          />
        );
      })()}

      {shareAd && <ShareOverlay ad={shareAd} onClose={() => setShareAd(null)} />}

      {viewsAd && (
        <ViewsOverlay
          ad={viewsAd.ad}
          onClose={() => setViewsAd(null)}
          initialTab={viewsAd.tab}
          myReaction={viewsAd.myReaction}
        />
      )}

      {editingPost && (
        editingPost.event ? (
          <CreateEventModal
            initialEvent={editingPost.event}
            onClose={() => setEditingPost(null)}
            onCreate={(data) => handleUpdateEvent(editingPost.id, data)}
          />
        ) : (
          <CreatePostModal
            initialPost={editingPost}
            onClose={() => setEditingPost(null)}
            onCreate={(data) => handleUpdatePost(editingPost.id, data)}
          />
        )
      )}

      {toast && (
        <Toast
          key={toast.id}
          message={toast.message}
          undoLabel={toast.undoLabel}
          onUndo={toast.onUndo}
          onDismiss={() => setToast(null)}
        />
      )}

      {isPostOpen && (
        <CreatePostModal
          initialMode="post"
          initialPhotos={postInitialPhotos}
          initialVideo={postInitialVideo}
          initialVideos={postInitialVideos}
          initialYoutubeOpen={postInitialYoutubeOpen}
          onClose={() => {
            setIsPostOpen(false);
            setPostInitialPhotos([]);
            setPostInitialVideo(null);
            setPostInitialVideos([]);
            setPostInitialYoutubeOpen(false);
          }}
          onCreate={handleCreatePost}
        />
      )}

      <CommunityBottomNav
        activeTab={tab}
        onCreate={() => {
          setPostInitialPhotos([]);
          setPostInitialVideo(null);
          setPostInitialVideos([]);
          setPostInitialYoutubeOpen(false);
          setIsPostOpen(true);
        }}
      />
    </div>
  );
};

export default Community;
